
import React, { useState, useCallback, useMemo, Suspense, lazy, useEffect } from 'react';
import { QUIZ_DATA } from './quizData';
import { Page, QuizConfig, QuizData, Option, ElementType, PageElement, ProjectMeta, StoredQuiz } from './types';
import logo from './src/assets/logo-quiz.png';
import { supabaseRPC, supabase, getUTMParameters, addUTMsToUrl } from './src/lib/supabase';
import { useProjects } from './src/hooks/useProjects';
import { useUserSettings } from './src/hooks/useUserSettings';
import { useQuizStatistics } from './src/hooks/useQuizStatistics';
import { useQuizViewer } from './src/hooks/useQuizViewer';
import { generatePageUniqueId } from './src/utils/helpers';
import { TRANSLATION_PROMPT } from './translation-prompt';

// Componentes de Página para a Pré-visualização
import SingleChoiceQuestionPage from './components/SingleChoiceQuestionPage';
import MultiChoiceQuestionPage from './components/MultiChoiceQuestionPage';
import ImagePage from './components/ImagePage';
import AudioQuestionPage from './components/AudioQuestionPage';
import QuizHeader from './components/QuizHeader';
import ScorePopup from './src/components/ScorePopup';
import ResultPage from './src/components/ResultPage';
import ElementPalette from './components/ElementPalette';
import PageElementsEditor from './components/PageElementsEditor';
import ElementEditor from './components/ElementEditor';
import { DndContext, PointerSensor, KeyboardSensor, useSensors, useSensor, DragOverlay, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MobilePreviewModal from './components/MobilePreviewModal';
import PageRenderer from './components/PageRenderer';
import DatePicker from './components/DatePicker';
import { ProjectSettingsModal } from './components/ProjectSettingsModal';
import { ImageLibrary } from './components/ImageLibrary';

// Lazy loading de componentes pesados (carregados sob demanda)
const LeadsDashboard = lazy(() => import('./components/LeadsDashboard'));


const LoadingSpinner: React.FC = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-[9999]">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-sky-500 border-t-transparent mb-2"></div>
    </div>
  </div>
);


// Helper to generate unique IDs
const generateId = () => `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const toSlug = (s: string) => s.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const sanitizeSlugInput = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9-]/g, '');
const isValidSlug = (s: string) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(s);

// Helper to get content width class
const getContentWidthClass = (width: 'small' | 'medium' | 'large' = 'small') => {
  switch (width) {
    case 'small':
      return 'max-w-lg';
    case 'medium':
      return 'max-w-2xl';
    case 'large':
      return 'max-w-4xl';
    default:
      return 'max-w-lg';
  }
};

// Helper to get element size classes
const getElementSizeClasses = (size: 'small' | 'medium' | 'large' | 'extra' = 'small') => {
  switch (size) {
    case 'small':
      return 'text-base py-3 px-4';
    case 'medium':
      return 'text-lg py-4 px-6';
    case 'large':
      return 'text-xl py-5 px-8';
    case 'extra':
      return 'text-2xl py-6 px-10';
    default:
      return 'text-base py-3 px-4';
  }
};

// Helper to get spacing value (between elements)
const getSpacingValue = (spacing: 'none' | 'simple' | 'medium' | 'large' | 'extra' = 'medium') => {
  switch (spacing) {
    case 'none':
      return 0;
    case 'simple':
      return 1;
    case 'medium':
      return 2;
    case 'large':
      return 3.5;
    case 'extra':
      return 6;
    default:
      return 2;
  }
};

// Helper to get spacing classes (container gap)
const getSpacingClasses = (spacing: 'none' | 'simple' | 'medium' | 'large' | 'extra' = 'medium') => {
  const value = getSpacingValue(spacing);
  return `gap-${value * 4}`;
};

// Helper to get border radius class
const getBorderRadiusClass = (radius: 'none' | 'light' | 'medium' | 'heavy' = 'light') => {
  switch (radius) {
    case 'none':
      return 'rounded-none';
    case 'light':
      return 'rounded-md';
    case 'medium':
      return 'rounded-lg';
    case 'heavy':
      return 'rounded-xl';
    default:
      return 'rounded-md';
  }
};

// --- ICONS (defined internally to avoid creating new files) ---
const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
);
const TrashIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
);
const CopyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
);

const PagePropertiesEditor = React.lazy(() => import('./components/PagePropertiesEditor'));

// --- EDITOR COMPONENTS ---

const PersonalizationEditor: React.FC<{ config: QuizConfig; onUpdate: (newConfig: QuizConfig) => void }> = ({ config, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false); // Start collapsed
  const [imgLib, setImgLib] = useState<{ isOpen: boolean; callback?: (url: string) => void }>({ isOpen: false });
  const openImageLib = (cb: (url: string) => void) => setImgLib({ isOpen: true, callback: cb });

  return (
    <div className="bg-slate-100 rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-3 text-left"
        aria-expanded={isOpen}
      >
        <h3 className="font-bold text-base text-slate-800">Personalização</h3>
        <svg
          className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-none' : 'max-h-0'}`}>
        <div className="px-4 pb-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Cores</label>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Primária</label>
                <input type="color" value={config.colors.primary} onChange={e => onUpdate({ ...config, colors: { ...config.colors, primary: e.target.value } })} className="w-full h-8 p-0.5 border-slate-300 rounded-md" title="Cor Primária" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Fundo</label>
                <input type="color" value={config.colors.background} onChange={e => onUpdate({ ...config, colors: { ...config.colors, background: e.target.value } })} className="w-full h-8 p-0.5 border-slate-300 rounded-md" title="Cor de Fundo" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Títulos</label>
                <input type="color" value={config.colors.title || '#1e293b'} onChange={e => onUpdate({ ...config, colors: { ...config.colors, title: e.target.value } })} className="w-full h-8 p-0.5 border-slate-300 rounded-md" title="Cor dos Títulos" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Textos</label>
                <input type="color" value={config.colors.text || '#64748b'} onChange={e => onUpdate({ ...config, colors: { ...config.colors, text: e.target.value } })} className="w-full h-8 p-0.5 border-slate-300 rounded-md" title="Cor dos Textos" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Fonte</label>
            <select
              value={config.fontFamily}
              onChange={e => onUpdate({ ...config, fontFamily: e.target.value })}
              className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
            >
              <option value="Inter">Inter</option>
              <option value="Roboto">Roboto</option>
              <option value="Open Sans">Open Sans</option>
              <option value="Montserrat">Montserrat</option>
              <option value="Lato">Lato</option>
              <option value="Roboto Condensed">Roboto Condensed</option>
              <option value="Arial">Arial</option>
              <option value="PT Sans">PT Sans</option>
              <option value="Nunito">Nunito</option>
              <option value="Raleway">Raleway</option>
              <option value="Poppins">Poppins</option>
              <option value="Merriweather">Merriweather</option>
              <option value="Playfair Display">Playfair Display</option>
              <option value="Crimson Text">Crimson Text</option>
              <option value="Georgia">Georgia</option>
              <option value="Roboto Slab">Roboto Slab</option>
              <option value="IBM Plex Serif">IBM Plex Serif</option>
              <option value="Source Serif Pro">Source Serif Pro</option>
              <option value="Libre Baskerville">Libre Baskerville</option>
              <option value="Alegreya">Alegreya</option>
            </select>
          </div>
          <div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Título</label>
                <input type="text" value={config.fontSizes.title} onChange={e => onUpdate({ ...config, fontSizes: { ...config.fontSizes, title: e.target.value } })} className="w-full p-1 border border-slate-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Textos</label>
                <input type="text" value={config.fontSizes.subtitle} onChange={e => onUpdate({ ...config, fontSizes: { ...config.fontSizes, subtitle: e.target.value } })} className="w-full p-1 border border-slate-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Opção</label>
                <input type="text" value={config.fontSizes.option} onChange={e => onUpdate({ ...config, fontSizes: { ...config.fontSizes, option: e.target.value } })} className="w-full p-1 border border-slate-300 rounded-md text-sm" />
              </div>
            </div>
          </div>
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm font-medium text-slate-600 mb-2">Layout</label>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Estilo do Container</label>
                <select
                  value={config.containerStyle || 'flat'}
                  onChange={e => onUpdate({
                    ...config,
                    containerStyle: e.target.value as 'flat' | 'boxed'
                  })}
                  className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                >
                  <option value="flat">Fundo Chapado</option>
                  <option value="boxed">Card com Sombra</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Alinhamento do Conteúdo</label>
                  <select
                    value={config.contentAlignment || 'center'}
                    onChange={e => onUpdate({
                      ...config,
                      contentAlignment: e.target.value as 'center' | 'top'
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                  >
                    <option value="center">Centralizado</option>
                    <option value="top">Alinhado ao topo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Largura do Conteúdo</label>
                  <select
                    value={config.contentWidth || 'small'}
                    onChange={e => onUpdate({
                      ...config,
                      contentWidth: e.target.value as 'small' | 'medium' | 'large'
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                  >
                    <option value="small">Pequena</option>
                    <option value="medium">Média</option>
                    <option value="large">Grande</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tamanho dos Elementos</label>
                  <select
                    value={config.elementSize || 'small'}
                    onChange={e => onUpdate({
                      ...config,
                      elementSize: e.target.value as 'small' | 'medium' | 'large' | 'extra'
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                  >
                    <option value="small">Pequeno</option>
                    <option value="medium">Médio</option>
                    <option value="large">Grande</option>
                    <option value="extra">Extra</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Espaçamento Elementos</label>
                  <select
                    value={config.spacing || 'medium'}
                    onChange={e => onUpdate({
                      ...config,
                      spacing: e.target.value as 'none' | 'simple' | 'medium' | 'large' | 'extra'
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                  >
                    <option value="none">Nenhum</option>
                    <option value="simple">Simples</option>
                    <option value="medium">Médio</option>
                    <option value="large">Grande</option>
                    <option value="extra">Extra</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Bordas/Cantos</label>
                <select
                  value={config.borderRadius || 'light'}
                  onChange={e => onUpdate({
                    ...config,
                    borderRadius: e.target.value as 'none' | 'light' | 'medium' | 'heavy'
                  })}
                  className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                >
                  <option value="none">Sem Arredondamento</option>
                  <option value="light">Levemente Arredondado</option>
                  <option value="medium">Arredondado</option>
                  <option value="heavy">Muito Arredondado</option>
                </select>
              </div>

            </div>
          </div>
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm font-medium text-slate-600 mb-2">Cabeçalho</label>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">URL da Logo</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={config.header?.logoUrl || ''}
                    onChange={e => onUpdate({
                      ...config,
                      header: {
                        ...config.header,
                        logoUrl: e.target.value || undefined
                      }
                    })}
                    className="flex-1 p-2 border border-slate-300 rounded-md text-sm"
                    placeholder="https://..."
                  />
                  <button
                    type="button"
                    onClick={() => openImageLib((url) => onUpdate({
                      ...config,
                      header: { ...config.header, logoUrl: url }
                    }))}
                    className="px-3 py-2 bg-slate-100 border border-slate-300 rounded-md hover:bg-slate-200 text-slate-600"
                    title="Selecionar imagem da biblioteca"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                    </svg>
                  </button>
                </div>
                {config.header?.logoUrl && (
                  <div className="mt-2 relative group w-16 h-16 bg-slate-100 rounded border overflow-hidden">
                    <img src={config.header.logoUrl} alt="Preview" className="w-full h-full object-contain" />
                    <button
                      type="button"
                      onClick={() => onUpdate({ ...config, header: { ...config.header, logoUrl: undefined } })}
                      className="absolute top-0 right-0 p-0.5 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                      title="Remover"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tamanho da Logo</label>
                  <select
                    value={config.header?.logoSize || 'small'}
                    onChange={e => onUpdate({
                      ...config,
                      header: {
                        ...config.header,
                        logoSize: e.target.value as 'small' | 'large'
                      }
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                  >
                    <option value="small">Pequeno</option>
                    <option value="large">Grande</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Posição da Logo</label>
                  <select
                    value={config.header?.logoPosition || 'center'}
                    onChange={e => onUpdate({
                      ...config,
                      header: {
                        ...config.header,
                        logoPosition: e.target.value as 'center' | 'left'
                      }
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                  >
                    <option value="center">Centro</option>
                    <option value="left">Esquerda</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Barra de Progresso</label>
                <select
                  value={config.header?.progressBarStyle || 'default'}
                  onChange={e => onUpdate({
                    ...config,
                    header: {
                      ...config.header,
                      progressBarStyle: e.target.value as 'default' | 'flat' | 'hidden'
                    }
                  })}
                  className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm mb-1"
                >
                  <option value="default">Padrão</option>
                  <option value="flat">Flat</option>
                  <option value="hidden">Não exibir</option>
                </select>
              </div>
              <div className="flex items-center justify-between">
                <label htmlFor="toggleBackButton" className="text-sm text-slate-700">Mostrar Botão Voltar</label>
                {(() => {
                  const enabled = (config.header?.showBackButton !== false); return (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={enabled}
                        onChange={() => onUpdate({ ...config, header: { ...config.header, showBackButton: !enabled } })}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  );
                })()}
              </div>
              <div className="flex items-center justify-between">
                <label htmlFor="toggleStepIndicator" className="text-sm text-slate-700">Mostrar Indicador de Etapa</label>
                {(() => {
                  const enabled = (config.header?.showStepIndicator !== false); return (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={enabled}
                        onChange={() => onUpdate({ ...config, header: { ...config.header, showStepIndicator: !enabled } })}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  );
                })()}
              </div>
              <div className="flex items-center justify-between">
                <label htmlFor="toggleCoverHide" className="text-sm text-slate-700">1ª Página em Modo Capa</label>
                {(() => {
                  const enabled = (config.header?.hideOnFirstPage === true); return (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={enabled}
                        onChange={() => onUpdate({ ...config, header: { ...config.header, hideOnFirstPage: !enabled } })}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  );
                })()}
              </div>
            </div>
          </div>
          <div className="border-t pt-4 mt-4">
            <button
              onClick={() => {
                const defaultConfig: QuizConfig = {
                  colors: {
                    background: '#ffffff',
                    primary: '#000000',
                    title: '#1e293b',
                    text: '#64748b',
                  },
                  fontSizes: {
                    title: '1.7rem',
                    subtitle: '0.9rem',
                    option: '1rem',
                  },
                  fontFamily: 'Inter',
                  contentAlignment: 'center',
                  contentWidth: 'small',
                  elementSize: 'small',
                  spacing: 'medium',
                  borderRadius: 'light',
                  header: {
                    showBackButton: true,
                    showStepIndicator: true,
                    progressBarStyle: 'default',
                  },
                  gamification: {
                    coinsEnabled: false,
                    coinsPreText: 'Seu Saldo: ',
                    coinsPostText: 'ptos',
                  },
                  validation: {
                    requiredMessage: 'Preencha os campos obrigatórios antes de continuar.',
                  },
                  scripts: {
                    head: '',
                    footer: '',
                  },
                };
                onUpdate(defaultConfig);
              }}
              className="w-full px-4 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
            >
              Resetar
            </button>
          </div>
        </div>
      </div>

      {/* Image Library Modal */}
      {imgLib.isOpen && (
        <ImageLibrary
          onSelect={(url) => {
            if (imgLib.callback) imgLib.callback(url);
            setImgLib({ isOpen: false });
          }}
          onClose={() => setImgLib({ isOpen: false })}
          initialUrl={config.header?.logoUrl}
        />
      )}
    </div>
  );
};

const GeneralSettingsEditor: React.FC<{ config: QuizConfig; onUpdate: (newConfig: QuizConfig) => void }> = ({ config, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false); // Start collapsed
  const [imgLib, setImgLib] = useState<{ isOpen: boolean; callback?: (url: string) => void }>({ isOpen: false });
  const openImageLib = (cb: (url: string) => void) => setImgLib({ isOpen: true, callback: cb });

  return (
    <div className="bg-slate-100 rounded-lg mt-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-3 text-left"
        aria-expanded={isOpen}
      >
        <h3 className="font-bold text-base text-slate-800">Configurações Gerais</h3>
        <svg
          className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-none' : 'max-h-0'}`}>
        <div className="px-4 pb-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">SEO</label>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Título da Página</label>
                <input
                  type="text"
                  value={config.seoTitle || ''}
                  onChange={e => onUpdate({ ...config, seoTitle: e.target.value || undefined })}
                  className="w-full p-2 border border-slate-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Descrição da Página</label>
                <textarea
                  value={config.seoDescription || ''}
                  onChange={e => onUpdate({ ...config, seoDescription: e.target.value || undefined })}
                  maxLength={160}
                  rows={2}
                  className="w-full p-2 border border-slate-300 rounded-md text-sm"
                />
              </div>
            </div>
          </div>
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm font-medium text-slate-600 mb-2">Validação</label>
            <div className="space-y-3">

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Mensagem de Campos Obrigatórios</label>
                <input
                  type="text"
                  value={config.validation?.requiredMessage || 'Preencha os campos obrigatórios antes de continuar.'}
                  onChange={e => onUpdate({
                    ...config,
                    validation: { ...(config.validation || {}), requiredMessage: e.target.value }
                  })}
                  className="w-full p-2 border border-slate-300 rounded-md text-sm"
                />
              </div>
            </div>
          </div>
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm font-medium text-slate-600 mb-2">Recuperação de Sessão</label>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Modo de Retorno do Usuário</label>
                <select
                  value={config.session?.mode || 'reset'}
                  onChange={e => onUpdate({
                    ...config,
                    session: { ...(config.session || { durationDays: 1 }), mode: e.target.value as 'reset' | 'recover' }
                  })}
                  className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                >
                  <option value="reset">Reiniciar Quiz (Padrão)</option>
                  <option value="recover">Se concluiu, exibe a última página</option>
                </select>
                <p className="text-[10px] text-slate-500 mt-1">
                  {config.session?.mode === 'recover'
                    ? 'Se concluiu, ao retornar exibe a última página. Se não, reinicia.'
                    : 'Usuários que retornarem começarão do início.'}
                </p>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Janela de Retorno (Dias)</label>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={config.session?.durationDays ?? 1}
                    onChange={e => onUpdate({
                      ...config,
                      session: {
                        ...(config.session || { mode: 'reset' }),
                        durationDays: parseFloat(e.target.value) || 1
                      }
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md text-sm"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Tempo para considerar como "mesma sessão" (não conta nova view).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingsEditor: React.FC<{ config: QuizConfig; onUpdate: (newConfig: QuizConfig) => void }> = ({ config, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false); // Start collapsed
  const [imgLib, setImgLib] = useState<{ isOpen: boolean; callback?: (url: string) => void }>({ isOpen: false });
  const openImageLib = (cb: (url: string) => void) => setImgLib({ isOpen: true, callback: cb });

  const liveRef = React.useRef<HTMLDivElement>(null);
  const [liveOverflow, setLiveOverflow] = React.useState(false);
  React.useEffect(() => {
    const el = liveRef.current;
    if (!el) return;
    setLiveOverflow(el.scrollHeight > el.clientHeight + 1);
  });

  return (
    <div className="bg-slate-100 rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-3 text-left"
        aria-expanded={isOpen}
      >
        <h3 className="font-bold text-base text-slate-800">Configurações Gerais</h3>
        <svg
          className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-none' : 'max-h-0'}`}>
        <div className="px-4 pb-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Cor Primária</label>
              <input type="color" value={config.colors.primary} onChange={e => onUpdate({ ...config, colors: { ...config.colors, primary: e.target.value } })} className="w-full h-10 p-1 border-slate-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Cor de Fundo</label>
              <input type="color" value={config.colors.background} onChange={e => onUpdate({ ...config, colors: { ...config.colors, background: e.target.value } })} className="w-full h-10 p-1 border-slate-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Cor Títulos</label>
              <input type="color" value={config.colors.title || '#1e293b'} onChange={e => onUpdate({ ...config, colors: { ...config.colors, title: e.target.value } })} className="w-full h-10 p-1 border-slate-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Cor Textos</label>
              <input type="color" value={config.colors.text || '#64748b'} onChange={e => onUpdate({ ...config, colors: { ...config.colors, text: e.target.value } })} className="w-full h-10 p-1 border-slate-300 rounded-md" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Fonte</label>
            <select
              value={config.fontFamily}
              onChange={e => onUpdate({ ...config, fontFamily: e.target.value })}
              className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
            >
              <option value="Inter">Inter</option>
              <option value="Roboto">Roboto</option>
              <option value="Open Sans">Open Sans</option>
              <option value="Montserrat">Montserrat</option>
              <option value="Lato">Lato</option>
              <option value="Roboto Condensed">Roboto Condensed</option>
              <option value="Arial">Arial</option>
              <option value="PT Sans">PT Sans</option>
              <option value="Nunito">Nunito</option>
              <option value="Raleway">Raleway</option>
              <option value="Poppins">Poppins</option>
              <option value="Merriweather">Merriweather</option>
              <option value="Playfair Display">Playfair Display</option>
              <option value="Crimson Text">Crimson Text</option>
              <option value="Georgia">Georgia</option>
              <option value="Roboto Slab">Roboto Slab</option>
              <option value="IBM Plex Serif">IBM Plex Serif</option>
              <option value="Source Serif Pro">Source Serif Pro</option>
              <option value="Libre Baskerville">Libre Baskerville</option>
              <option value="Alegreya">Alegreya</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Tamanhos da Fonte</label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Título</label>
                <input type="text" value={config.fontSizes.title} onChange={e => onUpdate({ ...config, fontSizes: { ...config.fontSizes, title: e.target.value } })} className="w-full p-1 border border-slate-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Textos</label>
                <input type="text" value={config.fontSizes.subtitle} onChange={e => onUpdate({ ...config, fontSizes: { ...config.fontSizes, subtitle: e.target.value } })} className="w-full p-1 border border-slate-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Opção</label>
                <input type="text" value={config.fontSizes.option} onChange={e => onUpdate({ ...config, fontSizes: { ...config.fontSizes, option: e.target.value } })} className="w-full p-1 border border-slate-300 rounded-md text-sm" />
              </div>
            </div>
          </div>
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm font-medium text-slate-600 mb-2">Layout</label>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Alinhamento do Conteúdo</label>
                  <select
                    value={config.contentAlignment || 'center'}
                    onChange={e => onUpdate({
                      ...config,
                      contentAlignment: e.target.value as 'center' | 'top'
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                  >
                    <option value="center">Centralizado</option>
                    <option value="top">Alinhado ao topo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Largura do Conteúdo</label>
                  <select
                    value={config.contentWidth || 'small'}
                    onChange={e => onUpdate({
                      ...config,
                      contentWidth: e.target.value as 'small' | 'medium' | 'large'
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                  >
                    <option value="small">Pequena</option>
                    <option value="medium">Média</option>
                    <option value="large">Grande</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tamanho dos Elementos</label>
                  <select
                    value={config.elementSize || 'small'}
                    onChange={e => onUpdate({
                      ...config,
                      elementSize: e.target.value as 'small' | 'medium' | 'large' | 'extra'
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                  >
                    <option value="small">Pequeno</option>
                    <option value="medium">Médio</option>
                    <option value="large">Grande</option>
                    <option value="extra">Extra</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Espaçamento Elementos</label>
                  <select
                    value={config.spacing || 'medium'}
                    onChange={e => onUpdate({
                      ...config,
                      spacing: e.target.value as 'none' | 'simple' | 'medium' | 'large' | 'extra'
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                  >
                    <option value="none">Nenhum</option>
                    <option value="simple">Simples</option>
                    <option value="medium">Médio</option>
                    <option value="large">Grande</option>
                    <option value="extra">Extra</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Bordas/Cantos</label>
                <select
                  value={config.borderRadius || 'light'}
                  onChange={e => onUpdate({
                    ...config,
                    borderRadius: e.target.value as 'none' | 'light' | 'medium' | 'heavy'
                  })}
                  className="w-full p-2 border border-slate-300 rounded-md shadow-sm bg-white text-sm"
                >
                  <option value="none">Sem Arredondamento</option>
                  <option value="light">Levemente Arredondado</option>
                  <option value="medium">Arredondado</option>
                  <option value="heavy">Muito Arredondado</option>
                </select>
              </div>
            </div>
          </div>
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm font-medium text-slate-600 mb-2">Cabeçalho</label>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">URL da Logo</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={config.header?.logoUrl || ''}
                    onChange={e => onUpdate({
                      ...config,
                      header: {
                        ...config.header,
                        logoUrl: e.target.value || undefined
                      }
                    })}
                    className="flex-1 p-2 border border-slate-300 rounded-md text-sm"
                    placeholder="https://..."
                  />
                  <button
                    type="button"
                    onClick={() => openImageLib((url) => onUpdate({
                      ...config,
                      header: { ...config.header, logoUrl: url }
                    }))}
                    className="px-3 py-2 bg-slate-100 border border-slate-300 rounded-md hover:bg-slate-200 text-slate-600"
                    title="Selecionar imagem da biblioteca"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                    </svg>
                  </button>
                </div>
                {config.header?.logoUrl && (
                  <div className="mt-2 relative group w-16 h-16 bg-slate-100 rounded border overflow-hidden">
                    <img src={config.header.logoUrl} alt="Preview" className="w-full h-full object-contain" />
                    <button
                      type="button"
                      onClick={() => onUpdate({ ...config, header: { ...config.header, logoUrl: undefined } })}
                      className="absolute top-0 right-0 p-0.5 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                      title="Remover"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <label htmlFor="toggleBackButton" className="text-sm text-slate-700">Mostrar Botão Voltar</label>
                {(() => {
                  const enabled = (config.header?.showBackButton !== false); return (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={enabled}
                        onChange={() => onUpdate({ ...config, header: { ...config.header, showBackButton: !enabled } })}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  );
                })()}
              </div>
              <div className="flex items-center justify-between">
                <label htmlFor="toggleStepIndicator" className="text-sm text-slate-700">Mostrar Indicador de Etapa</label>
                {(() => {
                  const enabled = (config.header?.showStepIndicator !== false); return (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={enabled}
                        onChange={() => onUpdate({ ...config, header: { ...config.header, showStepIndicator: !enabled } })}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  );
                })()}
              </div>
              <div className="flex items-center justify-between">
                <label htmlFor="toggleCoverHide" className="text-sm text-slate-700">1ª Página em Modo Capa</label>
                {(() => {
                  const enabled = (config.header?.hideOnFirstPage === true); return (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={enabled}
                        onChange={() => onUpdate({ ...config, header: { ...config.header, hideOnFirstPage: !enabled } })}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  );
                })()}
              </div>
            </div>
          </div>
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm font-medium text-slate-600 mb-2">Validação</label>
            <div className="space-y-3">

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Mensagem de Campos Obrigatórios</label>
                <input
                  type="text"
                  value={config.validation?.requiredMessage || 'Preencha os campos obrigatórios antes de continuar.'}
                  onChange={e => onUpdate({
                    ...config,
                    validation: { ...(config.validation || {}), requiredMessage: e.target.value }
                  })}
                  className="w-full p-2 border border-slate-300 rounded-md text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Library Modal */}
      {imgLib.isOpen && (
        <ImageLibrary
          onSelect={(url) => {
            if (imgLib.callback) imgLib.callback(url);
            setImgLib({ isOpen: false });
          }}
          onClose={() => setImgLib({ isOpen: false })}
          initialUrl={config.header?.logoUrl}
        />
      )}
    </div>
  );
};

const GamificationEditor: React.FC<{ config: QuizConfig; onUpdate: (newConfig: QuizConfig) => void }> = ({ config, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const coinsEnabled = config.gamification?.coinsEnabled === true;
  const coinsPreText = config.gamification?.coinsPreText ?? 'Seu Saldo: ';
  const coinsPostText = config.gamification?.coinsPostText ?? 'ptos';

  return (
    <div className="bg-slate-100 rounded-lg mt-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-3 text-left"
        aria-expanded={isOpen}
      >
        <h3 className="font-bold text-base text-slate-800">Gamificação</h3>
        <svg
          className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-none' : 'max-h-0'}`}>
        <div className="px-4 pb-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-700">Pontos</div>
            <button
              onClick={() => onUpdate({
                ...config,
                gamification: { ...config.gamification, coinsEnabled: !coinsEnabled }
              })}
              aria-pressed={coinsEnabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${coinsEnabled ? 'bg-blue-600' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${coinsEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>
          {coinsEnabled && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Estilo</label>
                <select
                  value={config.gamification?.style || 'coins'}
                  onChange={(e) => onUpdate({
                    ...config,
                    gamification: { ...config.gamification, style: e.target.value as 'coins' | 'money' }
                  })}
                  className="w-full p-2 border border-slate-300 rounded-md text-sm"
                >
                  <option value="coins">Moedas</option>
                  <option value="money">Dinheiro</option>
                </select>
              </div>
              {config.gamification?.style === 'money' && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Símbolo da Moeda</label>
                  <input
                    type="text"
                    value={config.gamification?.currencySymbol ?? 'US$ '}
                    onChange={(e) => onUpdate({
                      ...config,
                      gamification: { ...config.gamification, currencySymbol: e.target.value }
                    })}
                    className="w-full p-2 border border-slate-300 rounded-md text-sm"
                    placeholder="Ex: R$ , US$ , € "
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Tipo de Animação</label>
                <select
                  value={config.gamification?.animationType || 'flying'}
                  onChange={(e) => onUpdate({
                    ...config,
                    gamification: { ...config.gamification, animationType: e.target.value as 'simple' | 'flying' }
                  })}
                  className="w-full p-2 border border-slate-300 rounded-md text-sm"
                >
                  <option value="flying">Voando (Centro ao Topo)</option>
                  <option value="simple">Simples (Apenas Topo)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Som ao Ganhar Pontos</label>
                <div className="flex gap-2">
                  <select
                    value={config.gamification?.sound || 'default'}
                    onChange={(e) => onUpdate({
                      ...config,
                      gamification: { ...config.gamification, sound: e.target.value as any }
                    })}
                    className="flex-1 p-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value="default">Padrão (8-bit)</option>
                    <option value="cash-register">Caixa Registradora</option>
                    <option value="bonus-points">Bonus Points</option>
                    <option value="collect-points">Collect Points</option>
                    <option value="plin-points">Plin</option>
                  </select>
                  <button
                    onClick={() => {
                      const sound = config.gamification?.sound || 'default';
                      if (sound === 'default') {
                        try {
                          const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
                          const ctx = new Ctx();
                          const o = ctx.createOscillator();
                          const g = ctx.createGain();
                          o.type = 'triangle';
                          o.frequency.setValueAtTime(700, ctx.currentTime);
                          g.gain.setValueAtTime(0.001, ctx.currentTime);
                          o.connect(g);
                          g.connect(ctx.destination);
                          o.start();
                          o.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.08);
                          g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.03);
                          g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
                          o.stop(ctx.currentTime + 0.3);
                        } catch { }
                      } else {
                        const audio = new Audio(`/sounds/${sound}.mp3`);
                        audio.volume = 0.6;
                        audio.play().catch(console.warn);
                      }
                    }}
                    className="p-2 bg-slate-100 border border-slate-300 rounded-md hover:bg-slate-200 text-slate-600"
                    title="Ouvir som"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-slate-600">Pré-Texto:</label>
                  <input
                    type="text"
                    value={coinsPreText}
                    onChange={(e) => onUpdate({ ...config, gamification: { ...config.gamification, coinsPreText: e.target.value } })}
                    className="border rounded px-2 py-1 text-sm"
                    placeholder="Seu Saldo: "
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-slate-600">Pós-Texto</label>
                  <input
                    type="text"
                    value={coinsPostText}
                    onChange={(e) => onUpdate({ ...config, gamification: { ...config.gamification, coinsPostText: e.target.value } })}
                    className="border rounded px-2 py-1 text-sm"
                    placeholder="ptos"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ScriptsEditor: React.FC<{ config: QuizConfig; onUpdate: (newConfig: QuizConfig) => void }> = ({ config, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const headVal = config.scripts?.head ?? '';
  const footerVal = config.scripts?.footer ?? '';
  return (
    <div className="bg-slate-100 rounded-lg mt-4">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center p-3 text-left" aria-expanded={isOpen}>
        <h3 className="font-bold text-base text-slate-800">Scripts</h3>
        <svg className={`w-5 h-5 text-slate-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
      </button>
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-none' : 'max-h-0'}`}>
        <div className="px-4 pb-4 space-y-2">
          <div>
            <div className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-2 py-1 mb-2 text-center">
              * UTMs já são tratadas e adicionadas aos links automaticamente
            </div>
            <label className="block text-xs font-medium text-slate-600 mb-1">&lt;head&gt;</label>
            <textarea rows={4} value={headVal} onChange={e => onUpdate({ ...config, scripts: { ...(config.scripts || {}), head: e.target.value } })} className="w-full p-2 border border-slate-300 rounded-md text-sm" placeholder="Tags ou scripts para inserir no &lt;head&gt;" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">&lt;footer&gt;</label>
            <textarea rows={4} value={footerVal} onChange={e => onUpdate({ ...config, scripts: { ...(config.scripts || {}), footer: e.target.value } })} className="w-full p-2 border border-slate-300 rounded-md text-sm" placeholder="Tags ou scripts para inserir no rodapé" />
          </div>
        </div>
      </div>
    </div>
  );
};

const SortablePageItem: React.FC<{
  page: Page;
  index: number;
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
}> = ({ page, index, selectedIndex, onSelect, onDelete, onDuplicate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: page.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      onClick={() => onSelect(index)}
      className={`flex justify-between items-center p-2 rounded-md cursor-pointer transition-colors ${selectedIndex === index ? 'bg-gray-200 text-gray-900' : 'bg-white hover:bg-slate-100'}`}
      title={page.title || `Página ${index + 1}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <button
          {...attributes}
          {...listeners}
          className="p-1 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing flex-shrink-0"
          title="Arrastar para reordenar"
        >
          ⋮⋮
        </button>
        <span className="font-medium truncate">{page.title || `Página ${index + 1}`}</span>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button
          onClick={(e) => { e.stopPropagation(); onDuplicate(index); }}
          className="p-1 rounded-full text-slate-400 hover:bg-blue-100 hover:text-blue-500"
          title="Duplicar página"
        >
          <CopyIcon className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(index); }}
          className={`p-1 rounded-full ${selectedIndex === index ? 'text-slate-400 hover:bg-red-100 hover:text-red-500' : 'text-slate-400 hover:bg-red-100 hover:text-red-500'}`}
          title="Excluir página"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
};

const PageList: React.FC<{ pages: Page[]; selectedIndex: number | null; onSelect: (index: number) => void; onAdd: () => void; onDelete: (index: number) => void; onDuplicate: (index: number) => void; onReorder?: (oldIndex: number, newIndex: number) => void; }> = ({ pages, selectedIndex, onSelect, onAdd, onDelete, onDuplicate, onReorder }) => {


  return (
    <div className="p-4">
      <h3 className="font-bold text-lg mb-4 text-slate-800">Páginas do Quiz</h3>
      <DndContext
        sensors={useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }))}
        collisionDetection={closestCenter}
        onDragEnd={(event) => {
          const { active, over } = event;
          if (active.id !== over?.id && onReorder) {
            const oldIndex = pages.findIndex(page => page.id === active.id);
            const newIndex = pages.findIndex(page => page.id === over?.id);
            onReorder(oldIndex, newIndex);
          }
        }}
      >
        <SortableContext items={pages.map(page => page.id)} strategy={verticalListSortingStrategy}>
          <ul className="space-y-2">
            {pages.map((page, index) => (
              <SortablePageItem
                key={page.id}
                page={page}
                index={index}
                selectedIndex={selectedIndex}
                onSelect={onSelect}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      <div className="mt-4">
        <button onClick={onAdd} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors">
          <PlusIcon className="w-5 h-5" /> Adicionar Página
        </button>
      </div>
    </div>
  );
};

const OptionsEditor: React.FC<{ options: Option[], onUpdate: (options: Option[]) => void }> = ({ options, onUpdate }) => {

  const handleUpdate = (index: number, field: keyof Option, value: string) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    onUpdate(newOptions);
  }

  const addOption = () => {
    onUpdate([...options, { id: generateId(), text: `Nova Opção ${options.length + 1}` }]);
  }

  const removeOption = (index: number) => {
    onUpdate(options.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-slate-600">Opções de Resposta</label>
      {options.map((opt, index) => (
        <div key={opt.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-md">
          <div className="flex-grow">
            <input type="text" placeholder="Texto da opção" value={opt.text} onChange={e => handleUpdate(index, 'text', e.target.value)} className="w-full p-2 border border-slate-300 rounded-md shadow-sm text-sm" />
            <input type="text" placeholder="URL da Imagem (opcional)" value={opt.imageUrl || ''} onChange={e => handleUpdate(index, 'imageUrl', e.target.value)} className="w-full p-2 border border-slate-300 rounded-md shadow-sm mt-1 text-sm" />
          </div>
          <button onClick={() => removeOption(index)} className="p-2 text-slate-400 hover:text-red-500"><TrashIcon className="w-5 h-5" /></button>
        </div>
      ))}
      <button onClick={addOption} className="mt-2 text-sm text-sky-600 font-semibold hover:text-sky-700">+ Adicionar Opção</button>
    </div>
  )
}

const PageEditor: React.FC<{ page: Page, onUpdate: (updatedPage: Page) => void }> = ({ page, onUpdate }) => {


  const updateField = (field: keyof Page, value: any) => {
    onUpdate({ ...page, [field]: value });
  };

  const handleElementDragStart = (elementType: ElementType) => {
    // O drag-and-drop é tratado no PageElementsEditor
  };

  const handleElementDrop = (elementType: ElementType, index: number) => {
    const newElement = createElement(elementType);
    const currentElements = page.elements || [];
    const newElements = [...currentElements];
    newElements.splice(index, 0, newElement);
    updateField('elements', newElements);
  };

  const createElement = (type: ElementType): PageElement => {
    const baseElement = {
      id: generateId(),
      type,
    };

    switch (type) {
      case ElementType.Title:
        return { ...baseElement, type: ElementType.Title, content: 'Novo Título' };
      case ElementType.Text:
        return { ...baseElement, type: ElementType.Text, content: 'Novo texto...' };
      case ElementType.Alert:
        return { ...baseElement, type: ElementType.Alert, content: 'Mensagem do alerta', variant: 'info' };
      case ElementType.Timer:
        return { ...baseElement, type: ElementType.Timer, content: '⏱️ Faltam <strong>[time]</strong> minutos para o fim da promoção', durationSeconds: 60, variant: 'error', align: 'center' } as any;
      case ElementType.List:
        return {
          ...baseElement,
          type: ElementType.List,
          items: [
            { title: 'Benefício 1', description: 'Descrição do benefício com <strong>HTML</strong> e emojis 🙂' },
            { title: 'Benefício 2', description: 'Texto de apoio com detalhes e exemplos.' },
            { title: 'Benefício 3', description: 'Use <em>ênfase</em>, quebras de linha e símbolos.' }
          ],
          titlePosition: 'top',
          layout: 'list',
          border: 'small',
          shadow: 'none',
          spacing: 'medium',
          textAlign: 'left'
        } as any;
      case ElementType.Price:
        return {
          ...baseElement,
          type: ElementType.Price,
          items: [
            { title: 'Plano Start', value: 'R$ 59,90', suffix: 'à vista', color: 'neutral', priceType: 'illustrative', enableCheckbox: false },
            { title: 'Plano Pro', prefix: '10% off', value: 'R$ 89,90', suffix: 'à vista', topHighlight: '⭐ Mais Escolhido', color: 'neutral', priceType: 'illustrative', enableCheckbox: false },
            { title: 'Plano Gold', prefix: '10% off', value: 'R$ 129,90', suffix: 'à vista', color: 'neutral', priceType: 'illustrative', enableCheckbox: false }
          ],
          layout: 'horizontal',
          verticalAlign: 'bottom',
          border: 'small',
          shadow: 'none'
        } as any;
      case ElementType.Options:
        return {
          ...baseElement,
          type: ElementType.Options,
          title: 'Qual a questão a ser respondida?',
          description: 'Digite aqui uma descrição de ajuda para introduzir o usuário à questão.',
          options: [
            { id: generateId(), text: 'Opção 1' },
            { id: generateId(), text: 'Opção 2' },
            { id: generateId(), text: 'Opção 3' },
            { id: generateId(), text: 'Opção 4' }
          ],
          multiple: false,
          layout: 'grid2',
          spacing: 'medium',
          style: 'simple',
          checkStyle: 'traditional',
          checkPosition: 'before',
          border: 'small',
          shadow: 'none'
        };
      case ElementType.QA:
        return {
          ...baseElement,
          type: ElementType.QA,
          items: [
            { question: 'Qual a primeira dúvida a ser resolvida?', answer: 'Este é apenas um texto de exemplo utilizado para ilustrar como a resposta de uma dúvida frequente será exibida nesta seção.' },
            { question: 'Descreva outra dúvida a ser resolvida.', answer: 'Texto genérico de demonstração. Serve apenas como modelo visual para representar uma resposta real a ser inserida posteriormente.' }
          ],
          openFirst: true,
          allowMultiple: false,
          border: 'small',
          shadow: 'none'
        } as any;
      case ElementType.Video:
        return { ...baseElement, type: ElementType.Video, url: '', title: '' };
      case ElementType.Button:
        return { ...baseElement, type: ElementType.Button, text: 'Continuar' };
      case ElementType.Image:
        return { ...baseElement, type: ElementType.Image, url: '', alt: '' };
      case ElementType.BeforeAfter:
        return { ...baseElement, type: ElementType.BeforeAfter, beforeUrl: '', afterUrl: '', beforeLabel: 'Antes', afterLabel: 'Depois', startPercent: 50, orientation: 'vertical', align: 'center', widthPercent: 100, border: 'small', shadow: 'none' } as any;
      case ElementType.Audio:
        return { ...baseElement, type: ElementType.Audio, url: '', question: '', style: 'default' };
      case ElementType.Spacer:
        return { ...baseElement, type: ElementType.Spacer, height: 20 };
      case ElementType.Loading:
        return { ...baseElement, type: ElementType.Loading, title: 'Analisando...', subtitle: 'Estamos analisando suas respostas...', durationSeconds: 10, showPercent: true } as any;
      case ElementType.Testimonial:
        return {
          ...baseElement,
          type: ElementType.Testimonial,
          name: 'Nome da pessoa',
          info: '@usuario',
          stars: 5,
          text: '"Escreva aqui o depoimento..."',
          shadow: 'small'
        } as any;
      case ElementType.Form:
        return {
          ...baseElement,
          type: ElementType.Form,
          fieldId: '',
          title: 'Texto',
          fieldType: 'text',
          placeholder: '',
          mask: '',
          required: false,
          fontSize: 'normal',
          labelPosition: 'tradicional',
          align: 'left',
          border: 'small',
          shadow: 'none'
        } as any;
      case ElementType.FbComments:
        return {
          ...baseElement,
          type: ElementType.FbComments,
          comments: [
            { id: generateId(), name: 'Maria Silva', timeAgo: '5 min ago', content: 'Adorei o conteúdo! Muito útil.', avatarUrl: '' },
            { id: generateId(), name: 'João Santos', timeAgo: '15 min ago', content: 'Excelente explicação, parabéns!', avatarUrl: '' }
          ],
          totalComments: 124,
          border: 'small',
          shadow: 'none'
        } as any;
      default:
        throw new Error(`Tipo de elemento desconhecido: ${type}`);
    }
  };

  const updateElements = (elements: PageElement[]) => {
    updateField('elements', elements);
  };

  const handleAddModel = (modelName: string) => {
    console.log(`[App] handleAddModel called with: ${modelName}`);
    if (modelName === 'cover') {
      console.log('[App] Creating elements for cover model...');
      const title = createElement(ElementType.Title);
      (title as any).content = 'Título da Capa';

      const image = createElement(ElementType.Image);

      const button = createElement(ElementType.Button);
      (button as any).text = 'Começar Agora';
      (button as any).height = 'large';
      (button as any).animation = 'pulse';

      const currentElements = page.elements || [];
      const newElements = [...currentElements, title, image, button];
      console.log(`[App] Adding ${newElements.length - currentElements.length} new elements to page.`);
      updateField('elements', newElements);
    } else {
      console.warn(`[App] Unknown model name: ${modelName}`);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg h-full overflow-y-auto">
      <div className="space-y-6">


        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <React.Suspense fallback={<LoadingSpinner />}>
              <ElementPalette onDragStart={handleElementDragStart} onAddModel={handleAddModel} />
            </React.Suspense>
          </div>

          <div className="lg:col-span-2">
            <h3 className="font-bold text-lg mb-4 text-slate-800">Elementos da Página</h3>
            <React.Suspense fallback={<LoadingSpinner />}>
              <PageElementsEditor
                elements={page.elements || []}
                onUpdate={updateElements}
                onElementDrop={handleElementDrop}
              />
            </React.Suspense>
          </div>
        </div>
      </div>
    </div>
  );
};

const GeneratedCodeModal: React.FC<{ code: string; onClose: () => void }> = ({ code, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-800">Arquivo `quizData.ts` Gerado</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">&times;</button>
        </div>
        <div className="p-4 flex-grow overflow-y-auto">
          <p className="text-sm text-slate-600 mb-4">Copie este código e salve-o como <code className="bg-slate-100 p-1 rounded">quizData.ts</code> em seu projeto para ativar o novo quiz.</p>
          <pre className="bg-slate-800 text-white p-4 rounded-md text-sm overflow-x-auto">
            <code>{code}</code>
          </pre>
        </div>
        <div className="p-4 border-t flex justify-end">
          <button onClick={handleCopy} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors">
            <CopyIcon className="w-5 h-5" /> {copied ? 'Copiado!' : 'Copiar para Área de Transferência'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmDeleteModal: React.FC<{
  open: boolean;
  name: string;
  quizCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, name, quizCount, onCancel, onConfirm }) => {
  const [confirmationText, setConfirmationText] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setConfirmationText('');
      setError('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (confirmationText.trim() !== name.trim()) {
      setError('O nome digitado não corresponde exatamente ao nome do projeto.');
      return;
    }
    onConfirm();
  };

  const handleInputChange = (value: string) => {
    setConfirmationText(value);
    if (error) setError('');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 text-red-600">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h2 className="text-lg font-bold text-slate-800">Remover Projeto</h2>
          </div>
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-800">&times;</button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-slate-700">Você está prestes a remover o projeto <span className="font-semibold">"{name}"</span>.</p>
          {quizCount > 0 ? (
            <p className="text-slate-600">Isso irá excluir permanentemente todos os {quizCount} quizzes deste projeto. Esta ação não pode ser desfeita.</p>
          ) : (
            <p className="text-slate-600">Esta ação não pode ser desfeita.</p>
          )}

          <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800 font-medium mb-2">Confirmação crítica necessária</p>
            <p className="text-sm text-red-700 mb-3">Digite exatamente o nome do projeto para confirmar a exclusão:</p>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Digite: <span className="font-mono bg-slate-100 px-2 py-1 rounded text-sm">"{name}"</span>
            </label>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleConfirm();
                }
              }}
              className={`w-full px-3 py-2 border rounded-md text-sm font-mono ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
                } focus:outline-none focus:ring-1`}
              placeholder={`Digite "${name}"`}
              autoFocus
            />
            {error && (
              <p className="text-xs text-red-600 mt-1">{error}</p>
            )}
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 bg-slate-100 text-slate-800 rounded-md border hover:bg-slate-200">Cancelar</button>
          <button
            onClick={handleConfirm}
            disabled={!confirmationText.trim() || confirmationText.trim() !== name.trim()}
            className={`px-4 py-2 font-semibold rounded-md ${confirmationText.trim() === name.trim()
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-slate-300 text-slate-500 cursor-not-allowed'
              }`}
          >
            Remover Projeto
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmDeleteQuizModal: React.FC<{
  open: boolean;
  name: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, name, onCancel, onConfirm }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 text-red-600">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h2 className="text-lg font-bold text-slate-800">Excluir Quiz</h2>
          </div>
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-800">&times;</button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-slate-700">Você está prestes a excluir o quiz <span className="font-semibold">{name}</span>.</p>
          <p className="text-slate-600">Esta ação não pode ser desfeita.</p>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 bg-slate-100 text-slate-800 rounded-md border hover:bg-slate-200">Cancelar</button>
          <button onClick={onConfirm} className="px-4 py-2 bg-red-600 text-white font-semibold rounded-md hover:bg-red-700">Excluir</button>
        </div>
      </div>
    </div>
  );
};


const ErrorModal: React.FC<{ open: boolean; title: string; message: string; onClose: () => void }> = ({ open, title, message, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 5a7 7 0 100 14 7 7 0 000-14z" /></svg>
            </div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">{title}</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">&times;</button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-slate-700 dark:text-slate-300">{message}</p>
        </div>
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-slate-600 dark:bg-slate-700 text-white font-semibold rounded-md hover:bg-slate-700 dark:hover:bg-slate-600">Fechar</button>
        </div>
      </div>
    </div>
  );
};

const SuccessModal: React.FC<{ open: boolean; title: string; message: string; onClose: () => void }> = ({ open, title, message, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">{title}</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">&times;</button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-slate-700 dark:text-slate-300">{message}</p>
        </div>
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-green-600 dark:bg-green-700 text-white font-semibold rounded-md hover:bg-green-700 dark:hover:bg-green-600">Fechar</button>
        </div>
      </div>
    </div>
  );
};



const DuplicateLanguageModal: React.FC<{
  open: boolean;
  quizName: string;
  settings: any;
  onCancel: () => void;
  onConfirm: (sourceLang: string, targetLang: string) => void;
}> = ({ open, quizName, settings, onCancel, onConfirm }) => {
  const [sourceLang, setSourceLang] = React.useState('Português (pt_BR)');
  const [targetLang, setTargetLang] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setSourceLang('Português (pt_BR)');
      setTargetLang('');
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-slate-800">Duplicar em Outro Idioma</h2>
          </div>
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-800">&times;</button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-slate-700">Duplicar o quiz <span className="font-semibold">"{quizName}"</span> para outro idioma.</p>

          {!settings?.gemini_api_key && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">
                ⚠️ Você precisa configurar uma chave da API Gemini para usar esta funcionalidade.
                Acesse o menu Configurações para configurar sua chave.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Idioma de Origem</label>
            <input
              type="text"
              value={sourceLang}
              onChange={(e) => setSourceLang(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Idioma de Destino</label>
            <input
              type="text"
              value={targetLang}
              onChange={(e) => setTargetLang(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-md"
              placeholder="Inglês (en)"
            />
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 bg-slate-100 text-slate-800 rounded-md border hover:bg-slate-200">Cancelar</button>
          <button
            onClick={() => {
              if (sourceLang && targetLang && sourceLang !== targetLang) {
                // Extrair códigos de idioma dos formatos "Nome (código)"
                const extractLangCode = (lang: string) => {
                  const match = lang.match(/\(([^)]+)\)/);
                  return match ? match[1] : lang.trim();
                };
                const sourceCode = extractLangCode(sourceLang);
                const targetCode = extractLangCode(targetLang);
                onConfirm(sourceCode, targetCode);
              }
            }}
            disabled={!sourceLang || !targetLang || sourceLang === targetLang || !settings?.gemini_api_key}
            className="px-4 py-2 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
          >
            Duplicar
          </button>
        </div>
      </div>
    </div>
  );
};

const ChangeSlugModal: React.FC<{
  open: boolean;
  currentSlug: string;
  projects: ProjectMeta[];
  currentQuizId: string;
  onCancel: () => void;
  onConfirm: (newSlug: string) => void;
}> = ({ open, currentSlug, projects, currentQuizId, onCancel, onConfirm }) => {
  const [newSlug, setNewSlug] = React.useState(currentSlug);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setNewSlug(currentSlug);
      setError('');
    }
  }, [open, currentSlug]);

  const handleSave = () => {
    const slug = newSlug.trim().toLowerCase();

    // Validations
    if (!slug) {
      setError('O slug é obrigatório.');
      return;
    }

    if (!isValidSlug(slug)) {
      setError('O slug deve conter apenas letras minúsculas, números e hífens, sem espaços ou caracteres especiais.');
      return;
    }

    // Check uniqueness across all projects/quizzes of the user
    let isUnique = true;
    for (const p of projects) {
      for (const q of p.quizzes) {
        // Skip the current quiz itself
        if (q.id === currentQuizId) continue;

        const qSlug = q.slug || toSlug(q.name);
        if (qSlug === slug) {
          isUnique = false;
          break;
        }
      }
      if (!isUnique) break;
    }

    if (!isUnique) {
      setError('Este slug já está em uso por outro quiz.');
      return;
    }

    onConfirm(slug);
  };

  if (!open) return null;
  console.log('ChangeSlugModal OPENING', { newSlug });
  // alert('Modal Rendered!');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100 text-indigo-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
            </div>
            <h2 className="text-lg font-bold text-slate-800">Alterar Endereço (Slug)</h2>
          </div>
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-800">&times;</button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-sm text-slate-600">
            O slug define a URL do seu quiz. Alterar isso invalidará links antigos.
          </p>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Novo Slug</label>
            <div className="flex items-center border border-slate-300 rounded-md overflow-hidden bg-slate-50">
              <input
                type="text"
                value={newSlug}
                onChange={(e) => {
                  const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                  setNewSlug(val);
                  if (error) setError('');
                }}
                className="flex-1 px-3 py-2 bg-white text-slate-800 text-sm focus:outline-none"
                placeholder="meu-quiz-incrivel"
              />
            </div>
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 bg-slate-100 text-slate-800 rounded-md border hover:bg-slate-200 text-sm font-medium">Cancelar</button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 text-sm"
          >
            Salvar Alteração
          </button>
        </div>
      </div>
    </div>
  );
};

const ShareQuizModal: React.FC<{
  open: boolean;
  shareCode: string;
  onClose: () => void;
}> = ({ open, shareCode, onClose }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-slate-800">Compartilhar Quiz</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">&times;</button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-slate-700">Compartilhe este código para permitir que outros importem o quiz:</p>

          <div className="flex gap-2">
            <input
              type="text"
              value={shareCode}
              readOnly
              className="flex-1 px-3 py-2 bg-slate-50 border border-slate-300 rounded-md text-sm font-mono"
            />
            <button
              onClick={handleCopy}
              className={`p-2 rounded-md transition-colors ${copied
                ? 'bg-green-600 text-white'
                : 'bg-slate-600 text-white hover:bg-slate-700'
                }`}
              title="Copiar para área de transferência"
            >
              {copied ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>

          {copied && (
            <p className="text-green-600 text-sm font-medium">Código copiado para a área de transferência!</p>
          )}
        </div>
        <div className="p-4 border-t">
          <button onClick={onClose} className="w-full px-4 py-2 bg-slate-600 text-white font-semibold rounded-md hover:bg-slate-700">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

const TranslationLoadingModal: React.FC<{ open: boolean; title?: string; subtitle?: string }> = ({ open, title = 'Traduzindo Quiz', subtitle = 'Por favor, aguarde enquanto o conteúdo é traduzido.' }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[200]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm">
        <div className="p-6 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">{title}</h3>
          <p className="text-slate-600">{subtitle}</p>
        </div>
      </div>
    </div>
  );
};


const GenerateQuizModal: React.FC<{
  open: boolean;
  projectId: string;
  projectName: string;
  settings: any;
  existingSlugs: string[];
  onCancel: () => void;
  onGenerate: (data: any) => void;
}> = ({ open, projectId, projectName, settings, existingSlugs, onCancel, onGenerate }) => {
  // Estados para os campos (Modelagem sempre)
  const [aiQuizName, setAiQuizName] = React.useState('');
  const [aiQuizSlug, setAiQuizSlug] = React.useState('');
  // Reutilizando aiOffer para armazenar o resultado da modelagem/input de texto, para minimizar mudanças na lógica
  const [aiOffer, setAiOffer] = React.useState('');

  const [copiedPrompt, setCopiedPrompt] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);
  const [slugError, setSlugError] = React.useState('');

  React.useEffect(() => {
    if (open) {
      // Reset form when modal opens
      setAiQuizName('');
      setAiQuizSlug('');
      setAiOffer('');
      setShowErrors(false);
      setSlugError('');
    }
  }, [open]);

  const handleGenerate = () => {
    // Validar campos obrigatórios para Modelagem
    if (!aiQuizName.trim() || !aiOffer.trim()) {
      setShowErrors(true);
      return;
    }

    if (aiQuizSlug && existingSlugs.some(s => s.toLowerCase() === aiQuizSlug.trim().toLowerCase())) {
      setSlugError('Você já possui um quiz com este slug.');
      return;
    }

    onGenerate({
      type: 'modeling',
      quizName: aiQuizName.trim(),
      quizSlug: aiQuizSlug.trim(),
      modelingResult: aiOffer.trim()
    });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 text-green-600">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Gerar Quiz com I.A.</h2>
              <p className="text-sm text-slate-600">Projeto: {projectName}</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-slate-500 hover:text-slate-800">&times;</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-md text-sm mb-4">
            <p className="font-semibold mb-1">Criação por Modelagem ou Instruções Textuais</p>
            <p>Cole abaixo o JSON ou texto estruturado para gerar seu quiz automaticamente.</p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nome do Quiz *</label>
              <input
                type="text"
                value={aiQuizName}
                onChange={e => { setAiQuizName(e.target.value); if (showErrors) setShowErrors(false); }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${showErrors && !aiQuizName.trim() ? 'border-red-500' : 'border-slate-300'}`}
                placeholder="Ex.: Quiz de Oferta X"
              />
              {showErrors && !aiQuizName.trim() && <p className="text-red-500 text-xs mt-1">Campo obrigatório</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Slug</label>
              <input
                type="text"
                value={aiQuizSlug}
                onChange={e => { setAiQuizSlug(e.target.value); setSlugError(''); }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${slugError ? 'border-red-500' : 'border-slate-300'}`}
                placeholder="ex.: quiz-oferta-x"
              />
              {slugError && <p className="text-red-500 text-xs mt-1">{slugError}</p>}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-sm font-medium text-slate-700">Instruções de Modelagem</label>
                <div className="relative group">
                  <button className="text-slate-400 hover:text-slate-600 transition-colors">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                  <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 px-3 py-2 bg-slate-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 w-64">
                    Utilize o Manus.im (https://manus.im/) ou outro Agent para modelar um quiz concorrente com esse Prompt.
                  </div>
                </div>
              </div>
              <textarea
                value="Atue como um especialista em Marketing Digital com vasta experiência em espionagem e modelagem de ofertas. Estou criando um funil de vendas que será um Quiz. Quero que você acesse o site a seguir, que corresponde a um quiz concorrente e modele, especificando pra mim todas as etapas, opções e recursos de cada etapa, caminhos possíveis, resultados e funcionamento. Preciso saber cada página dele, o conteúdo completo de cada página (incluindo opções de respostas quando forem perguntas), recursos gráficos usados, etc. Seja o mais completo quanto possível. Quero que vc me entregue TODO o conteúdo, incluindo capa e todos os textos. \n\nURL: "
                readOnly
                className="w-full h-32 p-3 border border-slate-300 rounded-md bg-slate-50 text-slate-600 font-mono text-sm"
              />
              <div className="mt-2 flex justify-end">
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText('Atue como um especialista em Marketing Digital com vasta experiência em espionagem e modelagem de ofertas. Estou criando um funil de vendas que será um Quiz. Quero que você acesse o site a seguir, que corresponde a um quiz concorrente e modele, especificando pra mim todas as etapas, opções e recursos de cada etapa, caminhos possíveis, resultados e funcionamento. Preciso saber cada página dele, o conteúdo completo de cada página (incluindo opções de respostas quando forem perguntas), recursos gráficos usados, etc. Seja o mais completo quanto possível. Quero que vc me entregue TODO o conteúdo, incluindo capa e todos os textos. \n\nURL: ');
                      setCopiedPrompt(true);
                      setTimeout(() => setCopiedPrompt(false), 2000);
                    } catch (error) {
                      console.error('Erro ao copiar:', error);
                    }
                  }}
                  className={`px-4 py-2 text-white rounded-md text-sm transition-colors ${copiedPrompt
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-slate-500 hover:bg-slate-600'
                    }`}
                >
                  {copiedPrompt ? 'Copiado!' : 'Copiar Prompt'}
                </button>
              </div>
              <label className="block text-sm font-medium text-slate-700 mb-2 mt-4">Resultado da Modelagem ou Estrutura (Texto/JSON): *</label>
              <p className="text-xs text-slate-500 mb-2">
                Cole aqui o resultado da modelagem (seja texto ou JSON de outro agente).
              </p>
              <textarea
                value={aiOffer}
                onChange={e => { setAiOffer(e.target.value); if (showErrors) setShowErrors(false); }}
                placeholder=""
                className={`w-full h-48 p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${showErrors && !aiOffer.trim() ? 'border-red-500' : 'border-slate-300'}`}
              />
              {showErrors && !aiOffer.trim() && <p className="text-red-500 text-xs mt-1">Campo obrigatório</p>}
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 bg-slate-100 text-slate-800 rounded-md border hover:bg-slate-200">
            Cancelar
          </button>
          <button
            onClick={handleGenerate}
            disabled={!settings?.gemini_api_key || settings.gemini_api_key.trim() === ''}
            title={(!settings?.gemini_api_key || settings.gemini_api_key.trim() === '') ? 'Configure sua chave API Gemini nas configurações para usar este recurso' : ''}
            className={`px-4 py-2 font-semibold rounded-md ${(!settings?.gemini_api_key || settings.gemini_api_key.trim() === '')
              ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700'
              }`}
          >
            Gerar Quiz
          </button>
        </div>
      </div>
    </div>
  );
};

const QuizPreview: React.FC<{ page: Page | undefined, config: QuizConfig, currentStep: number, totalSteps: number, onNextPage?: () => void, onPrevPage?: () => void }> = ({ page, config, currentStep, totalSteps, onNextPage, onPrevPage }) => {

  if (!page) {
    return (
      <div className="flex items-center justify-center h-full p-4 text-center">
        <p className="text-slate-500">Nenhuma página selecionada para pré-visualizar.</p>
      </div>
    );
  }

  const [coins, setCoins] = React.useState(0);
  const [confettiOn, setConfettiOn] = React.useState(false);
  const confettiTimeoutsRef = React.useRef<number[]>([]);
  const [confettiKey, setConfettiKey] = React.useState(0);
  const [coinAnimOn, setCoinAnimOn] = React.useState(false);
  const [coinAward, setCoinAward] = React.useState(0);
  const [coinKeyP, setCoinKeyP] = React.useState('coin-fly-p');
  const [coinDxP, setCoinDxP] = React.useState(180);
  const [coinDyP, setCoinDyP] = React.useState(-250);
  const [coinStartLeftP, setCoinStartLeftP] = React.useState<number | null>(null);
  const [coinStartTopP, setCoinStartTopP] = React.useState<number | null>(null);
  const [showScorePopup, setShowScorePopup] = React.useState(false);
  const [popupContent, setPopupContent] = React.useState({ text: '', footer: '' });
  const [selectedOption, setSelectedOption] = React.useState<string | string[] | undefined>(undefined);
  React.useEffect(() => {
    setSelectedOption(undefined);
  }, [page?.id]);

  // Limpar timeouts ao mudar de página
  React.useEffect(() => {
    setConfettiOn(false);
    confettiTimeoutsRef.current.forEach(id => clearTimeout(id));
    confettiTimeoutsRef.current = [];
  }, [page?.id]);

  const [activeConfettiType, setActiveConfettiType] = React.useState<'exit' | 'entry'>('exit');

  const triggerConfetti = (type: 'exit' | 'entry' = 'exit') => {
    setActiveConfettiType(type);
    setConfettiKey(prev => prev + 1);
    setConfettiOn(true);
  };

  // Trigger confetti on entry if configured
  React.useEffect(() => {
    if (page?.confettiOnEntryConfig?.enabled) {
      // Pequeno delay para garantir que a página renderizou
      const t = setTimeout(() => triggerConfetti('entry'), 100);
      return () => clearTimeout(t);
    }
  }, [page?.id]);

  // Memoizar partículas de confetti
  const confettiParticles = React.useMemo(() => {
    if (!confettiOn) return null;

    const config = activeConfettiType === 'entry'
      ? (page.confettiOnEntryConfig || {})
      : (page.confettiConfig || {});
    const direction = config.direction || 'center';
    const amountBase = config.amount === 'high' ? 100 : config.amount === 'low' ? 30 : 60;
    const amount = direction === 'sides' ? amountBase * 2.5 : amountBase;
    const style = config.style || 'paper';

    const particles = Array.from({ length: amount }).map((_, i) => {
      let startX = '50%';
      let startY = '50%';
      let angle, dist;

      if (direction === 'top') {
        startX = `${Math.random() * 100}%`;
        startY = '-10%';
        angle = Math.PI / 2 + (Math.random() - 0.5) * 0.5;
        dist = window.innerHeight + 100;
      } else if (direction === 'sides') {
        const side = i % 2 === 0 ? 0 : 100;
        startX = `${side}%`;
        startY = `${60 + Math.random() * 30}%`; // Start from lower sides (60-90%)

        // Angle: Sweep towards center
        if (side === 0) {
          // Left side: Aim between -10 and -80 degrees
          angle = -0.2 - (Math.random() * 1.2);
        } else {
          // Right side: Aim between -170 and -100 degrees
          angle = -Math.PI + 0.2 + (Math.random() * 1.2);
        }

        dist = config.amount === 'high' ? 600 + Math.random() * 400 : 400 + Math.random() * 300;
      } else {
        angle = Math.random() * Math.PI * 2;
        dist = config.amount === 'high' ? 200 + Math.random() * 350 : 120 + Math.random() * 220;
      }

      let colors;
      if (style === 'stars') {
        colors = ['#FFD700', '#FFC107', '#FFEB3B', '#F9A825', '#FFB300'];
      } else if (style === 'fireworks') {
        colors = ['#FF1744', '#F50057', '#D500F9', '#651FFF', '#00E5FF', '#00E676', '#FFD600', '#FF6D00'];
      } else {
        colors = ['#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6'];
      }

      const baseSize = style === 'stars' ? 16 : style === 'fireworks' ? 4 : 5;
      const sizeVariation = style === 'stars' ? 14 : style === 'fireworks' ? 3 : 5;

      const baseDuration = direction === 'sides' ? 2500 : (style === 'fireworks' ? 1000 : 700);
      const varDuration = direction === 'sides' ? 1000 : 500;

      return {
        startX,
        startY,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist,
        duration: baseDuration + Math.random() * varDuration,
        size: baseSize + Math.random() * sizeVariation,
        delay: Math.random() * 60,
        bg: colors[i % colors.length],
        startRot: Math.floor(Math.random() * 360),
        endRot: Math.floor(Math.random() * 360)
      };
    });

    return { particles, style };
  }, [confettiKey, page, activeConfettiType]);
  const [plusLeftP, setPlusLeftP] = React.useState<number | null>(null);
  const [plusTopP, setPlusTopP] = React.useState<number | null>(null);
  const [plusKeyP, setPlusKeyP] = React.useState('plus-fly-p');
  const [plusDxP, setPlusDxP] = React.useState(0);
  const [plusDyP, setPlusDyP] = React.useState(0);
  const mockOnNext = () => {
    if (onNextPage && currentStep < totalSteps - 1) {
      const gm = config.gamification || {};
      if (gm.coinsEnabled) {
        const soundType = config.gamification?.sound || 'default';

        if (soundType === 'default') {
          try {
            const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
            const ctx = new Ctx();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = 'triangle';
            o.frequency.setValueAtTime(700, ctx.currentTime);
            g.gain.setValueAtTime(0.001, ctx.currentTime);
            o.connect(g);
            g.connect(ctx.destination);
            o.start();
            o.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.08);
            g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.03);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
            o.stop(ctx.currentTime + 0.3);
          } catch { }
        } else {
          const audio = new Audio(`/sounds/${soundType}.mp3`);
          audio.volume = 0.6;
          audio.play().catch(e => console.warn('⚠️ [QuizPreview] Erro ao tocar som:', e));
        }
        const t = document.querySelector('[data-coin-target="true"][data-coin-context="live"]') as HTMLElement | null;
        if (t) {
          const r = t.getBoundingClientRect();
          const centerX = window.innerWidth / 2;
          const centerY = window.innerHeight / 2;
          const targetX = r.left + r.width / 2;
          const targetY = r.top + r.height / 2;
          const dx = targetX - centerX;
          const dy = targetY - centerY;
          setCoinDxP(dx);
          setCoinDyP(dy);
          setCoinKeyP(prev => prev + 'x');
        }
        setCoinAward(1);
        setCoinAnimOn(true);
        if (page.scorePopupConfig?.enabled) {
          setPopupContent({
            text: page.scorePopupConfig.text || 'Foi adicionado ao seu saldo!',
            footer: page.scorePopupConfig.footer || 'Continue para ganhar mais! 🚀'
          });
          setShowScorePopup(true);
        }
        setTimeout(() => setCoins(c => c + 1), 950);
        setTimeout(() => setCoinAnimOn(false), 1100);
      }
      // Disparar confetti apenas se houver elemento Confetti na página
      const hasConfetti = page.confettiConfig?.enabled;
      if (hasConfetti) {
        triggerConfetti();
      }
      const tm = window.setTimeout(() => {
        onNextPage();
      }, hasConfetti ? 500 : 0);
      confettiTimeoutsRef.current.push(tm);

    }
  };

  const isMultipleChoice = (p: Page | undefined) => {
    if (!p) return false;
    return p.elements?.some(el => el.type === ElementType.Options && (el as any).multiple === true) || false;
  };

  const handleOptionSelect = (value: string | string[]) => {
    const multiple = isMultipleChoice(page);
    setSelectedOption(value);
    if (!multiple && onNextPage && currentStep < totalSteps - 1) {
      const gm = config.gamification || {};
      if (gm.coinsEnabled) {
        const soundType = config.gamification?.sound || 'default';

        if (soundType === 'default') {
          try {
            const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
            const ctx = new Ctx();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.type = 'triangle';
            o.frequency.setValueAtTime(700, ctx.currentTime);
            g.gain.setValueAtTime(0.001, ctx.currentTime);
            o.connect(g);
            g.connect(ctx.destination);
            o.start();
            o.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.08);
            g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.03);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
            o.stop(ctx.currentTime + 0.3);
          } catch { }
        } else {
          const audio = new Audio(`/sounds/${soundType}.mp3`);
          audio.volume = 0.6;
          audio.play().catch(e => console.warn('⚠️ [QuizPreview] Erro ao tocar som:', e));
        }
        const t = document.querySelector('[data-coin-target="true"][data-coin-context="live"]') as HTMLElement | null;
        if (t) {
          const r = t.getBoundingClientRect();
          const centerX = window.innerWidth / 2;
          const centerY = window.innerHeight / 2;
          const targetX = r.left + r.width / 2;
          const targetY = r.top + r.height / 2;
          const dx = targetX - centerX;
          const dy = targetY - centerY;
          setCoinDxP(dx);
          setCoinDyP(dy);
          setCoinKeyP(prev => prev + 'x');
        }
        setCoinAward(1);
        setCoinAnimOn(true);
        if (page.scorePopupConfig?.enabled) {
          setShowScorePopup(true);
        }
        setTimeout(() => setCoins(c => c + 1), 950);
        setTimeout(() => setCoinAnimOn(false), 1100);
      }
      // Disparar confetti apenas se houver elemento Confetti na página
      const hasConfetti = page.confettiConfig?.enabled;
      if (hasConfetti) {
        triggerConfetti();
      }
      const tm = window.setTimeout(() => {
        onNextPage();
      }, hasConfetti ? 500 : 0);
      confettiTimeoutsRef.current.push(tm);
    }
  };

  const [formValuesLive, setFormValuesLive] = React.useState<Record<number, Record<string, string>>>({});
  const [globalFormValuesLive, setGlobalFormValuesLive] = React.useState<Record<string, string>>({});
  const liveRef = React.useRef<HTMLDivElement>(null);
  const [liveOverflow, setLiveOverflow] = React.useState(false);
  React.useEffect(() => {
    const el = liveRef.current;
    if (!el) return;
    setLiveOverflow(el.scrollHeight > el.clientHeight + 1);
  });

  return (
    <div className="h-full w-full flex flex-col" style={{ backgroundColor: config.colors.background }}>
      <h3 className="text-lg font-bold text-slate-700 text-center mb-4 p-4">Pré-visualização Ao Vivo</h3>
      <div className="flex-grow flex flex-col">
        <QuizHeader
          currentStep={currentStep}
          totalSteps={totalSteps}
          config={config}
          onBack={page.hideBackButton ? undefined : onPrevPage}
          coins={coins}
          coinContext="live"
        />
        <div ref={liveRef} className={`flex-grow flex flex-col px-4 pb-4 ${config.contentAlignment === 'top' ? 'items-start pt-4' : (liveOverflow ? 'items-center pt-4 justify-start' : 'items-center justify-center')} overflow-y-auto min-h-0`}>
          <div className={`w-full ${getContentWidthClass(config.contentWidth)} mx-auto ${getSpacingClasses(config.spacing)}`}>
            <PageRenderer
              page={page}
              config={config}
              onOptionSelect={handleOptionSelect}
              selectedOption={selectedOption}
              multiple={isMultipleChoice(page)}
              onNext={mockOnNext}
              onFormChange={(vals) => {
                setFormValuesLive(prev => ({ ...prev, [page.id]: vals }));
                const map: Record<string, string> = {};
                (page.elements || []).forEach((el) => {
                  if (el.type === ElementType.Form) {
                    const fieldId = (el as any).fieldId;
                    const idKey = String(el.id);
                    const v = (vals[fieldId || ''] ?? vals[idKey] ?? '');
                    if (typeof fieldId === 'string' && fieldId.trim()) {
                      map[fieldId.trim()] = v;
                    }
                  }
                });
                if (Object.keys(map).length) setGlobalFormValuesLive(prev => ({ ...prev, ...map }));
              }}
              formValues={formValuesLive[page.id]}
              globalFormValues={globalFormValuesLive}
              disableInterpolation={true}
              coins={coins}
            />
          </div>
          {coinAnimOn && (
            <div className="pointer-events-none fixed inset-0 z-[120] flex items-center justify-center">
              <div
                key={coinKeyP}
                className="relative flex items-center justify-center"
                style={{
                  animation: config.gamification?.animationType === 'simple'
                    ? 'coin-pop-p 1s ease-out forwards'
                    : 'coin-fly-p 1s ease-in-out forwards'
                }}
              >
                <div className="text-4xl filter drop-shadow-lg">
                  {config.gamification?.style === 'money' ? '💵' : '🪙'}
                </div>
                <div
                  className="absolute top-0 right-0 -mt-2 -mr-2 text-xl font-bold"
                  style={{
                    color: config.gamification?.style === 'money' ? '#15803d' : '#b45309',
                    textShadow: '0 1px 0 #fff'
                  }}
                >
                  +{coinAward}
                </div>
              </div>
              <style>{`
                @keyframes coin-fly-p {
                  0% { transform: translate(0, 0) scale(0.5); opacity: 0; }
                  20% { transform: translate(0, 0) scale(1.2); opacity: 1; }
                  40% { transform: translate(0, 0) scale(1); opacity: 1; }
                  80% { transform: translate(${coinDxP}px, ${coinDyP}px) scale(0.5); opacity: 1; }
                  100% { transform: translate(${coinDxP}px, ${coinDyP}px) scale(0); opacity: 0; }
                }
                @keyframes coin-pop-p {
                  0% { transform: translate(${coinDxP}px, ${coinDyP}px) scale(0.5); opacity: 0; }
                  20% { transform: translate(${coinDxP}px, ${coinDyP}px) scale(1.5); opacity: 1; }
                  80% { transform: translate(${coinDxP}px, ${coinDyP - 20}px) scale(1); opacity: 1; }
                  100% { transform: translate(${coinDxP}px, ${coinDyP - 30}px) scale(0.5); opacity: 0; }
                }
              `}</style>
            </div>
          )}
          {confettiOn && confettiParticles && (
            <div className="pointer-events-none fixed inset-0">
              {(() => {
                const { particles, style } = confettiParticles;
                const borderRadius = style === 'circles' || style === 'fireworks' ? '50%' : style === 'stars' ? '0' : '2px';
                const clipPath = style === 'stars' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' : undefined;

                return (
                  <>
                    {particles.map((p, i) => (
                      <span
                        key={i}
                        className="absolute"
                        style={{
                          left: p.startX,
                          top: p.startY,
                          width: `${p.size}px`,
                          height: `${p.size * (style === 'stars' ? 1 : 1.4)}px`,
                          backgroundColor: p.bg,
                          borderRadius: borderRadius,
                          clipPath: clipPath,
                          transform: 'translate(-50%, -50%)',
                          animation: `burst-p-${i} ${p.duration}ms ease-out ${p.delay}ms forwards`
                        }}
                      />
                    ))}
                    <style>{`
                      ${particles.map((p, i) =>
                      `@keyframes burst-p-${i}{0%{transform:translate(-50%,-50%) scale(0.6) rotate(${p.startRot}deg);opacity:1}50%{opacity:1}100%{transform:translate(calc(-50% + ${p.dx}px), calc(-50% + ${p.dy}px)) rotate(${p.endRot}deg) scale(1);opacity:0}}`
                    ).join('')}
                    `}</style>
                  </>
                );
              })()}
            </div>
          )}
          {showScorePopup && (
            <ScorePopup
              points={coinAward}
              text={popupContent.text}
              footer={popupContent.footer}
              onClose={() => setShowScorePopup(false)}
              style={config.gamification?.style}
              preText={config.gamification?.style === 'money' ? (config.gamification?.currencySymbol ?? 'US$ ') : ''}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const DesktopPreviewModal: React.FC<{
  quizData: QuizData;
  onClose: () => void;
  onButtonAction: (action: 'next' | 'redirect', redirectUrl?: string) => void;
  isRedirecting: boolean;
}> = ({ quizData, onClose, onButtonAction, isRedirecting }) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string | string[]>>({});
  const [formValuesDesktop, setFormValuesDesktop] = useState<Record<number, Record<string, string>>>({});
  const [globalFormValuesDesktop, setGlobalFormValuesDesktop] = useState<Record<string, string>>({});
  const [modalSize, setModalSize] = useState({ width: 1200, height: 800 });
  const [isResizing, setIsResizing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [coins, setCoins] = useState(0);
  const [confettiOn, setConfettiOn] = useState(false);
  const confettiTimeoutsRef = React.useRef<number[]>([]);
  const [confettiKey, setConfettiKey] = useState(0);
  const [coinAnimOn, setCoinAnimOn] = useState(false);
  const [coinAward, setCoinAward] = useState(0);
  const [coinKey, setCoinKey] = useState('coin-fly');
  const [coinDx, setCoinDx] = useState(180);
  const [coinDy, setCoinDy] = useState(-250);
  const [plusLeft, setPlusLeft] = useState<number | null>(null);
  const [plusTop, setPlusTop] = useState<number | null>(null);
  const [coinStartLeft, setCoinStartLeft] = useState<number | null>(null);
  const [coinStartTop, setCoinStartTop] = useState<number | null>(null);
  const [plusKey, setPlusKey] = useState('plus-fly');
  const [plusDx, setPlusDx] = useState(0);
  const [plusDy, setPlusDy] = useState(0);
  const [showScorePopup, setShowScorePopup] = useState(false);
  const [popupContent, setPopupContent] = React.useState({ text: '', footer: '' });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [invalidFieldsDesktop, setInvalidFieldsDesktop] = useState<string[]>([]);
  const [awardedSteps, setAwardedSteps] = useState<Set<number>>(new Set());

  const currentPage = quizData.pages[currentPageIndex];
  const isLastPage = currentPageIndex === quizData.pages.length - 1;

  // Limpar timeouts ao mudar de página
  React.useEffect(() => {
    setConfettiOn(false);
    confettiTimeoutsRef.current.forEach(id => clearTimeout(id));
    confettiTimeoutsRef.current = [];

    // Reset validation state
    setInvalidFieldsDesktop([]);
    setValidationError(null);
  }, [currentPageIndex]);

  const [activeConfettiType, setActiveConfettiType] = React.useState<'exit' | 'entry'>('exit');

  const triggerConfetti = (type: 'exit' | 'entry' = 'exit') => {
    setActiveConfettiType(type);
    setConfettiKey(prev => prev + 1);
    setConfettiOn(true);
  };

  // Trigger confetti on entry if configured
  React.useEffect(() => {
    if (currentPage?.confettiOnEntryConfig?.enabled) {
      // Pequeno delay para garantir que a página renderizou
      const t = setTimeout(() => triggerConfetti('entry'), 100);
      return () => clearTimeout(t);
    }
  }, [currentPageIndex]);

  // Memoizar partículas de confetti
  const confettiParticles = React.useMemo(() => {
    if (!confettiOn) return null;

    const config = activeConfettiType === 'entry'
      ? (currentPage.confettiOnEntryConfig || {})
      : (currentPage.confettiConfig || {});
    const direction = config.direction || 'center';
    const amountBase = config.amount === 'high' ? 100 : config.amount === 'low' ? 30 : 60;
    const amount = direction === 'sides' ? amountBase * 2.5 : amountBase;
    const style = config.style || 'paper';

    const particles = Array.from({ length: amount }).map((_, i) => {
      let startX = '50%';
      let startY = '50%';
      let angle, dist;

      if (direction === 'top') {
        startX = `${Math.random() * 100}%`;
        startY = '-10%';
        angle = Math.PI / 2 + (Math.random() - 0.5) * 0.5;
        dist = window.innerHeight + 100;
      } else if (direction === 'sides') {
        const side = i % 2 === 0 ? 0 : 100;
        startX = `${side}%`;
        startY = `${60 + Math.random() * 30}%`; // Start from lower sides (60-90%)

        // Angle: Sweep towards center
        if (side === 0) {
          // Left side: Aim between -10 and -80 degrees
          angle = -0.2 - (Math.random() * 1.2);
        } else {
          // Right side: Aim between -170 and -100 degrees
          angle = -Math.PI + 0.2 + (Math.random() * 1.2);
        }

        dist = config.amount === 'high' ? 600 + Math.random() * 400 : 400 + Math.random() * 300;
      } else {
        angle = Math.random() * Math.PI * 2;
        dist = config.amount === 'high' ? 200 + Math.random() * 350 : 150 + Math.random() * 250;
      }

      let colors;
      if (style === 'stars') {
        colors = ['#FFD700', '#FFC107', '#FFEB3B', '#F9A825', '#FFB300'];
      } else if (style === 'fireworks') {
        colors = ['#FF1744', '#F50057', '#D500F9', '#651FFF', '#00E5FF', '#00E676', '#FFD600', '#FF6D00'];
      } else {
        colors = ['#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6'];
      }

      const baseSize = style === 'stars' ? 16 : style === 'fireworks' ? 4 : 5;
      const sizeVariation = style === 'stars' ? 14 : style === 'fireworks' ? 3 : 5;

      const baseDuration = direction === 'sides' ? 2500 : (style === 'fireworks' ? 1000 : 700);
      const varDuration = direction === 'sides' ? 1000 : 500;

      return {
        startX,
        startY,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist,
        duration: baseDuration + Math.random() * varDuration,
        size: baseSize + Math.random() * sizeVariation,
        delay: Math.random() * 60,
        bg: colors[i % colors.length],
        startRot: Math.floor(Math.random() * 360),
        endRot: Math.floor(Math.random() * 360)
      };
    });

    return { particles, style };
  }, [confettiKey, currentPage, activeConfettiType]);



  // Função helper para verificar se a página é de múltipla escolha
  const isMultipleChoice = (page: Page) => {
    const hasMultipleChoiceElement = page.elements?.some(element =>
      element.type === ElementType.Options &&
      (element as any).multiple === true
    ) || false;



    return hasMultipleChoiceElement;
  };

  // Debug: verificar tipo da página atual
  const currentPageType = isMultipleChoice(currentPage) ? 'MULTIPLA_ESCOLHA' : 'UNICA_ESCOLHA';

  const handleAnswer = (answer: string | string[]) => {


    const wasAnswered = answers[currentPage.id] !== undefined;
    setAnswers(prev => ({
      ...prev,
      [currentPage.id]: answer
    }));

    // Para perguntas de escolha única, avançar automaticamente após feedback visual
    if (!isMultipleChoice(currentPage) && !isLastPage) {

      const gm = quizData.config.gamification || {};
      const hdr = quizData.config.header || {};
      const onCover = hdr.hideOnFirstPage === true && currentPageIndex === 0;
      const pointsToAward = currentPage.scoreConfig?.enabled ? (currentPage.scoreConfig.points || 0) : 0;
      if (gm.coinsEnabled && !wasAnswered && !onCover && !awardedSteps.has(currentPageIndex) && pointsToAward > 0) {
        playCoin();
        setCoinAward(pointsToAward);
        const t = document.querySelector('[data-coin-target="true"][data-coin-context="desktop"]') as HTMLElement | null;
        if (t) {
          const r = t.getBoundingClientRect();
          const centerX = window.innerWidth / 2;
          const centerY = window.innerHeight / 2;
          const targetX = r.left + r.width / 2;
          const targetY = r.top + r.height / 2;
          const dx = targetX - centerX;
          const dy = targetY - centerY;
          setCoinDx(dx);
          setCoinDy(dy);
          setCoinKey(prev => prev + 'x');
        }
        setCoinAnimOn(true);
        setAwardedSteps(prev => new Set(prev).add(currentPageIndex));
        if (currentPage.scorePopupConfig?.enabled) {
          setPopupContent({
            text: currentPage.scorePopupConfig.text || 'Foi adicionado ao seu saldo!',
            footer: currentPage.scorePopupConfig.footer || 'Continue para ganhar mais! 🚀'
          });
          setShowScorePopup(true);
        }
        setTimeout(() => setCoins(c => c + pointsToAward), 950);
        setTimeout(() => setCoinAnimOn(false), 1100);
      }

      // Disparar confetti se configurado na página
      const hasConfetti = currentPage.confettiConfig?.enabled;
      if (hasConfetti) {
        triggerConfetti();
      }

      const tm = window.setTimeout(() => {
        setCurrentPageIndex(prev => prev + 1);
      }, hasConfetti ? 500 : 0);
      confettiTimeoutsRef.current.push(tm);
    } else {

    }
  };

  const handleNext = () => {
    if (!isLastPage) {
      const gm = quizData.config.gamification || {};
      const wasAnswered = answers[currentPage.id] !== undefined;
      const hdr = quizData.config.header || {};
      const onCover = hdr.hideOnFirstPage === true && currentPageIndex === 0;
      const pointsToAward = currentPage.scoreConfig?.enabled ? (currentPage.scoreConfig.points || 0) : 0;
      if (gm.coinsEnabled && !wasAnswered && !onCover && !awardedSteps.has(currentPageIndex) && pointsToAward > 0) {
        playCoin();
        setCoinAward(pointsToAward);
        const t = document.querySelector('[data-coin-target="true"][data-coin-context="desktop"]') as HTMLElement | null;
        if (t) {
          const r = t.getBoundingClientRect();
          const centerX = window.innerWidth / 2;
          const centerY = window.innerHeight / 2;
          const targetX = r.left + r.width / 2;
          const targetY = r.top + r.height / 2;
          const dx = targetX - centerX;
          const dy = targetY - centerY;
          setCoinDx(dx);
          setCoinDy(dy);
          setCoinKey(prev => prev + 'x');
        }
        setCoinAnimOn(true);
        setAwardedSteps(prev => new Set(prev).add(currentPageIndex));
        if (currentPage.scorePopupConfig?.enabled) {
          setShowScorePopup(true);
        }
        setTimeout(() => setCoins(c => c + pointsToAward), 950);
        setTimeout(() => setCoinAnimOn(false), 1100);
      }

      // Disparar confetti se configurado na página
      const hasConfetti = currentPage.confettiConfig?.enabled;
      if (hasConfetti) {
        triggerConfetti();
      }

      const tm = window.setTimeout(() => {
        setCurrentPageIndex(prev => prev + 1);
      }, hasConfetti ? 500 : 0);
      confettiTimeoutsRef.current.push(tm);
    }
  };

  const handlePrev = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(prev => prev - 1);
    }
  };

  const handleRestart = () => {
    setCurrentPageIndex(0);
    setAnswers({});
    setCoins(0);
    setAwardedSteps(new Set());
  };



  const onNext = () => {
    if (!isLastPage) {
      const pageVals = formValuesDesktop[currentPage.id] || {};
      const invalidIds: string[] = [];
      (currentPage.elements || []).forEach((el: any) => {
        if (el.type !== ElementType.Form) return;
        if (el.required !== true) return;
        const keyA = typeof el.fieldId === 'string' && el.fieldId.trim() ? el.fieldId.trim() : '';
        const keyB = String(el.id);
        const v = (pageVals[keyA] ?? pageVals[keyB] ?? '');
        if (String(v).trim() === '') {
          if (keyA) invalidIds.push(keyA);
          invalidIds.push(keyB);
        }
      });
      if (invalidIds.length > 0) {
        setInvalidFieldsDesktop(invalidIds);
        setValidationError(quizData.config.validation?.requiredMessage || 'Preencha os campos obrigatórios antes de continuar.');
        return;
      }
      setInvalidFieldsDesktop([]);
      setValidationError(null);
      handleNext();
    }
  };



  const playCoin = () => {
    const soundType = quizData.config.gamification?.sound || 'default';

    if (soundType === 'default') {
      try {
        const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
        const ctx = new Ctx();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(700, ctx.currentTime);
        g.gain.setValueAtTime(0.001, ctx.currentTime);
        o.connect(g);
        g.connect(ctx.destination);
        o.start();
        o.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.08);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
        o.stop(ctx.currentTime + 0.3);
      } catch { }
    } else {
      const audio = new Audio(`/sounds/${soundType}.mp3`);
      audio.volume = 0.6;
      audio.play().catch(e => console.warn('⚠️ [DesktopPreview] Erro ao tocar som:', e));
    }
  };

  const renderPageContent = () => {
    return (
      <PageRenderer
        page={currentPage}
        config={quizData.config}
        onOptionSelect={handleAnswer}
        selectedOption={answers[currentPage.id] as string | string[]}
        multiple={isMultipleChoice(currentPage)}
        onNext={onNext}
        onFormChange={(vals) => {
          setFormValuesDesktop(prev => ({ ...prev, [currentPage.id]: vals }));
          if (validationError) setValidationError(null);
          if (invalidFieldsDesktop.length) setInvalidFieldsDesktop([]);
          const map: Record<string, string> = {};
          (currentPage.elements || []).forEach((el) => {
            if (el.type === ElementType.Form) {
              const fieldId = (el as any).fieldId;
              const idKey = String(el.id);
              const v = (vals[fieldId || ''] ?? vals[idKey] ?? '');
              if (typeof fieldId === 'string' && fieldId.trim()) {
                map[fieldId.trim()] = v;
              }
            }
          });
          if (Object.keys(map).length) setGlobalFormValuesDesktop(prev => ({ ...prev, ...map }));
        }}
        formValues={formValuesDesktop[currentPage.id]}
        globalFormValues={globalFormValuesDesktop}
        disableInterpolation={false}
        invalidFieldIds={invalidFieldsDesktop}
        validationMessage={quizData.config.validation?.requiredMessage}
        onButtonAction={onButtonAction}
        isRedirecting={isRedirecting}
        addUTMsToUrl={(url: string) => addUTMsToUrl(url, getUTMParameters())}
        coins={coins}
      />
    );
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    setStartPos({ x: e.clientX, y: e.clientY });
    setStartSize({ width: modalSize.width, height: modalSize.height });
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = e.clientX - startPos.x;
    const deltaY = e.clientY - startPos.y;

    const newWidth = Math.max(800, startSize.width + deltaX);
    const newHeight = Math.max(600, startSize.height + deltaY);

    setModalSize({ width: newWidth, height: newHeight });
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    document.body.style.userSelect = '';
  };

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, startPos, startSize]);

  React.useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  });

  const alignTop = quizData.config.contentAlignment === 'top';
  const contentAlignClass = alignTop ? 'items-start pt-4' : (overflowing ? 'items-center pt-4 justify-start' : 'items-center justify-center');
  const isBoxed = quizData.config.containerStyle === 'boxed';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div
        className="bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col"
        style={{
          width: `${modalSize.width}px`,
          height: `${modalSize.height}px`,
          maxWidth: '90vw',
          maxHeight: '90vh'
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-slate-50">
          <h2 className="text-xl font-bold text-slate-800">Pré-visualização Desktop</h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-grow flex flex-col overflow-hidden" style={{ backgroundColor: isBoxed ? '#f1f5f9' : quizData.config.colors.background }}>
          <QuizHeader
            currentStep={currentPageIndex}
            totalSteps={quizData.pages.length}
            config={quizData.config}
            onBack={currentPageIndex > 0 && quizData.pages[currentPageIndex]?.hideBackButton !== true ? handlePrev : undefined}
            coins={coins}
            coinContext="desktop"
          />
          <div ref={contentRef} className={`flex-1 flex flex-col px-6 pb-6 ${contentAlignClass} overflow-y-auto min-h-0`}>
            <div className={`w-full ${getContentWidthClass(quizData.config.contentWidth)} mx-auto ${getSpacingClasses(quizData.config.spacing)}`}>
              {isBoxed ? (
                <div
                  className="bg-white rounded-lg shadow-lg p-8"
                  style={{
                    backgroundColor: quizData.config.colors.background,
                    borderRadius: quizData.config.borderRadius === 'heavy' ? '1.5rem' : quizData.config.borderRadius === 'medium' ? '1rem' : quizData.config.borderRadius === 'light' ? '0.5rem' : '0'
                  }}
                >
                  {renderPageContent()}
                </div>
              ) : (
                <>
                  {renderPageContent()}
                </>
              )}
            </div>

            {confettiOn && confettiParticles && (
              <div className="pointer-events-none fixed inset-0 z-[110]">
                {(() => {
                  const { particles, style } = confettiParticles;
                  const borderRadius = style === 'circles' || style === 'fireworks' ? '50%' : style === 'stars' ? '0' : '2px';
                  const clipPath = style === 'stars' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' : undefined;

                  return (
                    <>
                      {particles.map((p, i) => (
                        <span
                          key={i}
                          className="absolute"
                          style={{
                            left: p.startX,
                            top: p.startY,
                            width: `${p.size}px`,
                            height: `${p.size * (style === 'stars' ? 1 : 1.4)}px`,
                            backgroundColor: p.bg,
                            borderRadius: borderRadius,
                            clipPath: clipPath,
                            transform: 'translate(-50%, -50%)',
                            animation: `burst-d-${i} ${p.duration}ms ease-out ${p.delay}ms forwards`
                          }}
                        />
                      ))}
                      <style>{`
                        ${particles.map((p, i) =>
                        `@keyframes burst-d-${i}{0%{transform:translate(-50%,-50%) scale(0.6) rotate(${p.startRot}deg);opacity:1}50%{opacity:1}100%{transform:translate(calc(-50% + ${p.dx}px), calc(-50% + ${p.dy}px)) rotate(${p.endRot}deg) scale(1);opacity:0}}`
                      ).join('')}
                      `}</style>
                    </>
                  );
                })()}
              </div>
            )}
            {coinAnimOn && (
              <div className="pointer-events-none fixed inset-0 z-[120] flex items-center justify-center">
                {quizData.config.gamification?.animationType === 'simple' ? (
                  <div
                    key={coinKey}
                    className="absolute"
                    style={{
                      left: `calc(50% + ${coinDx}px)`,
                      top: `calc(50% + ${coinDy}px)`,
                      transform: 'translate(-50%, -50%)',
                      animation: 'coin-pop-d-simple 1s ease-out forwards'
                    }}
                  >
                    <div
                      className="text-2xl font-bold"
                      style={{
                        color: quizData.config.gamification?.style === 'money' ? '#15803d' : '#b45309',
                        textShadow: '0 1px 0 #fff'
                      }}
                    >
                      +{coinAward}
                    </div>
                  </div>
                ) : (
                  <div
                    key={coinKey}
                    className="relative flex items-center justify-center"
                    style={{
                      animation: 'coin-fly-d 1s ease-in-out forwards'
                    }}
                  >
                    <div className="text-4xl filter drop-shadow-lg">
                      {quizData.config.gamification?.style === 'money' ? '💵' : '🪙'}
                    </div>
                    <div
                      className="absolute top-0 right-0 -mt-2 -mr-2 text-xl font-bold"
                      style={{
                        color: quizData.config.gamification?.style === 'money' ? '#15803d' : '#b45309',
                        textShadow: '0 1px 0 #fff'
                      }}
                    >
                      +{coinAward}
                    </div>
                  </div>
                )}
                <style>{`
                  @keyframes coin-fly-d {
                    0% { transform: translate(0, 0) scale(0.5); opacity: 0; }
                    20% { transform: translate(0, 0) scale(1.2); opacity: 1; }
                    40% { transform: translate(0, 0) scale(1); opacity: 1; }
                    80% { transform: translate(${coinDx}px, ${coinDy}px) scale(0.5); opacity: 1; }
                    100% { transform: translate(${coinDx}px, ${coinDy}px) scale(0); opacity: 0; }
                  }
                  @keyframes coin-pop-d-simple {
                    0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
                    20% { transform: translate(-50%, -50%) scale(1.5); opacity: 1; }
                    80% { transform: translate(-50%, -150%) scale(1); opacity: 1; }
                    100% { transform: translate(-50%, -200%) scale(0.5); opacity: 0; }
                  }
                `}</style>
              </div>
            )}

          </div>
          {showScorePopup && (
            <ScorePopup
              points={coinAward}
              text={popupContent.text}
              footer={popupContent.footer}
              onClose={() => setShowScorePopup(false)}
              style={quizData.config.gamification?.style}
              preText={quizData.config.gamification?.style === 'money' ? (quizData.config.gamification?.currencySymbol ?? 'US$ ') : ''}
            />
          )}
        </div>

        {/* Resize Handle */}
        <div
          className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize bg-slate-100 hover:bg-slate-200 border-t border-l border-slate-300 flex items-end justify-end p-1"
          onMouseDown={handleMouseDown}
        >
          <svg
            className="w-4 h-4 text-slate-400"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM18 18H16V16H18V18ZM14 22H12V20H14V22ZM22 14H20V12H22V14Z" />
          </svg>
        </div>
      </div>
    </div>
  );
};


const App: React.FC = () => {
  const [appView, setAppView] = useState<'dashboard' | 'editor' | 'ai' | 'settings' | 'stats' | 'preview' | 'leads'>('dashboard');
  const { projects, loading: projectsLoading, error: projectsError, createProject, updateProject, deleteProject, loadProjects, clearProjects, updateQuiz } = useProjects();
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [currentQuizId, setCurrentQuizId] = useState<string | null>(null);
  const [geminiKey, setGeminiKey] = useState<string>('');
  const [validatingGemini, setValidatingGemini] = useState<boolean>(false);
  const { settings, loading: settingsLoading, error: settingsError, saveSettings, loadSettings, validateGeminiKey, clearGeminiKey } = useUserSettings();
  const [openQuizMenuId, setOpenQuizMenuId] = useState<string | null>(null);
  const [addingProject, setAddingProject] = useState<boolean>(false);
  const [newProjectName, setNewProjectName] = useState<string>('');
  const newProjectInputRef = React.useRef<HTMLInputElement>(null);
  const [userName, setUserName] = useState<string>(() => localStorage.getItem('qc.user.name') || 'João Silva');
  const [loginEmail, setLoginEmail] = useState('allanperon@gmail.com');
  const [loginPassword, setLoginPassword] = useState('teste123');
  const [loginError, setLoginError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [authReady, setAuthReady] = useState<boolean>(false);
  const [startupError, setStartupError] = useState<string>('');
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const [spyMenuOpen, setSpyMenuOpen] = useState<boolean>(false);
  const [aiLoading, setAiLoading] = useState(false);

  // Estado para armazenar domínios verificados por projeto (projectId -> hostname)
  const [projectDomains, setProjectDomains] = useState<Record<string, string>>({});

  // Carregar domínios verificados de todos os projetos do usuário
  const loadProjectDomains = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('domains')
        .select('project_id, hostname, is_verified')
        .eq('is_verified', true);

      if (error) {
        console.error('Erro ao carregar domínios:', error);
        return;
      }

      if (data) {
        const domainMap: Record<string, string> = {};
        data.forEach((d: any) => {
          if (d.project_id && d.hostname) {
            domainMap[d.project_id] = d.hostname;
          }
        });
        setProjectDomains(domainMap);
        console.log('✅ Domínios verificados carregados:', domainMap);
      }
    } catch (err) {
      console.error('Erro ao carregar domínios:', err);
    }
  }, []);

  // Carregar domínios quando os projetos são carregados
  React.useEffect(() => {
    if (projects.length > 0) {
      loadProjectDomains();
    }
  }, [projects.length, loadProjectDomains]);

  // Estados para a tela de IA (preenchidos pelo modal de geração)
  const [aiQuizName, setAiQuizName] = useState('');
  const [aiQuizSlug, setAiQuizSlug] = useState('');
  const [aiProduct, setAiProduct] = useState('');
  const [aiIdealCustomer, setAiIdealCustomer] = useState('');
  const [aiOffer, setAiOffer] = useState('');
  const [aiPromises, setAiPromises] = useState('');
  const [aiHasVSL, setAiHasVSL] = useState<boolean | null>(null);
  const [aiVSLText, setAiVSLText] = useState('');


  // Fechar menu de espionagem ao clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (spyMenuOpen && !target.closest('.spy-menu-container')) {
        setSpyMenuOpen(false);
      }
    };

    if (spyMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [spyMenuOpen]);



  // Função para traduzir JSON completo do quiz usando Gemini API
  const translateQuizWithGemini = useCallback(async (quizData: any, sourceLang: string, targetLang: string): Promise<any> => {
    console.log('🤖 [Gemini] Iniciando tradução completa do quiz...', {
      sourceLang,
      targetLang,
      dataSize: JSON.stringify(quizData).length
    });

    const geminiApiKey = settings?.gemini_api_key;
    if (!geminiApiKey) {
      console.error('❌ [Gemini] Chave da API Gemini não configurada');
      throw new Error('Chave da API Gemini não configurada');
    }

    // Converte o JSON para string formatada
    const jsonString = JSON.stringify(quizData, null, 2);
    console.log('📄 [Gemini] JSON original preparado, tamanho:', jsonString.length);

    const prompt = TRANSLATION_PROMPT
      .replace('{json_content}', jsonString)
      .replace('{source_lang}', sourceLang)
      .replace('{target_lang}', targetLang);

    console.log('📝 [Gemini] Prompt final preparado, tamanho:', prompt.length);

    const geminiModel = import.meta.env.VITE_GEMINI_MODEL || 'gemini-1.5-flash';
    console.log('🔧 [Gemini] Usando modelo:', geminiModel);

    console.log('🌐 [Gemini] Fazendo requisição para API...');
    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${geminiModel}:generateContent?key=${encodeURIComponent(geminiApiKey)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: prompt
          }]
        }],
        generationConfig: {
          temperature: 0.1,
          topK: 1,
          topP: 1,
          maxOutputTokens: 65536,
          responseMimeType: 'application/json'
        }
      })
    });

    console.log('📡 [Gemini] Resposta recebida, status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ [Gemini] Erro na API:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      throw new Error(`Erro na API Gemini: ${response.status} - ${errorData.error?.message || 'Erro desconhecido'}`);
    }

    const data = await response.json();
    console.log('📦 [Gemini] Dados da resposta recebidos');

    const rawText = data.candidates?.[0]?.content?.parts?.[0]?.text;

    if (!rawText) {
      console.error('❌ [Gemini] Resposta não contém texto:', data);
      throw new Error('Resposta da API Gemini não contém texto traduzido');
    }

    console.log('📝 [Gemini] Texto bruto recebido, tamanho:', rawText.length);

    // DEBUG em modo dev: logar o texto bruto completo
    if (import.meta.env.DEV) {
      console.log('🔍 [Gemini][DEV] Texto bruto COMPLETO:');
      console.log(rawText);
      console.log('🔍 [Gemini][DEV] Últimos 200 chars:', JSON.stringify(rawText.slice(-200)));
      console.log('🔍 [Gemini][DEV] Primeiros 200 chars:', JSON.stringify(rawText.slice(0, 200)));
    }

    // Limpeza robusta da resposta
    let cleanedText = rawText;

    // 1. Remover BOM e caracteres invisíveis
    cleanedText = cleanedText.replace(/^\uFEFF/, '');
    cleanedText = cleanedText.replace(/[\u200B-\u200D\uFEFF\u00A0]/g, ' ');

    // 2. Remover blocos de markdown (```json ... ``` ou ``` ... ```)
    cleanedText = cleanedText.replace(/^```(?:json)?\s*\n?/i, '');
    cleanedText = cleanedText.replace(/\n?```\s*$/i, '');

    // 3. Caso ainda tenha backticks soltos
    cleanedText = cleanedText.replace(/^`+/, '').replace(/`+$/, '');

    // 4. Trim final
    cleanedText = cleanedText.trim();

    // 5. Garantir que começa com { ou [
    const firstBrace = cleanedText.indexOf('{');
    const firstBracket = cleanedText.indexOf('[');
    let startIdx = -1;
    if (firstBrace >= 0 && firstBracket >= 0) {
      startIdx = Math.min(firstBrace, firstBracket);
    } else if (firstBrace >= 0) {
      startIdx = firstBrace;
    } else if (firstBracket >= 0) {
      startIdx = firstBracket;
    }
    if (startIdx > 0) {
      console.warn('⚠️ [Gemini] Removendo lixo antes do JSON, chars removidos:', startIdx);
      cleanedText = cleanedText.substring(startIdx);
    }

    // 6. Garantir que termina com } ou ]
    const lastBrace = cleanedText.lastIndexOf('}');
    const lastBracket = cleanedText.lastIndexOf(']');
    const endIdx = Math.max(lastBrace, lastBracket);
    if (endIdx >= 0 && endIdx < cleanedText.length - 1) {
      console.warn('⚠️ [Gemini] Removendo lixo depois do JSON, chars removidos:', cleanedText.length - endIdx - 1);
      cleanedText = cleanedText.substring(0, endIdx + 1);
    }

    // 7. Remover trailing commas antes de } ou ] (erro comum de LLMs)
    cleanedText = cleanedText.replace(/,\s*([\]}])/g, '$1');

    console.log('🧹 [Gemini] Texto limpo, tamanho:', cleanedText.length);

    if (import.meta.env.DEV) {
      console.log('🔍 [Gemini][DEV] Texto limpo - primeiros 300 chars:', cleanedText.substring(0, 300));
      console.log('🔍 [Gemini][DEV] Texto limpo - últimos 300 chars:', cleanedText.substring(cleanedText.length - 300));
    }

    try {
      const translatedData = JSON.parse(cleanedText);
      console.log('✅ [Gemini] JSON parseado com sucesso!');
      console.log('📊 [Gemini] Estrutura do JSON traduzido:', {
        hasName: !!translatedData.name,
        hasConfig: !!translatedData.config,
        hasPages: !!translatedData.pages,
        pagesCount: translatedData.pages?.length || 0
      });

      return translatedData;
    } catch (parseError) {
      console.error('❌ [Gemini] Erro ao fazer parse do JSON:', parseError);
      console.error('❌ [Gemini] Texto que falhou (primeiros 1000 chars):', cleanedText.substring(0, 1000));
      console.error('❌ [Gemini] Texto que falhou (últimos 500 chars):', cleanedText.substring(cleanedText.length - 500));

      // Identificar posição do erro se possível
      if (parseError instanceof SyntaxError) {
        const posMatch = parseError.message.match(/position (\d+)/i);
        if (posMatch) {
          const pos = parseInt(posMatch[1]);
          console.error('❌ [Gemini] Contexto do erro na posição', pos, ':',
            JSON.stringify(cleanedText.substring(Math.max(0, pos - 100), pos + 100)));
        }
      }

      throw new Error('A resposta da API Gemini não é um JSON válido. Verifique o console para mais detalhes.');
    }
  }, [settings]);

  // Função para traduzir JSON do quiz recursivamente
  // Função para garantir que todas as páginas tenham uniqueId
  const ensurePagesHaveUniqueIds = useCallback((data: QuizData): QuizData => {
    return {
      ...data,
      pages: data.pages.map((page, index) => ({
        ...page,
        id: index, // Garantir ID sequencial correto
        uniqueId: page.uniqueId || generatePageUniqueId() // Gerar se não existir
      }))
    }
  }, []);

  const [quizData, setQuizData] = useState<QuizData>(() => ensurePagesHaveUniqueIds(QUIZ_DATA));
  const [selectedPageIndex, setSelectedPageIndex] = useState<number>(0);
  const [showGeneratedCode, setShowGeneratedCode] = useState(false);
  const [published, setPublished] = useState<boolean>(false);
  const [showDesktopPreview, setShowDesktopPreview] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  // remove previewInitialStep

  // Estados para redirecionamentos nas previews
  const [isRedirectingDesktop, setIsRedirectingDesktop] = useState(false);

  useEffect(() => {
    const applyGlobalStylesHandler = (e: any) => {
      const src = e.detail;
      if (!src || src.type !== ElementType.Options) return;

      setQuizData(prev => {
        if (!prev) return prev;
        const newPages = prev.pages.map(p => ({
          ...p,
          elements: p.elements.map(el => {
            if (el.type === ElementType.Options && el.id !== src.id) {
              return {
                ...el,
                layout: src.layout,
                style: src.style,
                animation: src.animation,
                checkStyle: src.checkStyle,
                checkPosition: src.checkPosition,
                border: src.border,
                shadow: src.shadow,
                spacing: src.spacing,
                textAlign: src.textAlign,
                fontWeight: src.fontWeight,
                textColor: src.textColor,
                imageOrientation: src.imageOrientation,
                verticalImageRatio: src.verticalImageRatio || '1:1',
                mediaSize: src.mediaSize
              } as PageElement;
            }
            return el;
          })
        }));
        return { ...prev, pages: newPages };
      });
      alert('Estilo aplicado a todos os elementos de Opções do quiz!');
    };

    window.addEventListener('QUIZ_APPLY_GLOBAL_STYLES', applyGlobalStylesHandler);
    return () => window.removeEventListener('QUIZ_APPLY_GLOBAL_STYLES', applyGlobalStylesHandler);
  }, []);
  const [isRedirectingMobile, setIsRedirectingMobile] = useState(false);

  // Funções para redirecionamentos nas previews
  const handleDesktopPreviewButtonAction = useCallback(async (action: 'next' | 'redirect', redirectUrl?: string, options?: { target?: '_self' | '_blank' }) => {
    if (action === 'redirect' && redirectUrl) {
      // Para previews, respeitar o target configurado
      const target = options?.target || '_blank';
      const redirectUrlWithUTMs = addUTMsToUrl(redirectUrl, getUTMParameters());

      if (target === '_blank') {
        // Para nova aba, não bloquear interface - só abrir e pronto
        window.open(redirectUrlWithUTMs, '_blank');
      } else {
        // Para mesma aba, bloquear interface durante o redirect
        setIsRedirectingDesktop(true);
        window.location.href = redirectUrlWithUTMs;
      }
    }
  }, []);

  const handleMobilePreviewButtonAction = useCallback(async (action: 'next' | 'redirect', redirectUrl?: string, options?: { target?: '_self' | '_blank' }) => {
    if (action === 'redirect' && redirectUrl) {
      // Para previews, respeitar o target configurado
      const target = options?.target || '_blank';
      const redirectUrlWithUTMs = addUTMsToUrl(redirectUrl, getUTMParameters());

      if (target === '_blank') {
        // Para nova aba, não bloquear interface - só abrir e pronto
        window.open(redirectUrlWithUTMs, '_blank');
      } else {
        // Para mesma aba, bloquear interface durante o redirect
        setIsRedirectingMobile(true);
        window.location.href = redirectUrlWithUTMs;
      }
    }
  }, []);

  const [editingProjectName, setEditingProjectName] = useState(false);
  const [tempProjectName, setTempProjectName] = useState('');
  const [editingQuizName, setEditingQuizName] = useState<string | null>(null);
  const [tempQuizName, setTempQuizName] = useState('');
  const [addingQuiz, setAddingQuiz] = useState(false);
  const [newQuizName, setNewQuizName] = useState('');
  const [newQuizSlug, setNewQuizSlug] = useState('');
  const [newProjectError, setNewProjectError] = useState('');
  const [newQuizNameError, setNewQuizNameError] = useState('');
  const [newQuizSlugError, setNewQuizSlugError] = useState('');
  const newQuizInputRef = React.useRef<HTMLInputElement>(null);
  const newQuizSlugInputRef = React.useRef<HTMLInputElement>(null);
  const [importingQuiz, setImportingQuiz] = useState(false);
  const [importSharedCode, setImportSharedCode] = useState('');
  const [importQuizSlug, setImportQuizSlug] = useState('');
  const [importQuizSlugError, setImportQuizSlugError] = useState('');
  const importQuizSlugInputRef = React.useRef<HTMLInputElement>(null);
  const importSharedCodeInputRef = React.useRef<HTMLInputElement>(null);
  const [importAttempts, setImportAttempts] = useState<number[]>([]);
  const [changeSlugModal, setChangeSlugModal] = useState<{ projectId: string; quizId: string; currentSlug: string } | null>(null);
  const userMenuRef = React.useRef<HTMLDivElement>(null);

  // Fechar menu de usuário ao clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const resetUIState = useCallback(() => {
    // SEGURANÇA: Limpar projetos e quizzes IMEDIATAMENTE
    clearProjects();

    // SEGURANÇA: Limpar configurações do usuário
    setGeminiKey('');

    setUserMenuOpen(false);
    setOpenQuizMenuId(null);
    setAddingProject(false);
    setNewProjectName('');
    setEditingProjectName(false);
    setTempProjectName('');
    setCurrentProjectId(null);
    setCurrentQuizId(null);
    setAddingQuiz(false);
    setNewQuizName('');
    setNewQuizSlug('');
    setImportingQuiz(false);
    setImportSharedCode('');
    setImportQuizSlug('');
    setEditingQuizName(null);
    setTempQuizName('');
    setPendingDeleteProject(null);
    setPendingDeleteQuiz(null);
    setErrorModal(null);
    setDuplicateLanguageModal(null);
    setChangeSlugModal(null);
    setShareQuizModal(null);
    setQuizData(ensurePagesHaveUniqueIds(QUIZ_DATA));
    setShowGeneratedCode(false);
    setShowDesktopPreview(false);
    setShowMobilePreview(false);
    setPublished(false);
    setSelectedPageIndex(0);
  }, [clearProjects]);

  const CopiarUrlActionButton: React.FC<{ quiz: any; project: any; userName: string; customDomain?: string; onClose: () => void }> = ({ quiz, project, userName, customDomain, onClose }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (quiz.status !== 'published') return;

      const slug = quiz.slug || quiz.name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'quiz';
      let finalUrl = '';
      if (customDomain) {
        finalUrl = `https://${customDomain}/${slug}`;
      } else {
        const userSubdomain = (userName || 'user').toLowerCase().replace(/[^a-z0-9]/g, '');
        const baseUrl = (import.meta.env.VITE_APP_URL || window.location.origin).replace(/\/$/, '');
        finalUrl = `${baseUrl}/${userSubdomain}/${slug}`;
      }

      navigator.clipboard.writeText(finalUrl).then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
          onClose(); // Fechar o menu após o feedback visual
        }, 1000);
      }).catch(() => {
        alert('Erro ao copiar URL');
      });
    };

    return (
      <button
        onClick={handleCopy}
        disabled={quiz.status !== 'published'}
        className={`w-full text-left px-3 py-2 hover:bg-slate-100 flex items-center gap-2 ${quiz.status !== 'published' ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {copied ? (
          <>
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            <span className="text-green-600 font-medium">Copiado!</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
            Copiar URL
          </>
        )}
      </button>
    );
  };

  const QuizList: React.FC<{ proj: ProjectMeta }> = ({ proj }) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {[...proj.quizzes].sort((a, b) => a.name.localeCompare(b.name)).map((q) => (
          <div key={q.id} className="border border-slate-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md hover:border-slate-300 transition-all relative">
            <div className="flex flex-col gap-3">

              <div className="flex-1 pr-16">
                {editingQuizName === q.id ? (
                  <input
                    type="text"
                    value={tempQuizName}
                    onChange={(e) => setTempQuizName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        renameQuiz(proj.id, q.id, tempQuizName);
                        setEditingQuizName(null);
                      } else if (e.key === 'Escape') {
                        setEditingQuizName(null);
                        setTempQuizName('');
                      }
                    }}
                    onBlur={() => {
                      if (tempQuizName.trim()) {
                        renameQuiz(proj.id, q.id, tempQuizName);
                      }
                      setEditingQuizName(null);
                      setTempQuizName('');
                    }}
                    className="w-full text-left text-xl font-bold text-slate-800 font-sans bg-transparent border-b-2 border-slate-300 focus:border-slate-500 outline-none px-1"
                    autoFocus
                  />
                ) : (
                  <React.Fragment>
                    <h3
                      className="text-left text-xl font-bold text-slate-800 font-sans hover:text-slate-600 cursor-pointer m-0 mb-3"
                      onClick={() => {
                        setEditingQuizName(q.id);
                        setTempQuizName(q.name);
                      }}
                    >
                      {q.name}
                    </h3>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-slate-400 font-mono bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
                        /{q.slug || toSlug(q.name)}
                      </span>
                    </div>
                  </React.Fragment>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${q.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>{q.status === 'published' ? 'Publicado' : 'Rascunho'}</span>
                </div>
              </div>
              <div className="flex gap-2 items-center justify-center pt-3 border-t border-slate-100 flex-wrap">
                <button onClick={() => openEditor(proj.id, q.id)} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors flex items-center gap-2 bg-slate-600 text-white border-slate-600 hover:bg-slate-700`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  Editar
                </button>

                <button
                  disabled={q.status !== 'published'}
                  onClick={() => {
                    if (q.status !== 'published') return;
                    // Usar slug do quiz para criar URL amigável
                    const slug = q.slug || q.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

                    // Verificar se o projeto tem domínio personalizado verificado
                    const customDomain = projectDomains[proj.id];
                    if (customDomain) {
                      // Usar domínio personalizado: https://custom-domain.com/slug?preview=true
                      window.open(`https://${customDomain}/${slug}?preview=true`, '_blank');
                    } else {
                      // Usar padrão: /{subdomain}/{slug}?preview=true
                      const userSubdomain = (userName || 'user').toLowerCase().replace(/[^a-z0-9]/g, '');
                      window.open(`/${userSubdomain}/${slug}?preview=true`, '_blank');
                    }
                  }} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors flex items-center gap-2 ${q.status === 'published' ? 'bg-white text-slate-700 hover:bg-slate-50 border-slate-300 cursor-pointer' : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'}`} title={q.status === 'published' ? "Visualizar Quiz" : "Quiz não publicado"}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Visualizar
                </button>
                <div className="relative">
                  <button onClick={() => setOpenQuizMenuId(prev => prev === q.id ? null : q.id)} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors flex items-center gap-2 bg-white text-slate-700 hover:bg-slate-50 border-slate-300`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                    Ações
                  </button>
                  {openQuizMenuId === q.id && (
                    <div data-quiz-menu className="absolute right-0 mt-1 w-64 bg-white border border-slate-300 rounded shadow z-50">
                      <button onClick={() => { setOpenQuizMenuId(null); togglePublishQuiz(proj.id, q.id); }} className="w-full text-left px-3 py-2 hover:bg-slate-100 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                        {q.status === 'published' ? 'Pausar' : 'Publicar'}
                      </button>
                      <div className="border-t border-slate-200"></div>
                      <CopiarUrlActionButton
                        quiz={q}
                        project={proj}
                        userName={userName}
                        customDomain={projectDomains[proj.id]}
                        onClose={() => setOpenQuizMenuId(null)}
                      />
                      <div className="border-t border-slate-200"></div>
                      <button onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('📊 [Stats] Botão clicado:', { projectId: proj.id, quizId: q.id });
                        setCurrentProjectId(proj.id);
                        setCurrentQuizId(q.id);
                        // Limpar quizData local para forçar uso dos dados do projects (atualizados)
                        // Isso garante que mudanças salvas sejam refletidas
                        setQuizData(ensurePagesHaveUniqueIds(QUIZ_DATA));
                        setOpenQuizMenuId(null);
                        setAppView('stats');
                        console.log('📊 [Stats] Estados atualizados, mudando para view stats');
                      }} className="w-full text-left px-3 py-2 hover:bg-slate-100 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                        Ver Estatísticas
                      </button>
                      <div className="border-t border-slate-200"></div>
                      <button onClick={() => { setOpenQuizMenuId(null); setCurrentProjectId(proj.id); setCurrentQuizId(q.id); setAppView('leads'); }} className="w-full text-left px-3 py-2 hover:bg-slate-100 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        Ver Respostas e Leads
                      </button>
                      <div className="border-t border-slate-200"></div>

                      <button onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('🔗 [Menu] Abrindo modal de slug para quiz:', q.id);
                        setOpenQuizMenuId(null);
                        setChangeSlugModal({ projectId: proj.id, quizId: q.id, currentSlug: q.slug || toSlug(q.name) });
                      }} className="w-full text-left px-3 py-2 hover:bg-slate-100 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                        Alterar Slug
                      </button>
                      <div className="border-t border-slate-200"></div>
                      <button onClick={() => { setOpenQuizMenuId(null); duplicateQuiz(proj.id, q.id); }} className="w-full text-left px-3 py-2 hover:bg-slate-100 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V7M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" /></svg>
                        Duplicar
                      </button>
                      <div className="border-t border-slate-200"></div>
                      <button onClick={() => {
                        console.log('🎯 [Menu] Botão "Duplicar em Outro Idioma" clicado:', { projectId: proj.id, quizId: q.id, quizName: q.name });
                        setOpenQuizMenuId(null);
                        setDuplicateLanguageModal({ projectId: proj.id, quizId: q.id, quizName: q.name });
                      }} className="w-full text-left px-3 py-2 hover:bg-slate-100 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
                        Duplicar em Outro Idioma
                      </button>
                      <div className="border-t border-slate-200"></div>
                      <button onClick={() => { setOpenQuizMenuId(null); setShareQuizModal({ quizId: q.id, shareCode: q.share_id || '' }); }} className="w-full text-left px-3 py-2 hover:bg-slate-100 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        Compartilhar Quiz
                      </button>
                      <div className="border-t border-slate-200"></div>
                      <button onClick={() => {
                        setOpenQuizMenuId(null);
                        const structure = {
                          name: q.name,
                          description: "Quiz Structure Export",
                          exported_at: new Date().toISOString(),
                          config: q.data?.config,
                          pages: q.data?.pages
                        };
                        const blob = new Blob([JSON.stringify(structure, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `quiz-structure-${q.slug || toSlug(q.name)}.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }} className="w-full text-left px-3 py-2 hover:bg-slate-100 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        Exportar Estrutura
                      </button>
                      <div className="border-t border-slate-200"></div>
                      <button onClick={() => {
                        setOpenQuizMenuId(null);
                        let textContent = `Quiz: ${q.name}\n\n`;
                        if (q.data?.pages) {
                          q.data.pages.forEach((page, index) => {
                            textContent += `--- Página ${index + 1}: ${page.title || 'Sem título'} ---\n\n`;
                            page.elements.forEach((el: any) => {
                              const stripHtml = (html: string) => (html || '').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>?/gm, '');

                              if (el.type === ElementType.Title && el.content) {
                                textContent += `[Título]\n${stripHtml(el.content)}\n\n`;
                              } else if (el.type === ElementType.Text && el.content) {
                                textContent += `${stripHtml(el.content)}\n\n`;
                              } else if (el.type === ElementType.Alert && el.content) {
                                textContent += `[Alerta]\n${stripHtml(el.content)}\n\n`;
                              } else if (el.type === ElementType.Timer) {
                                textContent += `[Cronômetro - Duração: ${el.durationSeconds || 0}s]\n${stripHtml(el.content || '')}\n\n`;
                              } else if (el.type === ElementType.Options && el.options) {
                                if (el.title) textContent += `${stripHtml(el.title)}\n`;
                                el.options.forEach((opt: any, optIndex: number) => {
                                  textContent += `[ ] ${opt.text || `Opção ${optIndex + 1}`}\n`;
                                });
                                textContent += '\n';
                              } else if (el.type === ElementType.List && el.items) {
                                textContent += `[Lista]\n`;
                                el.items.forEach((item: any) => {
                                  textContent += `- ${stripHtml(item.title)}: ${stripHtml(item.description)}\n`;
                                });
                                textContent += '\n';
                              } else if (el.type === ElementType.QA && el.items) {
                                textContent += `[Perguntas Frequentes]\n`;
                                el.items.forEach((item: any) => {
                                  textContent += `Q: ${stripHtml(item.question)}\nR: ${stripHtml(item.answer)}\n\n`;
                                });
                              } else if (el.type === ElementType.Image && el.url) {
                                textContent += `[Imagem: ${el.url}]\n\n`;
                              } else if (el.type === ElementType.Audio && el.url) {
                                textContent += `[Áudio: ${el.url}]\n\n`;
                              } else if (el.type === ElementType.Video && el.url) {
                                textContent += `[Vídeo: ${el.url}]\n\n`;
                              } else if (el.type === ElementType.Form) {
                                textContent += `[Formulário - Campo] ${el.title || el.placeholder || el.fieldType}\n\n`;
                              } else if (el.type === ElementType.Button && el.text) {
                                textContent += `[Botão] ${el.text}\n\n`;
                              } else if (el.type === ElementType.Loading) {
                                textContent += `[Carregando - Duração: ${el.durationSeconds || 0}s]\n`;
                                if (el.title) textContent += `Título: ${stripHtml(el.title)}\n`;
                                if (el.subtitle) textContent += `Subtítulo: ${stripHtml(el.subtitle)}\n`;
                                if (el.subtitles && el.subtitles.length > 0) {
                                  textContent += `Frases de carregamento:\n`;
                                  el.subtitles.forEach((s: string) => textContent += `- ${stripHtml(s)}\n`);
                                }
                                textContent += '\n';
                              } else if (el.type === ElementType.Report) {
                                textContent += `[Resultado / Relatório]\n`;
                                if (el.title) textContent += `Título: ${stripHtml(el.title)}\n`;
                                if (el.subtitle) textContent += `Subtítulo: ${stripHtml(el.subtitle)}\n`;
                                if (el.resultLabel) textContent += `Label do Resultado: ${stripHtml(el.resultLabel)}\n`;
                                if (el.score !== undefined) textContent += `Pontuação: ${el.score}\n`;
                                textContent += '\n';
                              } else if (el.type === ElementType.Testimonial) {
                                textContent += `[Depoimento]\nNome: ${el.name}\n`;
                                if (el.stars) textContent += `Estrelas: ${el.stars}\n`;
                                textContent += `Texto: ${stripHtml(el.text)}\n\n`;
                              } else if (el.type === ElementType.Spacer) {
                                textContent += `[Espaçamento]\n\n`;
                              } else if (el.type === ElementType.Level) {
                                textContent += `[Barra de Nível]\n`;
                                if (el.title) textContent += `Título: ${stripHtml(el.title)}\n`;
                                if (el.subtitle) textContent += `Subtítulo: ${stripHtml(el.subtitle)}\n`;
                                textContent += `Progresso: ${el.percent || 0}%\n\n`;
                              } else if (el.type === ElementType.FbComments && el.comments) {
                                textContent += `[Comentários FB]\n`;
                                el.comments.forEach((c: any) => {
                                  textContent += `- ${c.name} (${c.timeAgo}): ${stripHtml(c.content)}\n`;
                                });
                                textContent += `\n`;
                              }
                            });
                            textContent += '\n';
                          });
                        }
                        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `quiz-texto-${q.slug || toSlug(q.name)}.txt`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }} className="w-full text-left px-3 py-2 hover:bg-slate-100 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        Exportar Modo Texto
                      </button>
                      <div className="border-t border-slate-200"></div>
                      <button onClick={() => {
                        setOpenQuizMenuId(null);
                        setPendingDeleteQuiz({ projectId: proj.id, quizId: q.id, name: q.name });
                      }} className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        Excluir Quiz
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {proj.quizzes.length === 0 && (
          <div className="col-span-full">
            <div className="flex items-start gap-3 p-6 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">Nenhum quiz neste projeto</p>
                <p className="text-sm text-blue-700 mt-1">Comece criando seu primeiro quiz para este projeto.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Fechar menu de quiz ao clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Verifica se o clique foi fora de qualquer menu de quiz
      if (openQuizMenuId && !target.closest('[data-quiz-menu]')) {
        setOpenQuizMenuId(null);
      }
    };

    if (openQuizMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openQuizMenuId]);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggingFromPaletteType, setDraggingFromPaletteType] = useState<ElementType | null>(null);
  const [draggingVariant, setDraggingVariant] = useState<string | null>(null);
  const [clearDataModal, setClearDataModal] = useState(false);
  const [isClearingData, setIsClearingData] = useState(false);
  const [clearingSuccess, setClearingSuccess] = useState(false);

  // Removido: Não salvar mais no localStorage, usar banco de dados
  // React.useEffect(() => {
  //   try {
  //     localStorage.setItem('qc.projects', JSON.stringify(projects));
  //   } catch { }
  // }, [projects]);

  React.useEffect(() => {
    setGeminiKey(settings?.gemini_api_key || '');
  }, [settings]);

  React.useEffect(() => {
    if (isLoggedIn && authReady) {
      try { loadSettings(); } catch { }
    }
  }, [isLoggedIn, authReady]);

  React.useEffect(() => {
    try {
      localStorage.setItem('qc.user.name', userName || '');
      localStorage.setItem('qc.user.logged', String(isLoggedIn));
    } catch { }
  }, [userName, isLoggedIn]);

  // Efeito para gerenciar autenticação
  React.useEffect(() => {
    let mounted = true;

    // Função para processar sessão
    const handleSession = (session: any) => {
      if (!mounted) return;
      const currentUser = session?.user || null;

      // Se não há usuário, limpar projetos IMEDIATAMENTE
      if (!currentUser) {
        clearProjects();
      }

      setIsLoggedIn(!!currentUser);
      setUserName(currentUser?.user_metadata?.full_name || currentUser?.email?.split('@')[0] || '');
      setAuthReady(true);
    };

    // 1. Check inicial com getSession
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSession(session);
    }).catch(err => {
      console.error('Erro ao verificar sessão:', err);
      if (mounted) {
        setAuthReady(true);
        setIsLoggedIn(false);
      }
    });

    // 2. Listener para mudanças
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      handleSession(session);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [clearProjects]);

  // Efeito para carregar projetos quando logado
  React.useEffect(() => {
    if (isLoggedIn && authReady) {
      loadProjects().catch(err => console.error('Erro ao carregar projetos:', err));
    }
  }, [isLoggedIn, authReady, loadProjects]);



  const handleLogin = useCallback(async (email: string, password: string) => {
    try {
      setLoginError('');

      // SEGURANÇA: Limpar TUDO antes de fazer login
      clearProjects();
      resetUIState();

      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setLoginError(error.message || 'Falha na autenticação');
        return;
      }
      const user = data.user;
      setIsLoggedIn(!!user);
      setUserName(user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Usuário');
      try {
        localStorage.setItem('qc.user.name', user?.user_metadata?.full_name || user?.email || '');
        localStorage.setItem('qc.user.logged', String(!!user));
      } catch { }
      setAppView('dashboard');
      await loadProjects();
    } catch (e) {
      setLoginError(e instanceof Error ? e.message : 'Erro de login');
    }
  }, [clearProjects, resetUIState, loadProjects]);

  const logout = useCallback(async () => {
    // IMPORTANTE: resetUIState já limpa projetos e quizzes
    setIsLoggedIn(false);
    resetUIState();

    try { await supabase.auth.signOut(); } catch { }

    setAppView('dashboard');

    // SEGURANÇA: Limpar TODAS as configurações do usuário do localStorage
    localStorage.removeItem('qc.user.logged');
    localStorage.removeItem('qc.user.name');
  }, [resetUIState]);




  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if (e.ctrlKey && e.altKey && k === 'l') {
        logout();
      }
      if (e.ctrlKey && e.altKey && k === 'r') {
        try { supabase.auth.signOut(); } catch { }
        try { localStorage.clear(); sessionStorage.clear(); } catch { }
        window.location.reload();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [logout]);

  const getCurrentProject = useCallback(() => projects.find(p => p.id === currentProjectId) || null, [projects, currentProjectId]);
  const getCurrentQuiz = useCallback(() => {
    const proj = projects.find(p => p.id === currentProjectId);
    if (!proj) return null;
    return proj.quizzes.find(q => q.id === currentQuizId) || null;
  }, [projects, currentProjectId, currentQuizId]);

  const openProject = useCallback((projectId: string) => {
    setCurrentProjectId(projectId);
  }, []);

  const backToProjects = useCallback(() => {
    setCurrentProjectId(null);
  }, []);

  const handleCreateProject = useCallback(async (name: string) => {
    try {
      await createProject(name);
      console.log('Projeto criado com sucesso no banco!');
    } catch (error) {
      console.error('Erro ao criar projeto:', error);
      setErrorModal({ title: 'Erro ao criar projeto', message: (error instanceof Error ? error.message : 'Erro desconhecido') });
    }
  }, [createProject]);

  const renameProject = useCallback(async (projectId: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    try {
      await updateProject(projectId, { name: trimmed });
    } catch (error) {
      console.error('Erro ao renomear projeto:', error);
      setErrorModal({ title: 'Erro ao renomear projeto', message: (error instanceof Error ? error.message : 'Erro desconhecido') });
    }
  }, [updateProject]);

  const [pendingDeleteProject, setPendingDeleteProject] = useState<{ id: string; name: string; quizCount: number } | null>(null);
  const [pendingDeleteQuiz, setPendingDeleteQuiz] = useState<{ projectId: string; quizId: string; name: string } | null>(null);
  const [errorModal, setErrorModal] = useState<{ title: string; message: string } | null>(null);
  const [successModal, setSuccessModal] = useState<{ title: string; message: string } | null>(null);
  React.useEffect(() => {
    if (addingProject) {
      setTimeout(() => newProjectInputRef.current?.focus(), 0);
    }
  }, [addingProject]);

  React.useEffect(() => {
    if (addingQuiz) {
      setTimeout(() => newQuizInputRef.current?.focus(), 0);
    }
  }, [addingQuiz]);

  const isSlugTakenByUser = useCallback((slug: string) => {
    const normalized = toSlug(slug || '');
    if (!normalized) return false;
    return projects.some(p => p.quizzes.some(q => (q.slug || '') === normalized));
  }, [projects]);

  const suggestUniqueSlug = useCallback((base: string) => {
    let root = toSlug(base || '');
    if (!root) root = 'quiz';
    if (!isSlugTakenByUser(root)) return root;
    let i = 2;
    while (isSlugTakenByUser(`${root} -${i} `)) i++;
    return `${root} -${i} `;
  }, [isSlugTakenByUser]);

  const [duplicateLanguageModal, setDuplicateLanguageModal] = useState<{ projectId: string; quizId: string; quizName: string } | null>(null);
  const [translationLoading, setTranslationLoading] = useState<boolean>(false);
  const [generateQuizModal, setGenerateQuizModal] = useState<{ projectId: string; projectName: string } | null>(null);
  const [shareQuizModal, setShareQuizModal] = useState<{ quizId: string; shareCode: string } | null>(null);
  const [statsPeriod, setStatsPeriod] = useState<'today' | '7d' | '30d' | 'all' | 'custom'>('7d');
  const [statsCustomFrom, setStatsCustomFrom] = useState<string>('');
  const [statsCustomTo, setStatsCustomTo] = useState<string>('');
  const [projectSettingsModal, setProjectSettingsModal] = useState<{ project: ProjectMeta } | null>(null);

  // Hook de estatísticas - sempre chamado no topo (regra dos hooks)
  // Obter dados do quiz atual para passar ao hook de estatísticas
  // IMPORTANTE: SEMPRE usar dados do projects (atualizados após salvar)
  // O quizData local só é usado no editor para preview em tempo real
  const currentQuizForStats = React.useMemo(() => {
    console.log('📊 [Stats] useMemo EXECUTADO', {
      currentProjectId,
      currentQuizId,
      projectsCount: projects.length,
      appView
    });

    const project = projects.find(p => p.id === currentProjectId);
    console.log('📊 [Stats] useMemo: Project encontrado?', {
      found: !!project,
      projectId: project?.id,
      quizzesCount: project?.quizzes.length || 0,
      quizIds: project?.quizzes.map(q => q.id) || []
    });

    const quiz = project?.quizzes.find(q => q.id === currentQuizId);
    if (!quiz) {
      console.log('📊 [Stats] useMemo: Quiz não encontrado');
      return null;
    }

    // SEMPRE usar dados do projects (atualizados após salvar)
    // O quizData local pode estar desatualizado se não foi salvo ainda
    console.log('📊 [Stats] useMemo: Quiz encontrado, usando dados do projects', {
      quizId: quiz.id,
      quizName: quiz.name,
      pagesCount: quiz.data?.pages?.length || 0,
      pagesOrder: quiz.data?.pages?.map((p, i) => ({
        index: i,
        title: p.title,
        id: p.id,
        uniqueId: p.uniqueId
      })) || [],
      fullPagesData: quiz.data?.pages || []
    });

    return quiz;
  }, [projects, currentProjectId, currentQuizId, appView]);

  const { statistics, loading: statsLoading, error: statsError, refetch: refreshStats } = useQuizStatistics({
    quizId: appView === 'stats' && currentQuizId ? currentQuizId : '',
    period: statsPeriod,
    customFrom: statsPeriod === 'custom' ? statsCustomFrom : undefined,
    customTo: statsPeriod === 'custom' ? statsCustomTo : undefined,
    quizData: currentQuizForStats?.data
  });

  console.log('📊 [Stats] Hook useQuizStatistics retornou:', {
    hasStatistics: !!statistics,
    loading: statsLoading,
    error: statsError,
    funnelDataCount: statistics?.funnelData?.length || 0,
    funnelData: statistics?.funnelData?.map(f => ({ step: f.step, label: f.label, views: f.views })) || []
  });



  const renameQuiz = useCallback(async (projectId: string, quizId: string, newName: string) => {
    const trimmedName = newName.trim();
    if (trimmedName) {
      await updateQuiz(projectId, quizId, { name: trimmedName });
    }
  }, [updateQuiz]);

  const createQuizInProject = useCallback(async (projectId: string, name: string, slug: string, data: QuizData) => {
    try {
      const trimmedName = name.trim();
      const normalizedName = trimmedName.toLowerCase();
      if (!trimmedName) {
        setErrorModal({ title: 'Erro ao criar quiz', message: 'Nome do quiz inválido' });
        return false;
      }
      const targetProject = projects.find(p => p.id === projectId);
      const hasDuplicateName = targetProject?.quizzes.some(q => (q.name || '').trim().toLowerCase() === normalizedName);
      if (hasDuplicateName) {
        setErrorModal({ title: 'Erro ao criar quiz', message: 'Já existe um quiz com este nome' });
        return false;
      }
      const currentUserId = await supabaseRPC.getCurrentUserId();
      if (!currentUserId) {
        setErrorModal({ title: 'Erro ao criar quiz', message: 'Usuário não autenticado' });
        return false;
      }

      // Garantir que todas as páginas tenham uniqueId antes de salvar
      const dataWithUniqueIds = ensurePagesHaveUniqueIds(data);

      // Criar quiz no banco de dados
      const newQuiz = await supabaseRPC.createQuiz({
        project_id: projectId,
        user_id: currentUserId,
        name: trimmedName || 'Novo Quiz',
        slug: slug || undefined,
        data: dataWithUniqueIds,
        status: 'draft',

      });

      // Atualizar estado local com o quiz criado
      const project = projects.find(p => p.id === projectId);
      if (project) {
        const q: StoredQuiz = {
          id: newQuiz.id,
          name: newQuiz.name,
          slug: newQuiz.slug,
          data: newQuiz.data,
          status: newQuiz.status,
          share_id: newQuiz.share_id,
          createdAt: new Date(newQuiz.created_at).getTime(),
          updatedAt: new Date(newQuiz.updated_at).getTime()
        };
        const updatedQuizzes = [q, ...project.quizzes];
        await updateProject(projectId, { name: project.name, quizzes: updatedQuizzes });
      }
      return true;
    } catch (error) {
      console.error('Erro ao criar quiz:', error);
      setErrorModal({ title: 'Erro ao criar quiz', message: (error instanceof Error ? error.message : 'Erro desconhecido') });
      return false;
    }
  }, [projects, updateProject]);

  const openEditor = useCallback((projectId: string, quizId: string) => {
    setCurrentProjectId(projectId);
    setCurrentQuizId(quizId);
    const proj = projects.find(p => p.id === projectId);
    const quiz = proj?.quizzes.find(q => q.id === quizId);
    if (quiz) {
      const validatedData = ensurePagesHaveUniqueIds(quiz.data);
      setQuizData({ ...validatedData, name: quiz.name });
      setSelectedPageIndex(0);
      setPublished(quiz.status === 'published');
      setAppView('editor');
    }
  }, [projects, ensurePagesHaveUniqueIds]);

  // Auto-save desabilitado - salvamento manual apenas
  // React.useEffect(() => {
  //   if (appView !== 'editor') return;
  //   if (!currentProjectId || !currentQuizId) return;

  //   const saveQuizData = async () => {
  //     try {
  //       // Atualizar no banco de dados primeiro
  //       await supabaseRPC.updateQuiz(currentQuizId, {
  //         data: quizData,
  //         updated_at: new Date().toISOString()
  //       });

  //       // Atualizar estado local
  //       const project = projects.find(p => p.id === currentProjectId);
  //       if (project) {
  //         const updatedQuizzes = project.quizzes.map(q =>
  //           q.id === currentQuizId ? {...q, data: quizData, updatedAt: Date.now() } : q
  //         );
  //         console.log('💾 [Editor] Salvando quiz, atualizando projects:', {
  //           quizId: currentQuizId,
  //           pagesCount: quizData.pages?.length || 0,
  //           pagesOrder: quizData.pages?.map((p, i) => ({index: i, title: p.title })) || []
  //         });
  //         await updateProject(currentProjectId, {name: project.name, quizzes: updatedQuizzes });
  //         console.log('💾 [Editor] Projects atualizado após salvar');
  //       }
  //     } catch (error) {
  //       console.error('Erro ao salvar dados do quiz:', error);
  //     }
  //   };

  //   const timeoutId = setTimeout(saveQuizData, 1000); // Debounce de 1 segundo
  //   return () => clearTimeout(timeoutId);
  // }, [quizData, appView, currentProjectId, currentQuizId, projects, updateProject]);

  // Estado para controlar o salvamento manual
  const [isSaving, setIsSaving] = useState(false);

  const togglePublishQuiz = useCallback(async (projectId: string, quizId: string) => {
    try {
      // Encontrar o quiz atual
      const currentProject = projects.find(p => p.id === projectId);
      const currentQuiz = currentProject?.quizzes.find(q => q.id === quizId);

      if (!currentQuiz) return;

      const newStatus = currentQuiz.status === 'published' ? 'draft' : 'published';

      // Atualizar estado local IMEDIATAMENTE para feedback visual rápido
      setPublished(newStatus === 'published');

      // Atualizar status no Supabase
      await supabaseRPC.toggleQuizStatus(quizId, newStatus);

      // Atualizar estado global dos projetos
      const project = projects.find(p => p.id === projectId);
      if (project) {
        const updatedQuizzes = project.quizzes.map(q => q.id === quizId ? { ...q, status: newStatus, updatedAt: Date.now() } : q);
        await updateProject(projectId, { name: project.name, quizzes: updatedQuizzes });
      }

    } catch (error) {
      console.error('Erro ao alternar status do quiz:', error);
      // Reverter estado visual em caso de erro
      setPublished(prev => !prev);
      alert('Erro ao alterar status do quiz. Por favor, tente novamente.');
    }
  }, [projects, updateProject]);

  const saveQuizNow = useCallback(async () => {
    console.log('🔘 [saveQuizNow] Botão clicado!', { currentProjectId, currentQuizId });
    if (!currentProjectId || !currentQuizId) {
      console.warn('⚠️ [saveQuizNow] Faltam IDs:', { currentProjectId, currentQuizId });
      return;
    }
    setIsSaving(true);
    console.log('💾 [saveQuizNow] Iniciando salvamento...');
    try {
      await supabaseRPC.updateQuiz(currentQuizId, {
        data: quizData
      });
      console.log('✅ [saveQuizNow] Quiz atualizado no Supabase');
      const project = projects.find(p => p.id === currentProjectId);
      if (project) {
        const updatedQuizzes = project.quizzes.map(q =>
          q.id === currentQuizId ? { ...q, data: quizData, updatedAt: Date.now() } : q
        );
        await updateProject(currentProjectId, { name: project.name, quizzes: updatedQuizzes });
        console.log('✅ [saveQuizNow] Projeto atualizado localmente');
      }
      console.log('✅ [saveQuizNow] Salvamento concluído com sucesso!');
    } catch (error) {
      console.error('❌ [saveQuizNow] Erro ao salvar:', error);
      alert('Erro ao salvar dados do quiz. Por favor, tente novamente.');
    } finally {
      setIsSaving(false);
      console.log('🏁 [saveQuizNow] Finalizando salvamento');
    }
  }, [currentProjectId, currentQuizId, quizData, projects, updateProject]);

  const handleClearData = useCallback(async () => {
    try {
      setIsClearingData(true);
      // Usar RPC segura para limpar dados (bypassing RLS para deletar sessões de outros usuários/anônimos)
      const { error } = await supabase.rpc('clear_quiz_data_v2', {
        p_quiz_id: currentQuizId || ''
      });

      if (error) throw error;

      await refreshStats();
      setIsClearingData(false);
      setClearingSuccess(true);

      // Fechar modal após 1.5s
      setTimeout(() => {
        setClearingSuccess(false);
        setClearDataModal(false);
      }, 1500);

    } catch (err: any) {
      setIsClearingData(false);
      console.error('Erro ao zerar dados:', err);
      // Feedback moderno de erro
      setErrorModal({
        title: 'Erro ao zerar dados',
        message: err.message || String(err)
      });
    }
  }, [currentQuizId, refreshStats]);

  const duplicateQuiz = useCallback(async (projectId: string, quizId: string) => {
    try {
      const currentUserId = await supabaseRPC.getCurrentUserId();
      if (!currentUserId) {
        throw new Error('Usuário não autenticado');
      }

      const project = projects.find(p => p.id === projectId);
      if (project) {
        const base = project.quizzes.find(q => q.id === quizId);
        if (base) {
          // Criar cópia no banco de dados
          const newQuiz = await supabaseRPC.createQuiz({
            project_id: projectId,
            user_id: currentUserId,
            name: `Cópia de ${base.name} `,
            slug: `${base.slug || 'quiz'} -copia`,
            data: base.data,
            status: 'draft',

          });

          // Atualizar estado local
          const copy: StoredQuiz = {
            id: newQuiz.id,
            name: newQuiz.name,
            slug: newQuiz.slug,
            data: newQuiz.data,
            status: newQuiz.status,
            share_id: newQuiz.share_id,
            createdAt: new Date(newQuiz.created_at).getTime(),
            updatedAt: new Date(newQuiz.updated_at).getTime()
          };
          const updatedQuizzes = [copy, ...project.quizzes];
          await updateProject(projectId, { name: project.name, quizzes: updatedQuizzes });
        }
      }
    } catch (error) {
      console.error('Erro ao duplicar quiz:', error);
      alert('Erro ao duplicar quiz: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
      throw error;
    }
  }, [projects, updateProject]);



  const duplicateQuizLanguage = useCallback(async (projectId: string, quizId: string, lang: string) => {
    console.log('🔄 [DuplicateLanguage] Iniciando duplicação:', { projectId, quizId, lang });
    setTranslationLoading(true);
    try {
      console.log('🔄 [DuplicateLanguage] Verificando usuário...');
      const currentUserId = await supabaseRPC.getCurrentUserId();
      if (!currentUserId) {
        throw new Error('Usuário não autenticado');
      }
      console.log('✅ [DuplicateLanguage] Usuário autenticado:', currentUserId);

      console.log('🔄 [DuplicateLanguage] Buscando projeto e quiz...');
      const project = projects.find(p => p.id === projectId);
      if (!project) {
        throw new Error(`Projeto ${projectId} não encontrado`);
      }
      console.log('✅ [DuplicateLanguage] Projeto encontrado:', project.name);

      const base = project.quizzes.find(q => q.id === quizId);
      if (!base) {
        throw new Error(`Quiz ${quizId} não encontrado no projeto`);
      }
      console.log('✅ [DuplicateLanguage] Quiz base encontrado:', base.name);

      // Verificar se há chave Gemini configurada
      console.log('🔄 [DuplicateLanguage] Verificando chave Gemini...');
      if (!settings?.gemini_api_key) {
        throw new Error('Chave da API Gemini não configurada. Configure uma chave nas configurações.');
      }
      console.log('✅ [DuplicateLanguage] Chave Gemini configurada');

      // Extrair códigos de idioma
      const extractLangCode = (lang: string) => {
        const match = lang.match(/\(([^)]+)\)/);
        return match ? match[1] : lang.trim();
      };

      const targetLangCode = extractLangCode(lang);
      const sourceLangCode = 'pt'; // Assumir português como idioma de origem por padrão

      console.log('🔄 [DuplicateLanguage] Iniciando tradução...', {
        sourceLang: lang,
        targetLangCode,
        sourceLangCode,
        originalName: base.name
      });

      // Traduzir os dados completos do quiz em uma única chamada
      console.log('🔄 [DuplicateLanguage] Chamando translateQuizWithGemini (tradução completa)...');
      const translatedData = await translateQuizWithGemini(base.data, sourceLangCode, targetLangCode);
      console.log('✅ [DuplicateLanguage] Tradução completa finalizada!');

      console.log('🔄 [DuplicateLanguage] Criando quiz duplicado no banco...');
      // Criar cópia no banco de dados com dados traduzidos
      const newQuiz = await supabaseRPC.createQuiz({
        project_id: projectId,
        user_id: currentUserId,
        name: `${base.name} (${lang})`,
        slug: `${base.slug || 'quiz'}-${targetLangCode}`,
        data: translatedData,
        status: 'draft'
      });
      console.log('✅ [DuplicateLanguage] Quiz criado no banco:', newQuiz.id);

      // Atualizar estado local
      console.log('🔄 [DuplicateLanguage] Atualizando estado local...');
      const copy: StoredQuiz = {
        id: newQuiz.id,
        name: newQuiz.name,
        slug: newQuiz.slug,
        data: newQuiz.data,
        status: newQuiz.status,
        createdAt: new Date(newQuiz.created_at).getTime(),
        updatedAt: new Date(newQuiz.updated_at).getTime()
      };
      console.log('✅ [DuplicateLanguage] Quiz local criado:', copy.name);

      const updatedQuizzes = [copy, ...project.quizzes];
      console.log('🔄 [DuplicateLanguage] Chamando updateProject...');
      await updateProject(projectId, { name: project.name, quizzes: updatedQuizzes });
      console.log('✅ [DuplicateLanguage] Estado atualizado com sucesso');
    } catch (error) {
      console.error('❌ [DuplicateLanguage] Erro ao duplicar quiz com idioma:', error);
      alert('Erro ao duplicar quiz com idioma: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
      throw error;
    } finally {
      console.log('🏁 [DuplicateLanguage] Processo finalizado');
      setTranslationLoading(false);
    }
  }, [projects, updateProject, settings, translateQuizWithGemini]);

  const exportQuiz = useCallback((quiz: StoredQuiz) => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(quiz.data, null, 2));
    const dl = document.createElement('a');
    dl.setAttribute('href', dataStr);
    dl.setAttribute('download', `${quiz.name.replace(/\s+/g, '_')}.json`);
    dl.click();
  }, []);

  const deleteQuiz = useCallback(async (projectId: string, quizId: string) => {
    try {
      // Deletar do banco de dados primeiro
      await supabaseRPC.deleteQuiz(quizId);

      // Atualizar estado local
      const project = projects.find(p => p.id === projectId);
      if (project) {
        const updatedQuizzes = project.quizzes.filter(q => q.id !== quizId);
        await updateProject(projectId, { name: project.name, quizzes: updatedQuizzes });
      }
    } catch (error) {
      console.error('Erro ao deletar quiz:', error);
      alert('Erro ao deletar quiz: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
      throw error;
    }
  }, [projects, updateProject]);
  const importQuizToProject = useCallback(async (projectId: string, shareCode: string, slug: string) => {
    try {
      const quiz = await supabaseRPC.getQuizByShareId(shareCode);
      if (!quiz) {
        console.warn('Quiz não encontrado para o share_id informado');
        return false;
      }
      // Manter slug informado (usuário pode definir)
      const finalSlug = slug;
      // Gerar nome único para o quiz importado
      const project = projects.find(p => p.id === projectId);
      const baseName = `Importado: ${quiz.name}`;
      let finalName = baseName;
      if (project) {
        const nameExists = (name: string) => project.quizzes.some(q => q.name === name);
        let counter = 1;
        while (nameExists(finalName)) {
          finalName = `${baseName} (${counter})`;
          counter++;
        }
      }
      const ok = await createQuizInProject(projectId, finalName, finalSlug, quiz.data);
      return !!ok;
    } catch (e) {
      console.error('Erro ao importar quiz:', e);
      return false;
    }
  }, [createQuizInProject, projects]);



  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleUpdateConfig = useCallback((newConfig: QuizConfig) => {
    setQuizData(prev => ({ ...prev, config: newConfig }));
  }, []);

  const handleAddPage = useCallback(() => {
    const newPage: Page = {
      id: quizData.pages.length, // Índice sequencial
      uniqueId: generatePageUniqueId(), // UUID único para tracking
      title: "Nova Página",
      elements: [],
    };
    setQuizData(prev => ({ ...prev, pages: [...prev.pages, newPage] }));
    setSelectedPageIndex(quizData.pages.length);
  }, [quizData.pages.length]);

  const handleDuplicatePage = useCallback((index: number) => {
    setQuizData(prev => {
      const pageToDuplicate = prev.pages[index];
      if (!pageToDuplicate) return prev;

      const duplicatedPage: Page = {
        ...pageToDuplicate,
        id: 0, // Será substituído no map
        title: `${pageToDuplicate.title || 'Página'} - Cópia`,
        uniqueId: generatePageUniqueId(),
        elements: (pageToDuplicate.elements || []).map(el => ({
          ...el,
          id: generateId()
        }))
      };

      const newPages = [...prev.pages];
      newPages.splice(index + 1, 0, duplicatedPage);

      const reIndexedPages = newPages.map((page, newIndex) => ({
        ...page,
        id: newIndex
      }));

      setSelectedPageIndex(index + 1);
      return { ...prev, pages: reIndexedPages };
    });
  }, []);

  const handleDeletePage = useCallback((index: number) => {
    setQuizData(prev => {
      const newPages = prev.pages
        .filter((_, i) => i !== index)
        .map((page, newIndex) => ({
          ...page,
          id: newIndex // Atualizar ID sequencial, preservar uniqueId
        }));
      if (selectedPageIndex === index) {
        setSelectedPageIndex(Math.max(0, index - 1));
      } else if (selectedPageIndex > index) {
        setSelectedPageIndex(selectedPageIndex - 1);
      }
      return { ...prev, pages: newPages };
    });
  }, [selectedPageIndex]);

  const handleReorderPages = useCallback((oldIndex: number, newIndex: number) => {
    setQuizData(prev => {
      const reorderedPages = arrayMove(prev.pages, oldIndex, newIndex);

      // Atualizar IDs sequenciais, preservar uniqueIds
      const newPages = reorderedPages.map((page, index) => {
        if (!page || typeof page !== 'object') {
          return page;
        }
        return {
          ...page,
          id: index // Atualizar ID sequencial, preservar uniqueId
        };
      });

      // Ajustar selectedPageIndex se necessário
      let newSelectedIndex = selectedPageIndex;
      if (selectedPageIndex === oldIndex) {
        newSelectedIndex = newIndex;
      } else if (selectedPageIndex > oldIndex && selectedPageIndex <= newIndex) {
        newSelectedIndex = selectedPageIndex - 1;
      } else if (selectedPageIndex < oldIndex && selectedPageIndex >= newIndex) {
        newSelectedIndex = selectedPageIndex + 1;
      }

      setSelectedPageIndex(newSelectedIndex);
      return { ...prev, pages: newPages };
    });
  }, [selectedPageIndex]);

  const handleUpdatePage = useCallback((index: number, updatedPage: Page) => {
    setQuizData(prev => {
      const newPages = [...prev.pages];
      newPages[index] = updatedPage;
      return { ...prev, pages: newPages };
    });
  }, []);

  const selectedPage = useMemo(() => {
    return quizData.pages[selectedPageIndex];
  }, [quizData.pages, selectedPageIndex]);

  React.useEffect(() => {
    setSelectedElementId(null);
  }, [selectedPageIndex]);

  const updateElements = useCallback((elements: PageElement[]) => {
    console.log('🟢 [updateElements] Atualizando elementos da página:', {
      pageIndex: selectedPageIndex,
      totalElements: elements.length,
      buttons: elements.filter(e => e.type === ElementType.Button).length,
      prices: elements.filter(e => e.type === ElementType.Price).length,
      elementTypes: elements.map(e => e.type)
    });
    setQuizData(prev => {
      const pages = [...prev.pages];
      pages[selectedPageIndex] = { ...pages[selectedPageIndex], elements };
      return { ...prev, pages };
    });
  }, [selectedPageIndex]);

  const updateSelectedElement = useCallback((updated: PageElement) => {
    setQuizData(prev => {
      const pages = [...prev.pages];
      const page = pages[selectedPageIndex];
      const idx = (page.elements || []).findIndex(el => el.id === updated.id);
      if (idx >= 0) {
        const newElements = [...(page.elements || [])];
        newElements[idx] = updated;
        pages[selectedPageIndex] = { ...page, elements: newElements };
      }
      return { ...prev, pages };
    });
  }, [selectedPageIndex]);

  const deleteSelectedElement = useCallback(() => {
    if (!selectedPage || !selectedElementId) return;
    const newElements = (selectedPage.elements || []).filter(el => el.id !== selectedElementId);
    updateElements(newElements);
    setSelectedElementId(null);
  }, [selectedPage, selectedElementId, updateElements]);

  const moveSelectedElement = useCallback((direction: 'up' | 'down') => {
    if (!selectedPage || !selectedElementId) return;
    const elements = [...(selectedPage.elements || [])];
    const idx = elements.findIndex(el => el.id === selectedElementId);
    if (idx < 0) return;
    if (direction === 'up' && idx > 0) {
      [elements[idx], elements[idx - 1]] = [elements[idx - 1], elements[idx]];
      updateElements(elements);
    } else if (direction === 'down' && idx < elements.length - 1) {
      [elements[idx], elements[idx + 1]] = [elements[idx + 1], elements[idx]];
      updateElements(elements);
    }
  }, [selectedPage, selectedElementId, updateElements]);

  const headerRef = React.useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = React.useState<number>(0);
  React.useEffect(() => {
    const compute = () => {
      const h = headerRef.current ? headerRef.current.getBoundingClientRect().height : 0;
      setAvailableHeight(Math.max(window.innerHeight - h, 0));
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  const canvasRef = React.useRef<HTMLDivElement>(null);
  const [canvasOverflow, setCanvasOverflow] = React.useState(false);
  React.useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const diff = Math.ceil(el.scrollHeight) - Math.ceil(el.clientHeight);
    setCanvasOverflow(diff > 0);
  });
  const canvasAlignClass = quizData.config.contentAlignment === 'top' ? 'items-start pt-4' : (canvasOverflow ? 'items-center pt-4 justify-start' : 'items-center justify-center');
  const canvasInnerMinH = (quizData.config.contentAlignment === 'top' || canvasOverflow) ? 'min-h-full' : '';
  const canvasInnerLayout = (quizData.config.contentAlignment === 'center' && !canvasOverflow) ? 'grid place-items-center justify-items-stretch h-full' : '';

  const handleQuickAdd = useCallback((elementType: ElementType, variant?: string) => {
    if (!selectedPage) return;
    console.log('🔵 [handleQuickAdd] Adicionando elemento:', { elementType, variant, pageId: selectedPage.id });
    const base: any = { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, type: elementType };
    const newElement: PageElement = (() => {
      switch (elementType) {
        case ElementType.Title: return { ...base, type: ElementType.Title, content: 'Novo Título', align: 'center' } as PageElement;
        case ElementType.Text: return { ...base, type: ElementType.Text, content: 'Novo texto...', align: 'center' } as PageElement;
        case ElementType.Alert: return { ...base, type: ElementType.Alert, content: 'Mensagem do alerta', variant: 'error' } as PageElement;
        case ElementType.Timer: return { ...base, type: ElementType.Timer, content: '⏱️ Faltam <strong>[time]</strong> minutos para o fim da promoção', durationSeconds: 60, variant: 'error', align: 'center' } as PageElement;
        case ElementType.List: return {
          ...base,
          type: ElementType.List,
          items: [
            { title: 'Benefício 1', description: 'Descrição do benefício com <strong>HTML</strong> e emojis 🙂', bgColor: '#ffffff', textColor: quizData.config.colors.text || '#374151' },
            { title: 'Benefício 2', description: 'Texto de apoio com detalhes e exemplos.', bgColor: '#ffffff', textColor: quizData.config.colors.text || '#374151' },
            { title: 'Benefício 3', description: 'Use <em>ênfase</em>, quebras de linha e símbolos.', bgColor: '#ffffff', textColor: quizData.config.colors.text || '#374151' }
          ],
          titlePosition: 'top',
          layout: 'list',
          border: 'small',
          shadow: 'none',
          spacing: 'medium',
          textAlign: 'left'
        } as PageElement;
        case ElementType.Form:
          return {
            ...base,
            type: ElementType.Form,
            title: 'Texto',
            fieldType: 'text',
            placeholder: '',
            mask: '',
            required: false,
            align: 'left',
            border: 'small',
            shadow: 'none'
          } as PageElement;
        case ElementType.Seal: return { ...base, type: ElementType.Seal, icon: 'medal', iconColor: quizData.config.colors.title || '#0f172a', iconSize: 'medium', title: 'Seu risco é zero!', description: 'Utilize este componente para deixar o seu cliente tranquilo em relação às garantias que você oferece.', align: 'center', border: 'small', shadow: 'none' } as PageElement;
        case ElementType.Price: return {
          ...base,
          type: ElementType.Price,
          items: [
            { title: 'Plano Start', value: 'R$ 59,90', suffix: 'à vista', color: 'neutral', priceType: 'illustrative', enableCheckbox: false },
            { title: 'Plano Pro', prefix: '10% off', value: 'R$ 89,90', suffix: 'à vista', topHighlight: '⭐ Mais Escolhido', color: 'neutral', priceType: 'illustrative', enableCheckbox: false },
            { title: 'Plano Gold', prefix: '10% off', value: 'R$ 129,90', suffix: 'à vista', color: 'neutral', priceType: 'illustrative', enableCheckbox: false }
          ],
          layout: 'horizontal',
          verticalAlign: 'bottom',
          border: 'small',
          shadow: 'none'
        } as PageElement;
        case ElementType.Options: {
          if (variant === 'single') {
            return {
              ...base,
              type: ElementType.Options,
              title: 'Qual a questão a ser respondida?',
              description: 'Digite aqui uma descrição de ajuda para introduzir o usuário à questão.',
              options: [
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 1' },
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 2' },
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 3' }
              ],
              multiple: false,
              layout: 'list',
              spacing: 'medium',
              style: 'simple',
              checkStyle: 'traditional',
              checkPosition: 'before',
              border: 'small',
              shadow: 'none'
            } as PageElement;
          }
          if (variant === 'multi') {
            return {
              ...base,
              type: ElementType.Options,
              title: 'Qual a questão a ser respondida?',
              description: 'Digite aqui uma descrição de ajuda para introduzir o usuário à questão.',
              options: [
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 1' },
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 2' },
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 3' },
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 4' },
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 5' },
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 6' }
              ],
              multiple: true,
              layout: 'grid2',
              spacing: 'medium',
              style: 'simple',
              checkStyle: 'traditional',
              checkPosition: 'before',
              border: 'small',
              shadow: 'none'
            } as PageElement;
          }
          if (variant === 'binary') {
            return {
              ...base,
              type: ElementType.Options,
              title: 'Qual a questão a ser respondida?',
              description: 'Digite aqui uma descrição de ajuda para introduzir o usuário à questão.',
              options: [
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: '✅ Sim' },
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: '🚫 Não' }
              ],
              multiple: false,
              layout: 'grid2',
              spacing: 'medium',
              style: 'simple',
              checkStyle: 'none',
              textAlign: 'center',
              border: 'small',
              shadow: 'none'
            } as PageElement;
          }
          return {
            ...base,
            type: ElementType.Options,
            title: 'Qual a questão a ser respondida?',
            description: 'Digite aqui uma descrição de ajuda para introduzir o usuário à questão.',
            options: [
              { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 1' },
              { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 2' },
              { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 3' },
              { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 4' }
            ],
            multiple: false,
            layout: 'grid3',
            spacing: 'medium',
            style: 'simple',
            checkStyle: 'traditional',
            checkPosition: 'before',
            border: 'small',
            shadow: 'none'
          } as PageElement;
        }
        case ElementType.QA: return {
          ...base,
          type: ElementType.QA,
          items: [
            { question: 'Qual a primeira dúvida a ser resolvida?', answer: 'Este é apenas um texto de exemplo utilizado para ilustrar como a resposta de uma dúvida frequente será exibida nesta seção.' },
            { question: 'Descreva outra dúvida a ser resolvida.', answer: 'Texto genérico de demonstração. Serve apenas como modelo visual para representar uma resposta real a ser inserida posteriormente.' }
          ],
          openFirst: true,
          allowMultiple: false,
          border: 'small',
          shadow: 'none'
        } as PageElement;
        case ElementType.Button: return { ...base, type: ElementType.Button, text: 'Continuar', widthPercent: 100, height: 'medium' } as PageElement;
        case ElementType.Video: return { ...base, type: ElementType.Video, url: '', title: '' } as PageElement;
        case ElementType.Image: return { ...base, type: ElementType.Image, url: '', alt: '' } as PageElement;
        case ElementType.BeforeAfter: return { ...base, type: ElementType.BeforeAfter, beforeUrl: '', afterUrl: '', beforeLabel: 'Antes', afterLabel: 'Depois', startPercent: 50, orientation: 'vertical', align: 'center', widthPercent: 100, border: 'small', shadow: 'none' } as PageElement;
        case ElementType.Audio: return { ...base, type: ElementType.Audio, url: '', question: '' } as PageElement;
        case ElementType.Spacer: return { ...base, type: ElementType.Spacer, height: 20 } as PageElement;
        case ElementType.Loading: return { ...base, type: ElementType.Loading, title: 'Analisando...', subtitle: 'Estamos analisando suas respostas...', durationSeconds: 10, showPercent: true, barColor: quizData.config.colors.primary } as PageElement;
        case ElementType.Testimonial: return { ...base, type: ElementType.Testimonial, name: 'Nome da pessoa', info: '@usuario', stars: 5, text: '"Escreva aqui o depoimento..."', imageUrl: '', starColor: '#f59e0b', border: 'small', shadow: 'small' } as PageElement;
        case ElementType.Carousel: return { ...base, type: ElementType.Carousel, items: [{ id: generateId(), imageUrl: '', description: 'Exemplo de descrição' }, { id: generateId(), imageUrl: '', description: 'Exemplo de descrição' }], layout: 'image-text', pagination: true, autoplay: false, align: 'center', widthPercent: 100, ratio: 'auto' } as PageElement;
        case ElementType.Level: return { ...base, type: ElementType.Level, title: 'Seu Potencial de Sucesso', subtitle: '* Baseado nos dados de nossos clientes semelhantes a você', percent: 97, indicatorText: '', legendLeft: 'Normal', legendCenter: 'Médio', legendRight: 'Alto', showMeter: true, showProgress: true, styleType: 'dashes', colorTheme: 'theme', border: 'none', shadow: 'none', backgroundColor: quizData.config.colors.background, titleColor: quizData.config.colors.title || '#1e293b', textColor: quizData.config.colors.text || '#64748b', barColor: quizData.config.colors.primary } as PageElement;
        case ElementType.Chart: return {
          ...base,
          type: ElementType.Chart,
          items: [
            { type: 'donut', percent: 80, title: 'Nível 1', text: 'Baseado no seu Perfil', color: quizData.config.colors.primary },
            { type: 'bar', percent: 10, title: 'Nível 2', text: '<strong>7 DIAS</strong><br />Inicio do Tramento', color: quizData.config.colors.primary },
            { type: 'bar', percent: 80, title: 'Nível 3', text: '<strong>21 DIAS</strong><br />Fim do Tramento', color: quizData.config.colors.primary }
          ],
          layout: 'grid3',
          border: 'small',
          shadow: 'none',
          animation: 'none',
          backgroundColor: '#ffffff',
          textColor: quizData.config.colors.text || '#64748b'
        } as PageElement;
        case ElementType.Report: return {
          ...base,
          type: ElementType.Report,
          title: 'Sua Pontuação',
          resultLabel: '<b>Resultado:</b> Bom 😍',
          score: 85,
          imageUrl: '',
          border: 'small',
          shadow: 'none',
          backgroundColor: '#ffffff',

          titleColor: quizData.config.colors.text || '#0f172a',
          scoreColor: quizData.config.colors.primary,
          percentColor: '#0f172a'
        } as PageElement;
        case ElementType.Roulette: return {
          ...base,
          type: ElementType.Roulette,
          title: 'Gire a Roleta!',
          buttonText: 'Girar Agora',
          winningMessage: 'Parabéns! Você ganhou: {prize}',
          subMessage: 'Resgate seu prêmio agora!',
          items: [
            { id: generateId(), emoji: '🎁', label: 'Prêmio 1' },
            { id: generateId(), emoji: '✨', label: 'Prêmio 2' },
            { id: generateId(), emoji: '🎉', label: 'Prêmio 3' },
            { id: generateId(), emoji: '💎', label: 'Prêmio 4' },
            { id: generateId(), emoji: '🚀', label: 'Prêmio 5' },
            { id: generateId(), emoji: '🔥', label: 'Prêmio 6' }
          ],
          border: 'small',
          shadow: 'none'
        } as PageElement;
        case ElementType.FbComments: return {
          ...base,
          type: ElementType.FbComments,
          comments: [
            { id: generateId(), name: 'Maria Silva', timeAgo: '5 min ago', content: 'Adorei o conteúdo! Muito útil.', avatarUrl: '' },
            { id: generateId(), name: 'João Santos', timeAgo: '15 min ago', content: 'Excelente explicação, parabéns!', avatarUrl: '' }
          ],
          totalComments: 124,
          border: 'small',
          shadow: 'none'
        } as PageElement;

        case ElementType.SlotMachine: return {
          ...base,
          type: ElementType.SlotMachine,
          title: 'Tente a Sorte',
          winningMessage: 'PARABÉNS! VOCÊ GANHOU!',
          losingMessage: 'Que pena, tente novamente!',
          subMessage: 'Clique abaixo para resgatar!',
          maxAttempts: 3,
          alwaysWin: true,
          align: 'center',
          border: 'small',
          shadow: 'none'
        } as PageElement;

        default: throw new Error('Tipo de elemento desconhecido');
      }
    })();
    const current = selectedPage.elements || [];
    console.log('🔵 [handleQuickAdd] Elementos atuais na página:', current.length, 'Elementos de preço:', current.filter(e => e.type === ElementType.Price).length);
    if (elementType === ElementType.Options && (variant === 'multi' || (newElement as any).multiple === true)) {
      const btn: PageElement = { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, type: ElementType.Button, text: 'Continuar', widthPercent: 100, height: 'medium' } as any;
      console.log('🔵 [handleQuickAdd] Adicionando Options múltiplo + botão automático');
      updateElements([...current, newElement, btn]);
    } else {
      console.log('🔵 [handleQuickAdd] Adicionando elemento único:', newElement.type, newElement.id);
      updateElements([...current, newElement]);
    }
    console.log('🔵 [handleQuickAdd] Total de elementos após adicionar:', (elementType === ElementType.Options && (variant === 'multi' || (newElement as any).multiple === true)) ? current.length + 2 : current.length + 1);
    setSelectedElementId(newElement.id);
  }, [selectedPage, updateElements]);

  const handleAddModel = useCallback((modelName: string) => {
    console.log(`[App] handleAddModel called with: ${modelName}`);
    if (!selectedPage) {
      console.warn('[App] No selected page');
      return;
    }

    if (modelName === 'cover') {
      console.log('[App] Creating elements for cover model...');
      const titleId = `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const imageId = `id_${Date.now() + 1}_${Math.random().toString(36).substr(2, 9)}`;
      const buttonId = `id_${Date.now() + 2}_${Math.random().toString(36).substr(2, 9)}`;

      const title: PageElement = { id: titleId, type: ElementType.Title, content: 'Título da Capa' } as any;
      const image: PageElement = { id: imageId, type: ElementType.Image, url: '', alt: '' } as any;
      const button: PageElement = { id: buttonId, type: ElementType.Button, text: 'Começar Agora', height: 'large', animation: 'pulse', widthPercent: 100 } as any;

      const currentElements = selectedPage.elements || [];
      const newElements = [...currentElements, title, image, button];
      console.log(`[App] Adding ${newElements.length - currentElements.length} new elements to page.`);
      updateElements(newElements);
    } else if (modelName === 'question-image-2x2') {
      console.log('[App] Creating elements for question-image-2x2 model...');
      const optionsId = `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const optionsElement: PageElement = {
        id: optionsId,
        type: ElementType.Options,
        title: 'Qual a sua faixa etária?',
        description: '',
        options: [
          { id: generateId(), text: '18 - 29 anos', imageUrl: 'https://ik.imagekit.io/aya/quiz/mulher-18-29.webp?updatedAt=1764467098790', mediaType: 'image' },
          { id: generateId(), text: '30 - 49 anos', imageUrl: 'https://ik.imagekit.io/aya/quiz/mulher-30-49.webp?updatedAt=1764467099788', mediaType: 'image' },
          { id: generateId(), text: '50 - 59 anos', imageUrl: 'https://ik.imagekit.io/aya/quiz/mulher-50-59.webp?updatedAt=1764467098626', mediaType: 'image' },
          { id: generateId(), text: '+60 anos', imageUrl: 'https://ik.imagekit.io/aya/quiz/mulher-60-mais.webp?updatedAt=1764467098823', mediaType: 'image' }
        ],
        multiple: false,
        layout: 'grid2',
        spacing: 'medium',
        style: 'relief',
        imageOrientation: 'vertical',
        checkStyle: 'arrow',
        checkPosition: 'after',
        border: 'small',
        shadow: 'none'
      } as any;

      const currentElements = selectedPage.elements || [];
      const newElements = [...currentElements, optionsElement];
      console.log(`[App] Adding ${newElements.length - currentElements.length} new elements to page.`);
      updateElements(newElements);
    } else if (modelName === 'question-image-2x1') {
      console.log('[App] Creating elements for question-image-2x1 model...');
      const optionsId = `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const optionsElement: PageElement = {
        id: optionsId,
        type: ElementType.Options,
        title: 'Qual o seu sexo?',
        description: '',
        options: [
          { id: generateId(), text: 'Homem', imageUrl: 'https://ik.imagekit.io/aya/quiz/homem.webp', mediaType: 'image' },
          { id: generateId(), text: 'Mulher', imageUrl: 'https://ik.imagekit.io/aya/quiz/mulher.webp', mediaType: 'image' }
        ],
        multiple: false,
        layout: 'grid2',
        spacing: 'medium',
        style: 'simple',
        imageOrientation: 'vertical',
        checkStyle: 'none',
        textAlign: 'center',
        border: 'small',
        shadow: 'none'
      } as any;

      const currentElements = selectedPage.elements || [];
      const newElements = [...currentElements, optionsElement];
      console.log(`[App] Adding ${newElements.length - currentElements.length} new elements to page.`);
      updateElements(newElements);
    } else if (modelName === 'question-emoji-buttons') {
      console.log('[App] Creating elements for question-emoji-buttons model...');
      const optionsId = `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const optionsElement: PageElement = {
        id: optionsId,
        type: ElementType.Options,
        title: 'Como você está se sentindo hoje?',
        description: '',
        options: [
          { id: generateId(), text: 'Feliz', emoji: '😊', mediaType: 'emoji' },
          { id: generateId(), text: 'Animado', emoji: '🤩', mediaType: 'emoji' },
          { id: generateId(), text: 'Calmo', emoji: '😌', mediaType: 'emoji' },
          { id: generateId(), text: 'Cansado', emoji: '😴', mediaType: 'emoji' },
          { id: generateId(), text: 'Triste', emoji: '😢', mediaType: 'emoji' },
          { id: generateId(), text: 'Estressado', emoji: '😤', mediaType: 'emoji' }
        ],
        multiple: false,
        layout: 'wrap',
        spacing: 'medium',
        style: 'simple',
        imageOrientation: 'horizontal',
        checkStyle: 'none',
        textAlign: 'center',
        border: 'small',
        shadow: 'none'
      } as any;

      const currentElements = selectedPage.elements || [];
      const newElements = [...currentElements, optionsElement];
      console.log(`[App] Adding ${newElements.length - currentElements.length} new elements to page.`);
      updateElements(newElements);
    } else if (modelName === 'menu-image-check') {
      console.log('[App] Creating elements for menu-image-check model...');
      const optionsId = `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const optionsElement: PageElement = {
        id: optionsId,
        type: ElementType.Options,
        title: 'Onde você sente dor?',
        description: 'Selecione todas as áreas que se aplicam',
        options: [
          { id: generateId(), text: 'Quadris / Pelve', imageUrl: 'https://ik.imagekit.io/aya/quiz/quadris.webp', mediaType: 'image' },
          { id: generateId(), text: 'Lombar', imageUrl: 'https://ik.imagekit.io/aya/quiz/lombar.webp?updatedAt=1764621943546', mediaType: 'image' },
          { id: generateId(), text: 'Pescoço', imageUrl: 'https://ik.imagekit.io/aya/quiz/pescoco.webp?updatedAt=1764621943639', mediaType: 'image' },
          { id: generateId(), text: 'Ombros', imageUrl: 'https://ik.imagekit.io/aya/quiz/ombros.webp?updatedAt=1764621943346', mediaType: 'image' },
          { id: generateId(), text: 'Joelho', imageUrl: 'https://ik.imagekit.io/aya/quiz/joelhos.png?updatedAt=1764621861842', mediaType: 'image' },
          { id: generateId(), text: 'Corpo inteiro', imageUrl: 'https://ik.imagekit.io/aya/quiz/corpo-inteiro.webp?updatedAt=1764621861835', mediaType: 'image' },
          { id: generateId(), text: 'Nenhuma alternativa', imageUrl: 'https://ik.imagekit.io/aya/quiz/nenhuma-alternativa.webp?updatedAt=1764621862080', mediaType: 'image' }
        ],
        multiple: true,
        layout: 'list',
        spacing: 'medium',
        style: 'simple',
        imageOrientation: 'horizontal',
        checkStyle: 'square',
        checkPosition: 'after',
        textAlign: 'left',
        border: 'small',
        shadow: 'none'
      } as any;

      const buttonElement: PageElement = {
        id: `id_${Date.now() + 1}_${Math.random().toString(36).substr(2, 9)}`,
        type: ElementType.Button,
        content: 'Continuar',
        action: 'next',
        align: 'center',
        border: 'small',
        shadow: 'medium'
      } as any;

      const currentElements = selectedPage.elements || [];
      const newElements = [...currentElements, optionsElement, buttonElement];
      console.log(`[App] Adding ${newElements.length - currentElements.length} new elements to page.`);
      updateElements(newElements);
    } else if (modelName === 'basic-form') {
      console.log('[App] Creating elements for basic-form model...');

      const titleElement: PageElement = {
        id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: ElementType.Title,
        content: 'Preencha seus dados',
        align: 'center'
      } as any;

      const nameFormElement: PageElement = {
        id: `id_${Date.now() + 1}_${Math.random().toString(36).substr(2, 9)}`,
        type: ElementType.Form,
        title: 'Nome',
        fieldType: 'text',
        fieldId: 'nome',
        placeholder: 'Digite seu nome',
        mask: '',
        required: false,
        align: 'left',
        border: 'small',
        shadow: 'none'
      } as any;

      const emailFormElement: PageElement = {
        id: `id_${Date.now() + 2}_${Math.random().toString(36).substr(2, 9)}`,
        type: ElementType.Form,
        title: 'E-mail',
        fieldType: 'email',
        fieldId: 'email',
        placeholder: 'Digite seu e-mail',
        mask: '',
        required: false,
        align: 'left',
        border: 'small',
        shadow: 'none'
      } as any;

      const phoneFormElement: PageElement = {
        id: `id_${Date.now() + 3}_${Math.random().toString(36).substr(2, 9)}`,
        type: ElementType.Form,
        title: 'Telefone',
        fieldType: 'phone',
        fieldId: 'telefone',
        placeholder: '(99) 99999-9999',
        mask: '(99) 99999-9999',
        required: false,
        align: 'left',
        border: 'small',
        shadow: 'none'
      } as any;

      const buttonElement: PageElement = {
        id: `id_${Date.now() + 4}_${Math.random().toString(36).substr(2, 9)}`,
        type: ElementType.Button,
        content: 'Continuar',
        action: 'next',
        align: 'center',
        border: 'small',
        shadow: 'medium'
      } as any;

      const currentElements = selectedPage.elements || [];
      const newElements = [...currentElements, titleElement, nameFormElement, emailFormElement, phoneFormElement, buttonElement];
      console.log(`[App] Adding ${newElements.length - currentElements.length} new elements to page.`);
      updateElements(newElements);
    } else {
      console.warn(`[App] Unknown model name: ${modelName}`);
    }
  }, [selectedPage, updateElements]);

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
    const id = String(event.active.id);
    if (id.startsWith('palette-')) {
      const parts = id.split('-');
      const t = parseInt(parts[1], 10) as ElementType;
      const v = parts.length > 2 ? parts[2] : null;
      setDraggingFromPaletteType(t);
      setDraggingVariant(v);
    } else {
      setDraggingFromPaletteType(null);
      setDraggingVariant(null);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setActiveId(null);
    const overId = over?.id as string | undefined;
    if (!selectedPage || !overId) return;

    const elements = selectedPage.elements || [];
    if (String(active.id).startsWith('palette-')) {
      const type = draggingFromPaletteType;
      if (type == null) return;
      const insertIndex = overId.startsWith('slot-') ? parseInt(overId.split('-')[1], 10) : elements.length;
      const base: any = { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, type };
      const newElement: PageElement = (() => {
        switch (type) {
          case ElementType.Title: return { ...base, type: ElementType.Title, content: 'Novo Título', align: 'center' } as PageElement;
          case ElementType.Text: return { ...base, type: ElementType.Text, content: 'Novo texto...', align: 'center' } as PageElement;
          case ElementType.Alert: return { ...base, type: ElementType.Alert, content: 'Mensagem do alerta', variant: 'error' } as PageElement;
          case ElementType.Timer: return { ...base, type: ElementType.Timer, content: '⏱️ Faltam <strong>[time]</strong> minutos para o fim da promoção', durationSeconds: 60, variant: 'error', align: 'center' } as PageElement;
          case ElementType.Form:
            return {
              ...base,
              type: ElementType.Form,
              title: 'Texto',
              fieldType: 'text',
              placeholder: '',
              mask: '',
              required: false,
              align: 'left',
              border: 'small',
              shadow: 'none'
            } as PageElement;
          case ElementType.Options: {
            if (draggingVariant === 'single') {
              return {
                ...base,
                type: ElementType.Options,
                title: 'Qual a questão a ser respondida?',
                description: 'Digite aqui uma descrição de ajuda para introduzir o usuário à questão.',
                options: [
                  { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 1' },
                  { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 2' },
                  { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 3' }
                ],
                multiple: false,
                layout: 'list',
                spacing: 'medium',
                style: 'simple',
                checkStyle: 'traditional',
                checkPosition: 'before',
                border: 'small',
                shadow: 'none'
              } as PageElement;
            }
            if (draggingVariant === 'multi') {
              return {
                ...base,
                type: ElementType.Options,
                title: 'Qual a questão a ser respondida?',
                description: 'Digite aqui uma descrição de ajuda para introduzir o usuário à questão.',
                options: [
                  { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 1' },
                  { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 2' },
                  { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 3' },
                  { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 4' },
                  { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 5' },
                  { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 6' }
                ],
                multiple: true,
                layout: 'grid2',
                spacing: 'medium',
                style: 'simple',
                checkStyle: 'traditional',
                checkPosition: 'before',
                border: 'small',
                shadow: 'none'
              } as PageElement;
            }
            if (draggingVariant === 'binary') {
              return {
                ...base,
                type: ElementType.Options,
                title: 'Qual a questão a ser respondida?',
                description: 'Digite aqui uma descrição de ajuda para introduzir o usuário à questão.',
                options: [
                  { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: '✅ Sim' },
                  { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: '🚫 Não' }
                ],
                multiple: false,
                layout: 'grid2',
                spacing: 'medium',
                style: 'simple',
                checkStyle: 'none',
                textAlign: 'center',
                border: 'small',
                shadow: 'none'
              } as PageElement;
            }
            return {
              ...base,
              type: ElementType.Options,
              title: 'Qual a questão a ser respondida?',
              description: 'Digite aqui uma descrição de ajuda para introduzir o usuário à questão.',
              options: [
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 1' },
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 2' },
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 3' },
                { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)} `, text: 'Opção 4' }
              ],
              multiple: false,
              layout: 'grid3',
              spacing: 'medium',
              style: 'simple',
              checkStyle: 'traditional',
              checkPosition: 'before',
              border: 'small',
              shadow: 'none'
            } as PageElement;
          }
          case ElementType.Button: return { ...base, type: ElementType.Button, text: 'Continuar', widthPercent: 100 } as PageElement;
          case ElementType.Video: return { ...base, type: ElementType.Video, url: '', title: '' } as PageElement;
          case ElementType.Image: return { ...base, type: ElementType.Image, url: '', alt: '' } as PageElement;
          case ElementType.BeforeAfter: return { ...base, type: ElementType.BeforeAfter, beforeUrl: '', afterUrl: '', beforeLabel: 'Antes', afterLabel: 'Depois', startPercent: 50, orientation: 'vertical', align: 'center', widthPercent: 100, border: 'small', shadow: 'none' } as PageElement;
          case ElementType.Audio: return { ...base, type: ElementType.Audio, url: '', question: '' } as PageElement;
          case ElementType.Spacer: return { ...base, type: ElementType.Spacer, height: 20 } as PageElement;
          case ElementType.Loading: return { ...base, type: ElementType.Loading, title: 'Analisando...', subtitle: 'Estamos analisando suas respostas...', durationSeconds: 10, showPercent: true, barColor: quizData.config.colors.primary } as PageElement;
          case ElementType.Testimonial: return { ...base, type: ElementType.Testimonial, name: 'Nome da pessoa', info: '@usuario', stars: 5, text: '"Escreva aqui o depoimento..."', imageUrl: '', starColor: '#f59e0b', border: 'small', shadow: 'small' } as PageElement;
          case ElementType.Carousel: return { ...base, type: ElementType.Carousel, items: [{ id: generateId(), imageUrl: '', description: 'Exemplo de descrição' }, { id: generateId(), imageUrl: '', description: 'Exemplo de descrição' }], layout: 'image-text', pagination: true, autoplay: false, align: 'center', widthPercent: 100, ratio: 'auto' } as PageElement;
          case ElementType.Level: return { ...base, type: ElementType.Level, title: 'Seu Potencial de Sucesso', subtitle: '* Baseado nos dados de nossos clientes semelhantes a você', percent: 97, indicatorText: '', legendLeft: 'Normal', legendCenter: 'Médio', legendRight: 'Alto', showMeter: true, showProgress: true, styleType: 'dashes', colorTheme: 'theme', border: 'none', shadow: 'none', backgroundColor: quizData.config.colors.background, titleColor: quizData.config.colors.title || '#1e293b', textColor: quizData.config.colors.text || '#64748b', barColor: quizData.config.colors.primary } as PageElement;
          case ElementType.Chart: return {
            ...base,
            type: ElementType.Chart,
            items: [
              { type: 'donut', percent: 80, title: 'Nível 1', text: 'Baseado no seu Perfil', color: quizData.config.colors.primary },
              { type: 'bar', percent: 10, title: 'Nível 2', text: '<strong>7 DIAS</strong><br />Inicio do Tramento', color: quizData.config.colors.primary },
              { type: 'bar', percent: 80, title: 'Nível 3', text: '<strong>21 DIAS</strong><br />Fim do Tramento', color: quizData.config.colors.primary }
            ],
            layout: 'grid2',
            border: 'small',
            shadow: 'none',
            animation: 'none',
            backgroundColor: '#ffffff',
            textColor: quizData.config.colors.text || '#64748b'
          } as PageElement;
          case ElementType.Report: return {
            ...base,
            type: ElementType.Report,
            title: 'Sua Pontuação',
            resultLabel: '<b>Resultado:</b> Bom 😍',
            score: 85,
            imageUrl: '',
            border: 'small',
            shadow: 'none',
            backgroundColor: '#ffffff',

            titleColor: quizData.config.colors.text || '#0f172a',
            scoreColor: quizData.config.colors.primary,
            percentColor: '#0f172a'
          } as PageElement;
          case ElementType.Price: return {
            ...base,
            type: ElementType.Price,
            items: [
              { title: 'Plano Start', value: 'R$ 59,90', suffix: 'à vista', color: 'neutral', priceType: 'illustrative', enableCheckbox: false },
              { title: 'Plano Pro', prefix: '10% off', value: 'R$ 89,90', suffix: 'à vista', topHighlight: '⭐ Mais Escolhido', color: 'neutral', priceType: 'illustrative', enableCheckbox: false },
              { title: 'Plano Gold', prefix: '10% off', value: 'R$ 129,90', suffix: 'à vista', color: 'neutral', priceType: 'illustrative', enableCheckbox: false }
            ],
            layout: 'horizontal',
            verticalAlign: 'bottom',
            border: 'small',
            shadow: 'none'
          } as PageElement;
          case ElementType.Seal: return { ...base, type: ElementType.Seal, icon: 'medal', iconColor: quizData.config.colors.title || '#0f172a', iconSize: 'medium', backgroundColor: '#ffffff', titleColor: quizData.config.colors.title || '#0f172a', textColor: quizData.config.colors.text || '#64748b', title: 'Seu risco é zero!', description: 'Utilize este componente para deixar o seu cliente tranquilo em relação às garantias que você oferece.', align: 'center', border: 'small', shadow: 'none' } as PageElement;
          case ElementType.Roulette: return {
            ...base,
            type: ElementType.Roulette,
            title: 'Gire a Roleta!',
            buttonText: 'Girar Agora',
            winningMessage: 'Parabéns! Você ganhou: {prize}',
            subMessage: 'Resgate seu prêmio agora!',
            items: [
              { id: generateId(), emoji: '🎁', label: 'Prêmio 1' },
              { id: generateId(), emoji: '✨', label: 'Prêmio 2' },
              { id: generateId(), emoji: '🎉', label: 'Prêmio 3' },
              { id: generateId(), emoji: '💎', label: 'Prêmio 4' },
              { id: generateId(), emoji: '🚀', label: 'Prêmio 5' },
              { id: generateId(), emoji: '🔥', label: 'Prêmio 6' }
            ],
            border: 'small',
            shadow: 'none'
          } as PageElement;
          case ElementType.FbComments: return {
            ...base,
            type: ElementType.FbComments,
            comments: [
              { id: generateId(), name: 'Maria Silva', timeAgo: '5 min ago', content: 'Adorei o conteúdo! Muito útil.', avatarUrl: '' },
              { id: generateId(), name: 'João Santos', timeAgo: '15 min ago', content: 'Excelente explicação, parabéns!', avatarUrl: '' }
            ],
            totalComments: 124,
            border: 'small',
            shadow: 'none'
          } as PageElement;

          case ElementType.SlotMachine: return {
            ...base,
            type: ElementType.SlotMachine,
            title: 'Tente a Sorte',
            winningMessage: 'PARABÉNS! VOCÊ GANHOU!',
            losingMessage: 'Que pena, tente novamente!',
            subMessage: 'Clique abaixo para resgatar!',
            symbols: [
              { id: generateId(), emoji: '🍒' },
              { id: generateId(), emoji: '🍋' },
              { id: generateId(), emoji: '⭐' },
              { id: generateId(), emoji: '💎' },
              { id: generateId(), emoji: '7️⃣' },
              { id: generateId(), emoji: '🔔' },
            ],
            maxAttempts: 3,
            alwaysWin: true,
            align: 'center',
            border: 'small',
            shadow: 'none'
          } as PageElement;

          default: throw new Error('Tipo de elemento desconhecido');
        }
      })();
      const newElements = [...elements];
      newElements.splice(insertIndex, 0, newElement);
      if (type === ElementType.Options && ((newElement as any).multiple === true)) {
        const btn: PageElement = { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, type: ElementType.Button, text: 'Continuar', widthPercent: 100 } as any;
        newElements.splice(insertIndex + 1, 0, btn);
      }
      updateElements(newElements);
    } else {
      const activeIndex = elements.findIndex(el => el.id === active.id);
      if (overId.startsWith('slot-')) {
        const dropIndex = parseInt(overId.split('-')[1], 10);
        if (activeIndex !== -1 && dropIndex !== activeIndex) {
          const newElements = [...elements];
          const [draggedElement] = newElements.splice(activeIndex, 1);
          newElements.splice(dropIndex, 0, draggedElement);
          updateElements(newElements);
        }
      } else {
        const overIndex = elements.findIndex(el => el.id === overId);
        if (activeIndex !== -1 && overIndex !== -1) {
          updateElements(arrayMove(elements, activeIndex, overIndex));
        }
      }
    }
  };

  const generateCode = () => {
    const pagesString = quizData.pages.map(p => {
      return JSON.stringify(p, null, 2);
    }).join(',\n    ');

    return `import {QuizData} from './types';

                                                                          export const QUIZ_DATA: QuizData = {
                                                                            config: ${JSON.stringify(quizData.config, null, 2)},
                                                                          pages: [
                                                                          ${pagesString}
                                                                          ]
};
                                                                          `;
  };

  if (!authReady) {
    return <LoadingSpinner />;
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <img src={logo} alt="Quiz Creator Studio" className="h-18 mx-auto mb-4" />
          </div>

          {startupError && (
            <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded mb-3">{startupError}</div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin(loginEmail, loginPassword);
            }}
            className="space-y-4"
          >
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="seu@email.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                Senha
              </label>
              <input
                id="password"
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
                required
              />
            </div>

            {loginError && (
              <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded">
                Email e/ou senha incorretos
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-gray-800 text-white py-2 px-4 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
            >
              Entrar
            </button>
          </form>


        </div>
      </div>
    );
  }

  if (appView === 'dashboard') {
    return (
      <div className="bg-slate-100 h-screen font-sans flex flex-col">
        <header className="bg-white shadow-md p-4 flex justify-between items-center border-b border-slate-200">
          <img src={logo} alt="Quiz Creator Studio" className="h-18 w-auto cursor-pointer" onClick={() => setAppView('dashboard')} />
          <div className="relative" ref={userMenuRef}>
            <button className="flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200" onClick={() => setUserMenuOpen(u => !u)}>
              <span>👤</span>
              <span className="font-medium">{isLoggedIn ? (userName || 'Usuário') : 'Convidado'}</span>
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-300 rounded shadow z-50">
                <button className="w-full text-left px-3 py-2 hover:bg-slate-100" onClick={() => { setAppView('settings'); setUserMenuOpen(false); }}>Configurações</button>
                <div className="border-t border-slate-200"></div>
                <button className="w-full text-left px-3 py-2 hover:bg-slate-100" onClick={logout}>Logout</button>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-slate-100">
          <div className="p-6">
            {!currentProjectId ? (
              <>
                {/* Header da página */}
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">Meus Projetos</h1>
                    <p className="text-slate-600">Gerencie seus projetos e quizzes</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setAddingProject(true)} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors flex items-center gap-2 bg-green-600 text-white border-green-600 hover:bg-green-700`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                      Novo Projeto
                    </button>
                    <div className="relative spy-menu-container">
                      <button onClick={() => setSpyMenuOpen(u => !u)} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors flex items-center gap-2 bg-slate-500 text-white border-slate-500 hover:bg-slate-600`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        Espionar
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {spyMenuOpen && (
                        <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded shadow z-50">
                          <button
                            className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-slate-700 dark:text-slate-200"
                            onClick={() => {
                              window.open('https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=BR&is_targeted_country=false&media_type=all&q=inlead&search_type=keyword_unordered', '_blank');
                              setSpyMenuOpen(false);
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            Espionar Inlead
                          </button>
                          <div className="border-t border-slate-200 dark:border-slate-600"></div>
                          <button
                            className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-slate-700 dark:text-slate-200"
                            onClick={() => {
                              window.open('https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=BR&is_targeted_country=false&media_type=all&q=xquiz&search_type=keyword_unordered', '_blank');
                              setSpyMenuOpen(false);
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            Espionar Xquiz
                          </button>
                          <div className="border-t border-slate-200 dark:border-slate-600"></div>
                          <button
                            className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-slate-700 dark:text-slate-200"
                            onClick={() => {
                              window.open('https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=BR&is_targeted_country=false&media_type=all&q=xpages&search_type=keyword_unordered', '_blank');
                              setSpyMenuOpen(false);
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            Espionar Xpages
                          </button>
                          <div className="border-t border-slate-200 dark:border-slate-600"></div>
                          <button
                            className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-slate-700 dark:text-slate-200"
                            onClick={() => {
                              window.open('https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=BR&is_targeted_country=false&media_type=all&q=quizlead.digital&search_type=keyword_unordered', '_blank');
                              setSpyMenuOpen(false);
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            Espionar Quizlead
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {addingProject && (
                  <div className="mb-6 bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Nome do Projeto</label>
                    <input
                      type="text"
                      value={newProjectName}
                      onChange={(e) => { setNewProjectName(e.target.value); if (newProjectError) setNewProjectError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { const n = newProjectName.trim(); if (!n) { setNewProjectError('Informe um nome para o projeto'); setTimeout(() => newProjectInputRef.current?.focus(), 0); return; } handleCreateProject(n).then(() => { setNewProjectName(''); setAddingProject(false); setNewProjectError(''); }).catch((err) => { setNewProjectError(err instanceof Error ? err.message : 'Erro ao criar projeto'); }); } }}
                      ref={newProjectInputRef}
                      className="w-full px-3 py-2 bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Ex.: Curso Marketing 2025"
                    />
                    {newProjectError && <div className="text-xs text-red-600 mt-1">{newProjectError}</div>}
                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={() => { const n = newProjectName.trim(); if (!n) { setNewProjectError('Informe um nome para o projeto'); setTimeout(() => newProjectInputRef.current?.focus(), 0); return; } handleCreateProject(n).then(() => { setNewProjectName(''); setAddingProject(false); setNewProjectError(''); }).catch((err) => { setNewProjectError(err instanceof Error ? err.message : 'Erro ao criar projeto'); }); }}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
                      >
                        Criar
                      </button>
                      <button onClick={() => { setAddingProject(false); setNewProjectName(''); setNewProjectError(''); }} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors font-medium">Cancelar</button>
                    </div>
                  </div>
                )}

                {projectsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-sky-500 border-t-transparent mb-2"></div>
                      <div className="text-slate-600">Carregando projetos...</div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-4">
                    {[...projects].sort((a, b) => a.name.localeCompare(b.name)).map(p => (
                      <button key={p.id} onClick={() => openProject(p.id)} className="group flex flex-col items-center justify-center border border-slate-200 rounded-lg p-6 bg-white hover:bg-slate-50 hover:border-slate-300 shadow-sm transition-all">
                        <div className="w-16 h-16 mb-3 text-slate-600 group-hover:text-slate-800 transition-colors">
                          <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
                            <path d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z" />
                          </svg>
                        </div>
                        <span className="text-lg font-semibold text-slate-800 group-hover:text-slate-900">{p.name}</span>
                        <span className="text-xs text-slate-500 mt-1">{p.quizzes.length} {p.quizzes.length === 1 ? 'quiz' : 'quizzes'}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              (() => {
                const proj = getCurrentProject();
                if (!proj) return null;
                return (
                  <div>
                    {/* Header da página */}
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex-1">
                        {/* Título do Projeto na primeira linha */}
                        {editingProjectName ? (
                          <input
                            type="text"
                            value={tempProjectName}
                            onChange={(e) => setTempProjectName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                renameProject(proj.id, tempProjectName);
                                setEditingProjectName(false);
                              } else if (e.key === 'Escape') {
                                setEditingProjectName(false);
                                setTempProjectName('');
                              }
                            }}
                            onBlur={() => {
                              if (tempProjectName.trim()) {
                                renameProject(proj.id, tempProjectName);
                              }
                              setEditingProjectName(false);
                              setTempProjectName('');
                            }}
                            className="text-2xl md:text-3xl font-bold text-slate-800 bg-transparent border-b-2 border-slate-300 focus:border-slate-500 outline-none px-1 mb-2"
                            autoFocus
                          />
                        ) : (
                          <h1
                            className="text-2xl md:text-3xl font-bold text-slate-800 cursor-pointer hover:text-slate-600 transition-colors mb-2"
                            onClick={() => {
                              setEditingProjectName(true);
                              setTempProjectName(proj.name);
                            }}
                          >
                            {proj.name}
                          </h1>
                        )}
                        {/* Botão voltar + subslogan na segunda linha */}
                        <div className="flex items-center gap-3">
                          <button onClick={backToProjects} className="p-2 bg-slate-600 text-white rounded-md hover:bg-slate-700 flex items-center justify-center transition-colors" aria-label="Voltar" title="Voltar">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <p className="text-slate-600">Gerencie os quizzes deste projeto</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={() => { setAddingQuiz(true); setNewQuizName(''); setNewQuizSlug(''); setImportingQuiz(false); }} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors flex items-center gap-2 bg-green-600 text-white border-green-600 hover:bg-green-700`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                          Novo Quiz
                        </button>
                        <button onClick={() => { setGenerateQuizModal({ projectId: proj.id, projectName: proj.name }); }} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors flex items-center gap-2 bg-green-600 text-white border-green-600 hover:bg-green-700`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                          Gerar com I.A.
                        </button>
                        <button onClick={() => { setImportingQuiz(true); setImportSharedCode(''); setImportQuizSlug(''); setAddingQuiz(false); setTimeout(() => importSharedCodeInputRef.current?.focus(), 0); }} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors flex items-center gap-2 bg-white text-slate-700 hover:bg-slate-50 border-slate-300`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                          Importar Quiz
                        </button>
                        <button onClick={() => setProjectSettingsModal({ project: proj })} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors flex items-center gap-2 bg-white text-slate-700 hover:bg-slate-50 border-slate-300`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                          Configurações
                        </button>
                        <button onClick={() => {
                          setPendingDeleteProject({ id: proj.id, name: proj.name, quizCount: proj.quizzes.length });
                        }} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors flex items-center gap-2 bg-white text-slate-700 hover:bg-slate-50 border-slate-300`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          Remover Projeto
                        </button>
                      </div>
                    </div>

                    {addingQuiz && (
                      <div className="mb-6 bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                        <label className="block text-sm font-medium text-slate-700 mb-2">Nome do Quiz</label>
                        <input
                          type="text"
                          value={newQuizName}
                          onChange={(e) => { setNewQuizName(e.target.value); setNewQuizSlug(toSlug(e.target.value)); if (newQuizNameError) setNewQuizNameError(''); setNewQuizSlugError(''); }}
                          onKeyDown={async (e) => { if (e.key === 'Enter') { const name = newQuizName.trim(); const raw = newQuizSlug.trim(); if (!name) { setNewQuizNameError('Informe o nome do quiz'); setTimeout(() => newQuizInputRef.current?.focus(), 0); return; } if (!raw || !isValidSlug(raw)) { setNewQuizSlugError('Informe um slug válido'); setTimeout(() => newQuizSlugInputRef.current?.focus(), 0); return; } const slug = raw; if (isSlugTakenByUser(slug)) { setErrorModal({ title: 'Slug indisponível', message: `O slug \"${slug}\" já está em uso no seu usuário. Altere para um valor único.` }); setTimeout(() => newQuizSlugInputRef.current?.focus(), 0); return; } const ok = await createQuizInProject(proj.id, name, slug, QUIZ_DATA); if (ok) { setNewQuizName(''); setNewQuizSlug(''); setAddingQuiz(false); setNewQuizNameError(''); setNewQuizSlugError(''); } } }}
                          ref={newQuizInputRef}
                          className="w-full px-3 py-2 bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="Ex.: Quiz Inicial"
                        />
                        {newQuizNameError && <div className="text-xs text-red-600 mt-1">{newQuizNameError}</div>}
                        <label className="block text-sm font-medium text-slate-700 mb-2 mt-4">Slug Único</label>
                        <input
                          type="text"
                          value={newQuizSlug}
                          onChange={(e) => { const v = sanitizeSlugInput(e.target.value); setNewQuizSlug(v); if (!v) { setNewQuizSlugError('Informe um slug válido'); } else if (!isValidSlug(v)) { setNewQuizSlugError('Use letras, números e hífen válidos'); } else { setNewQuizSlugError(''); } }}
                          ref={newQuizSlugInputRef}
                          className="w-full px-3 py-2 bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          placeholder="ex.: quiz-inicial"
                        />
                        {newQuizSlugError && <div className="text-xs text-red-600 mt-1">{newQuizSlugError}</div>}
                        {
                          newQuizSlug && isSlugTakenByUser(toSlug(newQuizSlug)) && (
                            <div className="text-xs text-red-600 mt-1">Slug indisponível para este usuário. Sugestão: {suggestUniqueSlug(newQuizSlug)}</div>
                          )
                        }
                        <div className="mt-4 flex gap-2">
                          <button
                            onClick={async () => { const name = newQuizName.trim(); const raw = newQuizSlug.trim(); if (!name) { setNewQuizNameError('Informe o nome do quiz'); setTimeout(() => newQuizInputRef.current?.focus(), 0); return; } if (!raw || !isValidSlug(raw)) { setNewQuizSlugError('Informe um slug válido'); setTimeout(() => newQuizSlugInputRef.current?.focus(), 0); return; } const slug = raw; if (isSlugTakenByUser(slug)) { setErrorModal({ title: 'Slug indisponível', message: `O slug \"${slug}\" já está em uso no seu usuário. Altere para um valor único.` }); setTimeout(() => newQuizSlugInputRef.current?.focus(), 0); return; } const ok = await createQuizInProject(proj.id, name, slug, QUIZ_DATA); if (ok) { setNewQuizName(''); setNewQuizSlug(''); setAddingQuiz(false); setNewQuizNameError(''); setNewQuizSlugError(''); } }}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
                          >
                            Criar
                          </button>
                          <button onClick={() => { setAddingQuiz(false); setNewQuizName(''); setNewQuizSlug(''); setNewQuizNameError(''); setNewQuizSlugError(''); }} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors font-medium">Cancelar</button>
                        </div>
                      </div >
                    )}

                    {
                      importingQuiz && (
                        <div className="mb-6 bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                          <label className="block text-sm font-medium text-slate-700 mb-2">Código de Compartilhamento</label>
                          <input
                            type="text"
                            value={importSharedCode}
                            onChange={(e) => setImportSharedCode(e.target.value)}
                            ref={importSharedCodeInputRef}
                            onKeyDown={async (e) => { if (e.key === 'Enter') { const now = Date.now(); const recent = importAttempts.filter(t => now - t < 60000); if (recent.length >= 5) { setErrorModal({ title: 'Muitas tentativas', message: 'Limite de 5 importações por minuto. Aguarde e tente novamente.' }); return; } const raw = importQuizSlug.trim(); const code = importSharedCode.trim(); if (!code) { setErrorModal({ title: 'Código obrigatório', message: 'Informe o código de compartilhamento antes de importar.' }); return; } if (!raw || !isValidSlug(raw)) { setImportQuizSlugError('Informe um slug válido'); setTimeout(() => importQuizSlugInputRef.current?.focus(), 0); return; } const slug = raw; if (isSlugTakenByUser(slug)) { setErrorModal({ title: 'Slug indisponível', message: `O slug "${slug}" já está em uso no seu usuário. Altere para um valor único.` }); setTimeout(() => importQuizSlugInputRef.current?.focus(), 0); return; } setImportAttempts([...recent, now]); const ok = await importQuizToProject(proj.id, code, slug); if (ok) { setImportingQuiz(false); setImportSharedCode(''); setImportQuizSlug(''); setImportQuizSlugError(''); } else { setErrorModal({ title: 'Código inválido', message: 'O código de compartilhamento informado não existe ou é inválido.' }); } } }}
                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder="Cole aqui o código de compartilhamento"
                          />
                          <label className="block text-sm font-medium text-slate-700 mb-2 mt-4">Slug Único</label>
                          <input
                            type="text"
                            value={importQuizSlug}
                            onChange={(e) => { const v = sanitizeSlugInput(e.target.value); setImportQuizSlug(v); if (!v) { setImportQuizSlugError('Informe um slug válido'); } else if (!isValidSlug(v)) { setImportQuizSlugError('Use letras, números e hífen válidos'); } else { setImportQuizSlugError(''); } }}
                            onKeyDown={async (e) => { if (e.key === 'Enter') { const now = Date.now(); const recent = importAttempts.filter(t => now - t < 60000); if (recent.length >= 5) { setErrorModal({ title: 'Muitas tentativas', message: 'Limite de 5 importações por minuto. Aguarde e tente novamente.' }); return; } const raw = importQuizSlug.trim(); const code = importSharedCode.trim(); if (!code) { setErrorModal({ title: 'Código obrigatório', message: 'Informe o código de compartilhamento antes de importar.' }); return; } if (!raw || !isValidSlug(raw)) { setImportQuizSlugError('Informe um slug válido'); setTimeout(() => importQuizSlugInputRef.current?.focus(), 0); return; } const slug = raw; if (isSlugTakenByUser(slug)) { setErrorModal({ title: 'Slug indisponível', message: `O slug \"${slug}\" já está em uso no seu usuário. Altere para um valor único.` }); setTimeout(() => importQuizSlugInputRef.current?.focus(), 0); return; } setImportAttempts([...recent, now]); const ok = await importQuizToProject(proj.id, code, slug); if (ok) { setImportingQuiz(false); setImportSharedCode(''); setImportQuizSlug(''); setImportQuizSlugError(''); } else { setErrorModal({ title: 'Código inválido', message: 'O código de compartilhamento informado não existe ou é inválido.' }); } } }}
                            ref={importQuizSlugInputRef}
                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            placeholder="ex.: quiz-importado"
                          />
                          {importQuizSlugError && (
                            <div className="text-xs text-red-600 mt-1">{importQuizSlugError}</div>
                          )}
                          {importQuizSlug && !importQuizSlugError && isSlugTakenByUser(toSlug(importQuizSlug)) && (
                            <div className="text-xs text-red-600 mt-1">Slug indisponível para este usuário. Sugestão: {suggestUniqueSlug(importQuizSlug)}</div>
                          )}
                          <div className="mt-4 flex gap-2">
                            <button
                              onClick={async () => {
                                const now = Date.now();
                                const recent = importAttempts.filter(t => now - t < 60000);
                                if (recent.length >= 5) {
                                  setErrorModal({ title: 'Muitas tentativas', message: 'Limite de 5 importações por minuto. Aguarde e tente novamente.' });
                                  return;
                                }
                                const raw = importQuizSlug.trim();
                                const code = importSharedCode.trim();
                                if (!code) {
                                  setErrorModal({ title: 'Código obrigatório', message: 'Informe o código de compartilhamento antes de importar.' });
                                  return;
                                }
                                if (!raw || !isValidSlug(raw)) {
                                  setImportQuizSlugError('Informe um slug válido');
                                  setTimeout(() => importQuizSlugInputRef.current?.focus(), 0);
                                  return;
                                }
                                const slug = raw;
                                if (isSlugTakenByUser(slug)) {
                                  setErrorModal({ title: 'Slug indisponível', message: `O slug "${slug}" já está em uso no seu usuário. Altere para um valor único.` });
                                  setTimeout(() => importQuizSlugInputRef.current?.focus(), 0);
                                  return;
                                }
                                setImportAttempts([...recent, now]);
                                const ok = await importQuizToProject(proj.id, code, slug);
                                if (ok) {
                                  setImportingQuiz(false);
                                  setImportSharedCode('');
                                  setImportQuizSlug('');
                                  setImportQuizSlugError('');
                                } else {
                                  setErrorModal({ title: 'Código inválido', message: 'O código de compartilhamento informado não existe ou é inválido.' });
                                }
                              }}
                              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
                            >
                              Importar
                            </button>
                            <button onClick={() => { setImportingQuiz(false); setImportSharedCode(''); setImportQuizSlug(''); setImportQuizSlugError(''); }} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300 transition-colors font-medium">Cancelar</button>
                          </div>
                        </div>
                      )
                    }

                    <QuizList proj={proj} />
                  </div>
                );
              })()
            )}
          </div>
        </main>
        {pendingDeleteProject && (
          <ConfirmDeleteModal
            open={!!pendingDeleteProject}
            name={pendingDeleteProject.name}
            quizCount={pendingDeleteProject.quizCount}
            onCancel={() => setPendingDeleteProject(null)}
            onConfirm={async () => {
              await deleteProject(pendingDeleteProject.id);
              setPendingDeleteProject(null);
              setCurrentProjectId(null);
              setAppView('dashboard');
              await loadProjects();
            }}
          />
        )}
        {
          errorModal && (
            <ErrorModal
              open={!!errorModal}
              title={errorModal.title}
              message={errorModal.message}
              onClose={() => setErrorModal(null)}
            />
          )
        }
        {
          successModal && (
            <SuccessModal
              open={!!successModal}
              title={successModal.title}
              message={successModal.message}
              onClose={() => setSuccessModal(null)}
            />
          )
        }
        {
          pendingDeleteQuiz && (
            <ConfirmDeleteQuizModal
              open={!!pendingDeleteQuiz}
              name={pendingDeleteQuiz.name}
              onCancel={() => setPendingDeleteQuiz(null)}
              onConfirm={() => { deleteQuiz(pendingDeleteQuiz.projectId, pendingDeleteQuiz.quizId); setPendingDeleteQuiz(null); }}
            />
          )
        }

        {
          duplicateLanguageModal && (
            <DuplicateLanguageModal
              open={!!duplicateLanguageModal}
              quizName={duplicateLanguageModal.quizName}
              settings={settings}
              onCancel={() => setDuplicateLanguageModal(null)}
              onConfirm={(sourceLang, targetLang) => {
                console.log('🎯 [Modal] Botão confirmar clicado:', { sourceLang, targetLang, projectId: duplicateLanguageModal.projectId, quizId: duplicateLanguageModal.quizId });
                duplicateQuizLanguage(duplicateLanguageModal.projectId, duplicateLanguageModal.quizId, targetLang);
                setDuplicateLanguageModal(null);
              }}
            />
          )
        }

        {
          translationLoading && (
            <TranslationLoadingModal
              open={translationLoading}
            />
          )
        }

        {
          aiLoading && (
            <TranslationLoadingModal
              open={aiLoading}
              title="Gerando Quiz com I.A."
              subtitle="A I.A. está analisando os dados e compondo o quiz. Isso pode levar até 1 minuto."
            />
          )
        }


        {
          generateQuizModal && (
            <GenerateQuizModal
              open={!!generateQuizModal}
              projectId={generateQuizModal.projectId}
              projectName={generateQuizModal.projectName}
              settings={settings}
              existingSlugs={projects.flatMap(p => p.quizzes).map(q => q.slug).filter((s): s is string => !!s)}
              onCancel={() => setGenerateQuizModal(null)}
              onGenerate={async (data) => {
                console.log('Gerando quiz com dados:', data);
                setGenerateQuizModal(null);

                try {
                  if (data.type === 'ai') {
                    // Usar a mesma lógica da tela de IA existente
                    // Copiar os valores para os estados da tela de IA
                    setAiQuizName(data.quizName);
                    setAiQuizSlug(data.quizSlug);
                    setAiProduct(data.product);
                    setAiIdealCustomer(data.idealCustomer);
                    setAiOffer(data.offer);
                    setAiPromises(data.promises);
                    setAiHasVSL(data.hasVSL);
                    setAiVSLText(data.vslText);

                    // Navegar para a tela de IA
                    setAppView('ai');
                    setCurrentProjectId(generateQuizModal.projectId);
                  } else if (data.type === 'modeling') {
                    setAiLoading(true);

                    try {
                      // O input sempre passa pela IA para garantir que o JSON final respeite a estrutura (Schema) do sistema,
                      // mesmo que o usuário já tenha colado um JSON de outra ferramenta.

                      const apiKey = settings?.gemini_api_key;
                      if (!apiKey) throw new Error("Chave API Gemini não configurada");

                      const docResponse = await fetch('/docs/structure.md');
                      if (!docResponse.ok) throw new Error("Falha ao carregar documentação de estrutura");
                      const structureDoc = await docResponse.text();

                      const prompt = `ATUE COMO UM ESPECIALISTA EM MARKETING DIGITAL E CRIAÇÃO DE QUIZZES.
Sua tarefa é criar um quiz completo no formato JSON baseado na descrição ou modelagem fornecida pelo usuário.

REGRAS ESTRUTURAIS (DOCUMENTAÇÃO V2):
${structureDoc}

INSTRUÇÕES DO USUÁRIO / MODELAGEM:
${data.modelingResult}

SAÍDA ESPERADA:
Apenas o JSON válido do objeto QuizData completo. Não inclua Markdown, explicações ou blocos de código (\`\`\`json). Apenas o JSON puro.`;

                      const geminiModel = import.meta.env.VITE_GEMINI_MODEL || 'gemini-1.5-flash';
                      if (import.meta.env.DEV) {
                        console.log('📤 [AI Debug] Enviando prompt para a IA:', prompt);
                      }
                      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${geminiModel}:generateContent?key=${encodeURIComponent(apiKey)}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          contents: [{ parts: [{ text: prompt }] }],
                          generationConfig: {
                            maxOutputTokens: 20000,
                            responseMimeType: "application/json"
                          }
                        })
                      });

                      const result = await response.json();
                      if (result.error) {
                        if (import.meta.env.DEV) console.error('❌ [AI Debug] Erro na API do Gemini:', result.error);
                        throw new Error(result.error.message);
                      }

                      let rawText = result.candidates?.[0]?.content?.parts?.[0]?.text;
                      if (!rawText) throw new Error("Resposta vazia da IA");

                      if (import.meta.env.DEV) console.log('🤖 [AI Debug] Texto bruto recebido da IA:', rawText);

                      rawText = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
                      const firstBrace = rawText.indexOf('{');
                      const lastBrace = rawText.lastIndexOf('}');
                      if (firstBrace !== -1 && lastBrace !== -1) {
                        rawText = rawText.substring(firstBrace, lastBrace + 1);
                      }

                      let modelingJson;
                      try {
                        modelingJson = JSON.parse(rawText);
                        if (import.meta.env.DEV) console.log('✅ [AI Debug] JSON parseado com sucesso:', modelingJson);
                      } catch (e) {
                        if (import.meta.env.DEV) console.error('❌ [AI Debug] Falha ao parsear JSON. Texto recebido:', rawText);
                        throw new Error('A IA não retornou um JSON válido. Verifique o console para detalhes.');
                      }

                      if (import.meta.env.DEV) console.log('🚀 [AI Debug] Iniciando criação do quiz no projeto...');
                      const success = await createQuizInProject(
                        generateQuizModal.projectId,
                        data.quizName,
                        data.quizSlug || toSlug(data.quizName),
                        modelingJson
                      );

                      if (import.meta.env.DEV) console.log('🏁 [AI Debug] Resultado da criação:', success ? 'Sucesso' : 'Falha');

                      if (success) {
                        await loadProjects();
                        alert('Quiz criado com sucesso!');
                      } else {
                        // Se falhou, o createQuizInProject já mostrou o modal de erro, 
                        // mas vamos logar para garantir
                        console.error('❌ [AI Debug] createQuizInProject retornou false');
                      }

                    } catch (err: any) {
                      console.error('Erro na geração IA:', err);
                      alert('Erro ao gerar quiz: ' + (err.message || String(err)));
                    } finally {
                      setAiLoading(false);
                    }
                  }
                } catch (error) {
                  console.error('Erro ao preparar geração:', error);
                  alert('Erro ao preparar geração do quiz: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
                }
              }}
            />
          )
        }

        {
          projectSettingsModal && (
            <ProjectSettingsModal
              open={!!projectSettingsModal}
              project={projectSettingsModal.project}
              onClose={() => setProjectSettingsModal(null)}
              onUpdate={() => { loadProjects(); loadProjectDomains(); }}
            />
          )
        }

        {
          shareQuizModal && (
            <ShareQuizModal
              open={!!shareQuizModal}
              shareCode={shareQuizModal.shareCode}
              onClose={() => setShareQuizModal(null)}
            />
          )
        }

        {/* ChangeSlugModal na view Dashboard */}
        {!!changeSlugModal && (
          <ChangeSlugModal
            open={true}
            currentSlug={changeSlugModal.currentSlug}
            projects={projects}
            currentQuizId={changeSlugModal.quizId}
            onCancel={() => {
              console.log('Closing modal from dashboard');
              setChangeSlugModal(null);
            }}
            onConfirm={(newSlug) => {
              console.log('Saving slug from dashboard:', newSlug);
              updateQuiz(changeSlugModal.projectId, changeSlugModal.quizId, { slug: newSlug });
              setChangeSlugModal(null);
            }}
          />
        )}
      </div >
    );
  }
  if (appView === 'leads') {
    const currentQuiz = projects.find(p => p.id === currentProjectId)?.quizzes.find(q => q.id === currentQuizId);
    if (!currentQuiz) return null;
    return (
      <div className="bg-slate-100 h-screen font-sans flex flex-col">
        <header className="bg-white shadow-md p-4 flex justify-between items-center border-b border-slate-200">
          <img src={logo} alt="Quiz Creator Studio" className="h-18 w-auto cursor-pointer" onClick={() => setAppView('dashboard')} />
          <div className="relative">
            <button className="flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200" onClick={() => setUserMenuOpen(u => !u)}>
              <span>👤</span>
              <span className="font-medium">{isLoggedIn ? (userName || 'Usuário') : 'Convidado'}</span>
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-300 rounded shadow z-50">
                <button className="w-full text-left px-3 py-2 hover:bg-slate-100" onClick={() => { setAppView('settings'); setUserMenuOpen(false); }}>Configurações</button>
                <div className="border-t border-slate-200"></div>
                <button className="w-full text-left px-3 py-2 hover:bg-slate-100" onClick={logout}>Logout</button>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-slate-100">
          <div className="p-6">
            <LeadsDashboard
              quizData={currentQuiz.data}
              quizId={currentQuiz.id}
              quizName={currentQuiz.name}
              updatedAt={currentQuiz.updatedAt}
              onBack={() => setAppView('dashboard')}
            />
          </div>
        </main>
      </div>
    );
  }
  if (appView === 'stats') {
    console.log('📊 [Stats] Renderizando view de stats:', {
      currentQuizId,
      currentProjectId,
      projectsCount: projects.length,
      appView
    });

    // Usar currentQuizForStats que já foi calculado no useMemo acima
    // Ele já prioriza quizData local se disponível
    const currentQuiz = currentQuizForStats || projects.find(p => p.id === currentProjectId)?.quizzes.find(q => q.id === currentQuizId);

    // Debug: verificar se os dados estão disponíveis
    if (!currentQuizId) {
      console.warn('⚠️ [Stats] currentQuizId não está definido:', { currentQuizId, currentProjectId });
    }
    if (!currentProjectId) {
      console.warn('⚠️ [Stats] currentProjectId não está definido:', { currentProjectId, currentQuizId });
    }
    if (!currentQuiz && currentQuizId) {
      console.warn('⚠️ [Stats] Quiz não encontrado nos projects:', {
        currentQuizId,
        currentProjectId,
        projectsCount: projects.length,
        projectIds: projects.map(p => p.id),
        quizzesInProject: projects.find(p => p.id === currentProjectId)?.quizzes.map(q => q.id)
      });
    }

    if (!currentQuiz || !currentQuizId) {
      return (
        <div className="bg-slate-100 h-screen font-sans flex flex-col">
          <header className="bg-white shadow-md p-4 flex justify-between items-center border-b border-slate-200">
            <img src={logo} alt="Quiz Creator Studio" className="h-18 w-auto cursor-pointer" onClick={() => setAppView('dashboard')} />
            <div className="relative">
              <button className="flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200" onClick={() => setUserMenuOpen(u => !u)}>
                <span>👤</span>
                <span className="font-medium">{isLoggedIn ? (userName || 'Usuário') : 'Convidado'}</span>
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-300 rounded shadow z-50">
                  <button className="w-full text-left px-3 py-2 hover:bg-slate-100" onClick={logout}>Logout</button>
                </div>
              )}
            </div>
          </header>
          <main className="flex-1 overflow-y-auto bg-slate-100">
            <div className="p-6">
              <div className="bg-white rounded-lg border border-slate-200 p-6 text-center">
                <h2 className="text-2xl font-bold text-slate-800 mb-4">Quiz não encontrado</h2>
                <p className="text-slate-600 mb-4">
                  {!currentQuizId ? 'ID do quiz não foi definido.' : 'Não foi possível carregar as estatísticas deste quiz.'}
                </p>
                <p className="text-xs text-slate-500 mb-4">
                  Debug: currentQuizId={currentQuizId || 'null'}, currentProjectId={currentProjectId || 'null'}
                </p>
                <button onClick={() => setAppView('dashboard')} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  Voltar ao Dashboard
                </button>
              </div>
            </div>
          </main>
        </div>
      );
    }



    const formatNumber = (n: number) => new Intl.NumberFormat('pt-BR').format(n);
    const formatPercent = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';
    const formatTime = (seconds: number) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Usar dados reais ou valores padrão
    const funnelData = statistics?.funnelData || [];
    const firstStepViews = funnelData.length > 0 ? funnelData[0].views : 0;
    const totalViews = statistics?.totalViews || 0;
    const completionRate = statistics?.completionRate || 0;
    const completedSessions = statistics?.completedSessions || 0;
    const externalClickSessions = statistics?.externalClickSessions || 0;
    const externalClickRate = totalViews > 0 ? (externalClickSessions / totalViews) * 100 : 0;
    const avgTime = statistics?.avgTimeSeconds ? formatTime(statistics.avgTimeSeconds) : '0:00';

    console.log('📊 [Stats] Renderizando com dados:', {
      hasStatistics: !!statistics,
      funnelDataCount: funnelData.length,
      funnelData: funnelData.map(f => ({ step: f.step, label: f.label, views: f.views })),
      totalViews,
      completionRate,
      avgTime,
      currentQuizPages: currentQuiz?.data?.pages?.map((p, i) => ({ index: i, title: p.title })) || []
    });

    return (
      <div className="bg-slate-100 h-screen font-sans flex flex-col">
        <header className="bg-white shadow-md p-4 flex justify-between items-center border-b border-slate-200">
          <img src={logo} alt="Quiz Creator Studio" className="h-18 w-auto cursor-pointer" onClick={() => setAppView('dashboard')} />
          <div className="relative">
            <button className="flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200" onClick={() => setUserMenuOpen(u => !u)}>
              <span>👤</span>
              <span className="font-medium">{isLoggedIn ? (userName || 'Usuário') : 'Convidado'}</span>
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-300 rounded shadow z-50">
                <button className="w-full text-left px-3 py-2 hover:bg-slate-100" onClick={() => { setAppView('settings'); setUserMenuOpen(false); }}>Configurações</button>
                <div className="border-t border-slate-200"></div>
                <button className="w-full text-left px-3 py-2 hover:bg-slate-100" onClick={logout}>Logout</button>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-slate-100">
          <div className="p-6">

            {/* Header da página */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">Estatísticas do Quiz</h1>
                <div className="flex items-center gap-3">
                  <button onClick={() => setAppView('dashboard')} className="p-2 bg-slate-600 text-white rounded-md hover:bg-slate-700 flex items-center justify-center">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <p className="text-slate-800 text-xl font-bold">{currentQuiz.name}</p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Última Alteração: {new Date(currentQuiz.updatedAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setStatsPeriod('today')} className={`${statsPeriod === 'today' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-300'} px-4 py-2 rounded-full border text-sm font-medium transition-colors`}>
                  Hoje
                </button>
                <button onClick={() => setStatsPeriod('yesterday')} className={`${statsPeriod === 'yesterday' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-300'} px-4 py-2 rounded-full border text-sm font-medium transition-colors`}>
                  Ontem
                </button>
                <button onClick={() => setStatsPeriod('7d')} className={`${statsPeriod === '7d' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-300'} px-4 py-2 rounded-full border text-sm font-medium transition-colors`}>
                  7 dias
                </button>
                <button onClick={() => setStatsPeriod('30d')} className={`${statsPeriod === '30d' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-300'} px-4 py-2 rounded-full border text-sm font-medium transition-colors`}>
                  30 dias
                </button>
                <button onClick={() => setStatsPeriod('all')} className={`${statsPeriod === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-300'} px-4 py-2 rounded-full border text-sm font-medium transition-colors`}>
                  Todo o período
                </button>
                <button onClick={() => setStatsPeriod('custom')} className={`${statsPeriod === 'custom' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-300'} px-4 py-2 rounded-full border text-sm font-medium transition-colors`}>
                  Personalizado
                </button>
                <button onClick={() => setClearDataModal(true)} className="flex items-center gap-2 px-4 py-2 bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors font-medium shadow-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  Zerar Dados
                </button>
                <button onClick={refreshStats} className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium shadow-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  Atualizar
                </button>
              </div>
            </div>

            {statsPeriod === 'custom' && (
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-700">De:</label>
                  <DatePicker
                    value={statsCustomFrom || new Date(currentQuiz.updatedAt).toLocaleDateString('pt-BR')}
                    onChange={setStatsCustomFrom}
                    placeholder="dd/mm/yyyy"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-700">Até:</label>
                  <DatePicker
                    value={statsCustomTo || new Date().toLocaleDateString('pt-BR')}
                    onChange={setStatsCustomTo}
                    placeholder="dd/mm/yyyy"
                  />
                </div>
              </div>
            )}

            {/* Loading state */}
            {statsLoading && (
              <div className="bg-white rounded-lg border border-slate-200 p-12 text-center mb-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent mb-4"></div>
                <p className="text-slate-600">Carregando estatísticas...</p>
              </div>
            )}

            {/* Error state */}
            {statsError && !statsLoading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
                <p className="text-red-800 font-medium">Erro ao carregar estatísticas</p>
                <p className="text-red-600 text-sm mt-1">{statsError}</p>
              </div>
            )}

            {/* Boxes com Estatísticas chave */}
            {!statsLoading && !statsError && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-600">Total de Visualizações</h3>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-800">{formatNumber(totalViews)}</div>
                </div>

                <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-600">Tempo Médio</h3>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-800">{avgTime}</div>
                </div>

                <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-600">Taxa de Conclusão</h3>
                    </div>
                  </div>
                  <div>
                    <span className={`text-3xl font-bold ${completionRate >= 60 ? 'text-green-600' : completionRate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {formatPercent(completionRate)}
                    </span>
                    <span className="text-sm text-slate-500 ml-2">({formatNumber(completedSessions)})</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    Mínimo: <span className="font-medium">50%</span> / Muito bom: <span className="font-medium">60%</span>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-600">Clicou/Ação</h3>
                    </div>
                  </div>
                  <div>
                    <span className={`text-3xl font-bold ${externalClickRate >= 14 ? 'text-green-600' : externalClickRate >= 10 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {formatPercent(externalClickRate)}
                    </span>
                    <span className="text-sm text-slate-500 ml-2">({formatNumber(externalClickSessions)})</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    Mínimo: <span className="font-medium">10%</span> / Muito bom: <span className="font-medium">14%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Tabela de funil */}
            {!statsLoading && !statsError && (
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-200">
                  <h2 className="text-xl font-bold text-slate-800">Funil de Conversão por Etapa</h2>
                  <p className="text-slate-600 mt-1">Análise detalhada do fluxo de usuários através do quiz</p>
                </div>

                {funnelData.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <p>Nenhum dado disponível para o período selecionado.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-slate-700">Página</th>
                          <th className="text-center py-4 px-6 text-sm font-semibold text-slate-700">Views</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-slate-700">Retenção (Global)</th>
                          <th className="text-center py-4 px-6 text-sm font-semibold text-slate-700">Benchmark</th>
                          <th className="text-center py-4 px-6 text-sm font-semibold text-slate-700">Status</th>
                          <th className="text-center py-4 px-6 text-sm font-semibold text-slate-700">Retenção (iniciados)</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-slate-700">Perda vs etapa anterior</th>
                        </tr>
                      </thead>
                      <tbody>
                        {funnelData.map((step, idx) => {
                          const retention = firstStepViews > 0 ? (step.views / firstStepViews) * 100 : 0;

                          // Calcular retenção dos iniciados (base: views da 2ª página, idx=1)
                          const startedViews = funnelData.length > 1 ? funnelData[1].views : 0;
                          const retentionStarted = idx > 0 && startedViews > 0
                            ? (step.views / startedViews) * 100
                            : 0;

                          const loss = idx === 0 ? null : funnelData[idx - 1].views > 0
                            ? ((funnelData[idx - 1].views - step.views) / funnelData[idx - 1].views) * 100
                            : 0;
                          const retentionWidth = Math.max(0, Math.min(100, retention));

                          // Calcular retenção relativa (da página anterior para esta)
                          const relativeRetention = idx === 0 ? 100 : (funnelData[idx - 1].views > 0
                            ? (step.views / funnelData[idx - 1].views) * 100
                            : 0);

                          // Determinar se esta página tem benchmark
                          // 2ª página (idx === 1): Mínimo 50%, Muito bom 70%
                          // Penúltima e última página: Mínimo 50%, Muito bom 60%
                          const isSecondPage = idx === 1;
                          const isPenultimatePage = funnelData.length >= 2 && idx === funnelData.length - 2;
                          const isLastPage = funnelData.length >= 1 && idx === funnelData.length - 1;
                          const hasBenchmark = isSecondPage || isPenultimatePage || isLastPage;

                          // Thresholds diferentes para cada tipo de página
                          const minThreshold = 50;
                          const goodThreshold = isSecondPage ? 70 : 60; // 2ª página: 70%, penúltima/última: 60%

                          let statusColor = '';
                          let statusIcon = '';
                          let statusTitle = '';
                          let benchmarkText = '';

                          if (!hasBenchmark) {
                            // Não tem benchmark (deixar vazio)
                            statusColor = '';
                            statusIcon = '';
                            statusTitle = '';
                            benchmarkText = '';
                          } else if (retention < minThreshold) { // Usar retenção global
                            statusColor = 'bg-red-100 text-red-600';
                            statusIcon = '✕';
                            statusTitle = `Fora do benchmark (${formatPercent(retention)} < ${minThreshold}%)`;
                            benchmarkText = isSecondPage ? '50% / 70%' : '50% / 60%';
                          } else if (retention < goodThreshold) { // Usar retenção global
                            statusColor = 'bg-yellow-100 text-yellow-700';
                            statusIcon = '!';
                            statusTitle = `Aceitável (${formatPercent(retention)} entre ${minThreshold}% e ${goodThreshold}%)`;
                            benchmarkText = isSecondPage ? '50% / 70%' : '50% / 60%';
                          } else { // Usar retenção global
                            statusColor = 'bg-green-100 text-green-600';
                            statusIcon = '✓';
                            statusTitle = `Muito bom (${formatPercent(retention)} ≥ ${goodThreshold}%)`;
                            benchmarkText = isSecondPage ? '50% / 70%' : '50% / 60%';
                          }

                          return (
                            <tr key={step.step} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                              <td className="py-5 px-6">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      // Abrir editor na página específica com lógica completa
                                      if (currentProjectId && currentQuizId) {
                                        const proj = projects.find(p => p.id === currentProjectId);
                                        const quiz = proj?.quizzes.find(q => q.id === currentQuizId);
                                        if (quiz) {
                                          const validatedData = ensurePagesHaveUniqueIds(quiz.data);
                                          setQuizData({ ...validatedData, name: quiz.name });
                                          setSelectedPageIndex(idx); // Define a página correta
                                          setPublished(quiz.status === 'published');
                                          setAppView('editor');
                                        }
                                      }
                                    }}
                                    className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                                    title="Editar esta página"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                  <span className="font-normal text-slate-800">{step.label}</span>
                                </div>
                              </td>
                              <td className="py-5 px-6 text-center">
                                <span className="font-semibold text-slate-800">{formatNumber(step.views)}</span>
                              </td>
                              <td className="py-5 px-6">
                                <div className="flex items-center gap-4 max-w-md">
                                  <div className="flex-1">
                                    <div className="h-3 bg-slate-200 rounded-full relative overflow-hidden">
                                      <div
                                        className="h-3 rounded-full transition-all duration-300"
                                        style={{
                                          width: `${retentionWidth}%`,
                                          backgroundColor: '#10b981' // verde
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <span className="font-bold text-slate-900 text-base whitespace-nowrap">
                                    {formatPercent(retention)}
                                  </span>
                                </div>
                              </td>
                              <td className="py-5 px-6 text-center">
                                {hasBenchmark && (
                                  <div className="text-xs text-slate-600">
                                    <div className="whitespace-nowrap">Mín: <span className="font-semibold text-amber-600">{minThreshold}%</span></div>
                                    <div className="whitespace-nowrap">Bom: <span className="font-semibold text-green-600">{goodThreshold}%~{isSecondPage ? 80 : 70}%</span></div>
                                  </div>
                                )}
                              </td>
                              <td className="py-5 px-6 text-center">
                                {hasBenchmark && statusIcon && (
                                  <div
                                    className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-lg font-bold ${statusColor} cursor-help transition-transform hover:scale-110`}
                                    title={statusTitle}
                                  >
                                    {statusIcon}
                                  </div>
                                )}
                              </td>
                              <td className="py-5 px-6 text-center">
                                {idx <= 1 ? (
                                  <span className="text-slate-400 text-sm">—</span>
                                ) : (
                                  <span className="font-semibold text-slate-700">{formatPercent(retentionStarted)}</span>
                                )}
                              </td>
                              <td className="py-5 px-6">
                                {loss === null ? (
                                  <span className="text-slate-400">—</span>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className={`text-sm ${Math.abs(loss) > 5 ? 'text-red-700 font-bold' : 'text-slate-600'}`}>↓</span>
                                    <span className={`text-sm ${Math.abs(loss) > 5 ? 'text-red-700 font-bold' : 'text-slate-600'}`}>
                                      −{formatPercent(Math.abs(loss))}
                                    </span>
                                    {Math.abs(loss) > 5 && (
                                      <span title="Perda alta (>5%)" className="flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-700 text-xs font-bold cursor-help">
                                        !
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {clearDataModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                  {clearingSuccess ? (
                    <div className="flex flex-col items-center justify-center py-6 text-center animate-fadeIn">
                      <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      <h3 className="text-xl font-bold text-slate-800 mb-2">Dados Zerados!</h3>
                      <p className="text-slate-600">Todas as estatísticas foram apagadas.</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-4 text-red-600">
                        <div className="p-2 bg-red-100 rounded-full">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 5a7 7 0 100 14 7 7 0 000-14z" /></svg>
                        </div>
                        <h3 className="text-xl font-bold">Zerar todos os dados?</h3>
                      </div>
                      <p className="text-slate-600 mb-6">
                        Tem certeza que deseja apagar <strong>todas as estatísticas e respostas</strong> deste quiz? Esta ação não pode ser desfeita.
                      </p>
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => setClearDataModal(false)}
                          disabled={isClearingData}
                          className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-md font-medium disabled:opacity-50"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleClearData}
                          disabled={isClearingData}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium flex items-center gap-2 disabled:opacity-50"
                        >
                          {isClearingData ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Zerando...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              Sim, zerar tudo
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }
  if (appView === 'preview') {
    const currentQuiz = projects.find(p => p.id === currentProjectId)?.quizzes.find(q => q.id === currentQuizId);
    if (!currentQuiz) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Quiz não encontrado</h2>
            <p className="text-slate-600 mb-4">Não foi possível carregar o quiz.</p>
            <button onClick={() => setAppView('dashboard')} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Voltar ao Dashboard
            </button>
          </div>
        </div>
      );
    }

    // Estado para controlar a navegação do quiz
    const [currentStep, setCurrentStep] = React.useState(0);
    const [answers, setAnswers] = React.useState<Record<string, any>>({});
    const [quizCompleted, setQuizCompleted] = React.useState(false);

    const currentPage = currentQuiz.data.pages[currentStep];
    const totalSteps = currentQuiz.data.pages.length;

    if (!currentPage) {
      if (currentStep !== 0) {
        setTimeout(() => setCurrentStep(0), 100);
        return <div className="min-h-screen flex items-center justify-center">Redirecionando para o início...</div>;
      }
      return <div className="min-h-screen flex items-center justify-center">Erro: Página inválida ou quiz vazio.</div>;
    }

    const handleNext = () => {
      if (currentStep < totalSteps - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        setQuizCompleted(true);
      }
    };

    const handlePrev = () => {
      if (currentStep > 0) {
        setCurrentStep(currentStep - 1);
      }
    };

    const handleAnswer = (answer: any) => {
      setAnswers(prev => ({ ...prev, [currentStep]: answer }));
    };

    if (quizCompleted) {
      return (
        <div className="min-h-screen" style={{ backgroundColor: currentQuiz.data.config.colors.background || '#f8fafc' }}>
          <div className="max-w-2xl mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Quiz Concluído!</h1>
                <p className="text-slate-600">Obrigado por participar.</p>
              </div>
              <button
                onClick={() => { setAppView('dashboard'); }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
              >
                Voltar ao Início
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen" style={{ backgroundColor: currentQuiz.data.config.colors.background || '#f8fafc' }}>
        {/* Header sutil */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/50">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setAppView('dashboard')}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Sair
                </button>
                <div className="text-sm text-slate-500">
                  {currentStep + 1} de {totalSteps}
                </div>
              </div>
              <h1 className="text-lg font-semibold text-slate-800">{currentQuiz.name}</h1>
              <div></div>
            </div>
          </div>
        </div>

        {/* Conteúdo do quiz */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <QuizPreview
            page={currentPage}
            config={currentQuiz.data.config}
            currentStep={currentStep}
            totalSteps={totalSteps}
            onNextPage={handleNext}
            onPrevPage={handlePrev}
          />
        </div>
      </div>
    );
  }

  const handleSaveGeminiKey = async () => {
    const k = geminiKey.trim();
    if (!k) {
      setErrorModal({ title: 'Campo obrigatório', message: 'Informe a chave ou clique em Limpar' });
      return;
    }
    try {
      setValidatingGemini(true);
      const result = await validateGeminiKey(k);
      if (!result.ok) {
        setErrorModal({ title: 'Chave inválida', message: result.message || 'Chave da API Gemini inválida' });
        return;
      }
      await saveSettings({ gemini_api_key: k });
      await loadSettings();
      if (result.message) {
        setSuccessModal({ title: 'Sucesso', message: result.message });
      } else {
        setSuccessModal({ title: 'Sucesso', message: 'Chave da API Gemini salva com sucesso!' });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErrorModal({ title: 'Erro ao salvar', message: 'Erro ao salvar chave: ' + msg });
    } finally {
      setValidatingGemini(false);
    }
  };

  if (appView === 'settings') {
    return (
      <div className="bg-slate-100 h-screen font-sans flex flex-col">
        <header className="bg-white shadow-md p-4 flex justify-between items-center border-b border-slate-200">
          <img src={logo} alt="Quiz Creator Studio" className="h-18 w-auto cursor-pointer" onClick={() => setAppView('dashboard')} />
          <div className="relative">
            <button className="flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200" onClick={() => setUserMenuOpen(u => !u)}>
              <span>👤</span>
              <span className="font-medium">{isLoggedIn ? (userName || 'Usuário') : 'Convidado'}</span>
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-300 rounded shadow z-50">
                <button className="w-full text-left px-3 py-2 hover:bg-slate-100" onClick={() => { setAppView('settings'); setUserMenuOpen(false); }}>Configurações</button>
                <div className="border-t border-slate-200"></div>
                <button className="w-full text-left px-3 py-2 hover:bg-slate-100" onClick={logout}>Logout</button>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-slate-100">
          <div className="p-6">
            {/* Header da página */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">Configurações</h1>
                <p className="text-slate-600 flex items-center gap-3">
                  <button onClick={() => setAppView('dashboard')} className="p-2 bg-slate-600 text-white rounded-md hover:bg-slate-700 flex items-center justify-center">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  Gerencie suas configurações e preferências
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Chave API Gemini</h2>
              <p className="text-sm text-slate-600 mb-4">Configure sua chave de API do Google Gemini para usar a funcionalidade de geração com I.A.</p>
              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Chave API Gemini</label>
                  <input
                    type="password"
                    value={geminiKey}
                    onChange={e => setGeminiKey(e.target.value)}
                    placeholder="Cole sua chave API aqui"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:text-sky-700 underline mt-1 inline-block">Obter chave da API Gemini</a>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleSaveGeminiKey}
                    className={`px-4 py-2 rounded-md ${(settingsLoading || validatingGemini) ? 'opacity-50 cursor-not-allowed' : ''} bg-green-600 text-white hover:bg-green-700 transition-colors font-medium shadow-sm`}
                    disabled={settingsLoading || validatingGemini}
                  >
                    {validatingGemini ? 'Validando…' : 'Salvar'}
                  </button>
                  <button
                    onClick={async () => { setGeminiKey(''); await clearGeminiKey(); await loadSettings(); }}
                    className={`px-4 py-2 rounded-md ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''} bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors font-medium`}
                    disabled={settingsLoading}
                  >
                    Limpar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
        {errorModal && (
          <ErrorModal
            open={!!errorModal}
            title={errorModal.title}
            message={errorModal.message}
            onClose={() => setErrorModal(null)}
          />
        )}
        {successModal && (
          <SuccessModal
            open={!!successModal}
            title={successModal.title}
            message={successModal.message}
            onClose={() => setSuccessModal(null)}
          />
        )}
      </div>
    );
  }
  return (
    <div className="bg-slate-200 h-screen font-sans flex flex-col" style={{ fontFamily: quizData.config.fontFamily || 'Inter, system-ui, -apple-system, sans-serif' }}>
      <header ref={headerRef} className="bg-white shadow-md p-4 flex justify-between items-center border-b border-slate-200">
        <img src={logo} alt="Quiz Creator Studio" className="h-18 w-auto cursor-pointer" onClick={() => setAppView('dashboard')} />
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowDesktopPreview(true)}
            className="px-4 py-2 bg-slate-200 text-slate-700 font-medium rounded-md hover:bg-slate-300 transition-all"
            aria-label="Desktop"
            title="Desktop"
          >
            🖥️
          </button>
          <button
            onClick={() => setShowMobilePreview(true)}
            className="px-4 py-2 bg-slate-200 text-slate-700 font-medium rounded-md hover:bg-slate-300 transition-all"
            aria-label="Celular"
            title="Celular"
          >
            📱
          </button>
          {published && (
            <button
              onClick={() => {
                const currentQuiz = projects.find(p => p.id === currentProjectId)?.quizzes.find(q => q.id === currentQuizId);
                if (currentQuiz?.slug && currentProjectId) {
                  // Verificar se o projeto tem domínio personalizado verificado
                  const customDomain = projectDomains[currentProjectId];
                  if (customDomain) {
                    // Usar domínio personalizado: https://custom-domain.com/slug?preview=true
                    window.open(`https://${customDomain}/${currentQuiz.slug}?preview=true`, '_blank');
                  } else {
                    // Usar padrão: /{subdomain}/{slug}?preview=true
                    const userSubdomain = (userName || 'user').toLowerCase().replace(/[^a-z0-9]/g, '');
                    window.open(`/${userSubdomain}/${currentQuiz.slug}?preview=true`, '_blank');
                  }
                }
              }}
              className="px-4 py-2 bg-slate-200 text-slate-700 font-medium rounded-md hover:bg-slate-300 transition-all mr-2"
              aria-label="Visualizar Quiz Publicado"
              title="Visualizar Quiz Publicado"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
          )}
          <button
            onClick={saveQuizNow}
            disabled={isSaving}
            className={`px-5 py-2 font-bold rounded-lg transition-all shadow-sm ${isSaving
              ? 'bg-green-700 text-white cursor-wait'
              : 'bg-green-600 text-white hover:bg-green-700'
              }`}
          >
            {isSaving ? 'Salvando...' : 'Salvar'}
          </button>
          <div className="flex items-center gap-2 pl-2">
            <span className="text-sm text-slate-700">Publicar</span>
            <button
              onClick={() => { if (currentProjectId && currentQuizId) { togglePublishQuiz(currentProjectId, currentQuizId); } }}
              aria-pressed={published}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${published ? 'bg-green-500' : 'bg-slate-300'} ${!currentProjectId || !currentQuizId ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Alternar publicação"
              disabled={!currentProjectId || !currentQuizId}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${published ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
      </header>
      <div className="flex" style={{ height: availableHeight }}>
        <aside className="w-1/5 max-w-sm bg-slate-50 border-r border-slate-300 flex flex-col overflow-hidden">
          <div className="p-4 border-b flex-shrink-0 overflow-y-auto">
            {quizData.name && (
              <div className="mb-4 pb-2 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <svg className="w-4 h-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {quizData.name}
                </h2>
              </div>
            )}
            <PersonalizationEditor config={quizData.config} onUpdate={handleUpdateConfig} />
            <GeneralSettingsEditor config={quizData.config} onUpdate={handleUpdateConfig} />
            <GamificationEditor config={quizData.config} onUpdate={handleUpdateConfig} />
            <ScriptsEditor config={quizData.config} onUpdate={handleUpdateConfig} />
          </div>
          <div className="flex-grow overflow-y-auto min-h-0">
            <PageList pages={quizData.pages} selectedIndex={selectedPageIndex} onSelect={(index) => { setSelectedPageIndex(index); setSelectedElementId(null); }} onAdd={handleAddPage} onDelete={handleDeletePage} onDuplicate={handleDuplicatePage} onReorder={handleReorderPages} />
          </div>
        </aside>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <aside className="w-1/5 max-w-sm bg-white border-r border-slate-300 overflow-y-auto">
            <div className="p-6">
              <React.Suspense fallback={<LoadingSpinner />}>
                <ElementPalette onDragStart={() => { }} onClickAdd={handleQuickAdd} onAddModel={handleAddModel} />
              </React.Suspense>
            </div>
          </aside>
          <main className="w-2/5 flex flex-col p-6 border-r border-slate-300 bg-slate-100 h-full min-h-0 overflow-hidden">
            {selectedPage ? (
              <div className="h-full min-h-0 flex flex-col space-y-6">
                <div>
                  <label htmlFor="page-title" className="block text-sm font-medium text-slate-700">Nome da Página</label>
                  <input
                    id="page-title"
                    type="text"
                    value={selectedPage.title}
                    onChange={e => handleUpdatePage(selectedPageIndex, { ...selectedPage, title: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white border border-slate-300 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    placeholder="Digite o nome da página..."
                  />
                </div>
                <div className="flex-1 min-h-0">
                  <div className="h-full w-full min-h-0 flex flex-col" style={{ backgroundColor: quizData.config.containerStyle === 'boxed' ? '#f1f5f9' : quizData.config.colors.background }}>
                    <QuizHeader
                      currentStep={selectedPageIndex}
                      totalSteps={quizData.pages.length}
                      config={quizData.config}
                      onBack={selectedPageIndex > 0 && selectedPage.hideBackButton !== true ? () => setSelectedPageIndex(selectedPageIndex - 1) : undefined}
                      isCanvas={true}
                    />
                    <div ref={canvasRef} className={`flex-1 flex flex-col px-4 pb-4 ${canvasAlignClass} overflow-y-auto min-h-0`}>
                      <div className={`w-full ${canvasInnerMinH} ${canvasInnerLayout} ${getContentWidthClass(quizData.config.contentWidth)} mx-auto ${getSpacingClasses(quizData.config.spacing)}`}>
                        <div
                          className={`w-full ${quizData.config.containerStyle === 'boxed' ? 'bg-white rounded-lg shadow-lg p-8' : ''}`}
                          style={quizData.config.containerStyle === 'boxed' ? {
                            backgroundColor: quizData.config.colors.background,
                            borderRadius: quizData.config.borderRadius === 'heavy' ? '1.5rem' : quizData.config.borderRadius === 'medium' ? '1rem' : quizData.config.borderRadius === 'light' ? '0.5rem' : '0'
                          } : {}}
                        >
                          <React.Suspense fallback={<LoadingSpinner />}>
                            <PageElementsEditor
                              elements={selectedPage.elements || []}
                              onUpdate={updateElements}
                              previewMode={false}
                              onElementDrop={(elementType, index) => {
                                const newElement = (() => {
                                  const base: any = { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, type: elementType };
                                  switch (elementType) {
                                    case ElementType.Title: return { ...base, type: ElementType.Title, content: 'Novo Título', align: 'center' } as PageElement;
                                    case ElementType.Text: return { ...base, type: ElementType.Text, content: 'Novo texto...', align: 'center' } as PageElement;
                                    case ElementType.Alert: return { ...base, type: ElementType.Alert, content: 'Mensagem do alerta', variant: 'error' } as PageElement;
                                    case ElementType.Timer: return { ...base, type: ElementType.Timer, content: '⏱️ Faltam <strong>[time]</strong> minutos para o fim da promoção', durationSeconds: 60, variant: 'error', align: 'center' } as PageElement;
                                    case ElementType.List: return {
                                      ...base,
                                      type: ElementType.List,
                                      items: [
                                        { title: 'Benefício 1', description: 'Descrição do benefício com <strong>HTML</strong> e emojis 🙂', bgColor: '#ffffff', textColor: quizData.config.colors.text || '#374151' },
                                        { title: 'Benefício 2', description: 'Texto de apoio com detalhes e exemplos.', bgColor: '#ffffff', textColor: quizData.config.colors.text || '#374151' },
                                        { title: 'Benefício 3', description: 'Use <em>ênfase</em>, quebras de linha e símbolos.', bgColor: '#ffffff', textColor: quizData.config.colors.text || '#374151' }
                                      ],
                                      titlePosition: 'top',
                                      layout: 'list',
                                      border: 'small',
                                      shadow: 'none',
                                      spacing: 'medium',
                                      textAlign: 'left'
                                    } as PageElement;
                                    case ElementType.Options: return {
                                      ...base,
                                      type: ElementType.Options,
                                      title: 'Qual a questão a ser respondida?',
                                      description: 'Digite aqui uma descrição de ajuda para introduzir o usuário à questão.',
                                      options: [
                                        { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, text: 'Opção 1', mediaType: 'none' },
                                        { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, text: 'Opção 2', mediaType: 'none' },
                                        { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, text: 'Opção 3', mediaType: 'none' },
                                        { id: `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, text: 'Opção 4', mediaType: 'none' }
                                      ],
                                      multiple: false,
                                      layout: 'grid3',
                                      imageOrientation: 'horizontal',
                                      verticalImageRatio: '1:1',
                                      spacing: 'medium',
                                      style: 'simple',
                                      checkStyle: 'traditional',
                                      checkPosition: 'before',
                                      border: 'small',
                                      shadow: 'none'
                                    } as PageElement;
                                    case ElementType.Level: return { ...base, type: ElementType.Level, title: 'Seu Potencial de Sucesso', subtitle: '* Baseado nos dados de nossos clientes semelhantes a você', percent: 97, indicatorText: '', legendLeft: 'Normal', legendCenter: 'Médio', legendRight: 'Alto', showMeter: true, showProgress: true, styleType: 'dashes', colorTheme: 'theme', border: 'none', shadow: 'none', backgroundColor: quizData.config.colors.background, titleColor: quizData.config.colors.title || '#1e293b', textColor: quizData.config.colors.text || '#64748b', barColor: quizData.config.colors.primary } as PageElement;
                                    case ElementType.QA: return {
                                      ...base,
                                      type: ElementType.QA,
                                      items: [
                                        { question: 'Qual a primeira dúvida a ser resolvida?', answer: 'Este é apenas um texto de exemplo utilizado para ilustrar como a resposta de uma dúvida frequente será exibida nesta seção.' },
                                        { question: 'Descreva outra dúvida a ser resolvida.', answer: 'Texto genérico de demonstração. Serve apenas como modelo visual para representar uma resposta real a ser inserida posteriormente.' }
                                      ],
                                      openFirst: true,
                                      allowMultiple: false,
                                      border: 'small',
                                      shadow: 'none'
                                    } as PageElement;
                                    case ElementType.Seal: return { ...base, type: ElementType.Seal, icon: 'medal', iconColor: quizData.config.colors.title || '#0f172a', iconSize: 'medium', backgroundColor: '#ffffff', titleColor: quizData.config.colors.title || '#0f172a', textColor: quizData.config.colors.text || '#64748b', title: 'Seu risco é zero!', description: 'Utilize este componente para deixar o seu cliente tranquilo em relação às garantias que você oferece.', align: 'center', border: 'small', shadow: 'none' } as PageElement;
                                    case ElementType.Button: return { ...base, type: ElementType.Button, text: 'Continuar', widthPercent: 100, height: 'medium' } as PageElement;
                                    case ElementType.Video: return { ...base, type: ElementType.Video, url: '', title: '' } as PageElement;
                                    case ElementType.Image: return { ...base, type: ElementType.Image, url: '', alt: '' } as PageElement;
                                    case ElementType.Audio: return { ...base, type: ElementType.Audio, url: '', question: '' } as PageElement;
                                    case ElementType.Spacer: return { ...base, type: ElementType.Spacer, height: 20 } as PageElement;
                                    case ElementType.Loading: return { ...base, type: ElementType.Loading, title: 'Analisando...', subtitle: 'Estamos analisando suas respostas...', durationSeconds: 10, showPercent: true, barColor: quizData.config.colors.primary } as PageElement;
                                    case ElementType.Testimonial: return { ...base, type: ElementType.Testimonial, name: 'Nome da pessoa', info: '@usuario', stars: 5, text: '"Escreva aqui o depoimento..."', imageUrl: '', starColor: '#f59e0b', border: 'small', shadow: 'small' } as PageElement;
                                    case ElementType.Chart: return {
                                      ...base,
                                      type: ElementType.Chart,
                                      items: [
                                        { type: 'donut', percent: 80, title: 'Nível 1', text: 'Baseado no seu Perfil', color: quizData.config.colors.primary },
                                        { type: 'bar', percent: 10, title: 'Nível 2', text: '<strong>7 DIAS</strong><br/>Inicio do Tramento', color: quizData.config.colors.primary },
                                        { type: 'bar', percent: 80, title: 'Nível 3', text: '<strong>21 DIAS</strong><br/>Fim do Tramento', color: quizData.config.colors.primary }
                                      ],
                                      layout: 'grid3',
                                      border: 'small',
                                      shadow: 'none',
                                      animation: 'none',
                                      backgroundColor: '#ffffff',
                                      textColor: quizData.config.colors.text || '#64748b'
                                    } as PageElement;
                                    case ElementType.Report: return {
                                      ...base,
                                      type: ElementType.Report,
                                      title: 'Sua Pontuação',
                                      resultLabel: '<b>Resultado:</b> Bom 😍',
                                      score: 85,
                                      imageUrl: '',
                                      border: 'small',
                                      shadow: 'none',
                                      backgroundColor: '#ffffff',

                                      titleColor: quizData.config.colors.text || '#0f172a',
                                      scoreColor: quizData.config.colors.primary,
                                      percentColor: '#0f172a'
                                    } as PageElement;
                                    case ElementType.Form: return {
                                      ...base,
                                      type: ElementType.Form,
                                      title: 'Nome Completo',
                                      fieldType: 'text',
                                      placeholder: 'Digite sua resposta...',
                                      required: true,
                                      fontSize: 'normal',
                                      style: 'boxed',
                                      labelPosition: 'tradicional',
                                      align: 'left',
                                      border: 'small',
                                      shadow: 'none'
                                    } as PageElement;
                                    case ElementType.FbComments: return {
                                      ...base,
                                      type: ElementType.FbComments,
                                      comments: [
                                        { id: '1', name: 'Maria Silva', timeAgo: 'há 2 min', content: 'Adorei o conteúdo! Muito obrigada.', avatarUrl: '' },
                                        { id: '2', name: 'João Santos', timeAgo: 'há 5 min', content: 'Simplesmente fantástico. Recomendo a todos.', avatarUrl: '' }
                                      ],
                                      totalComments: 124,
                                      showActions: true,
                                      border: 'small',
                                      shadow: 'none'
                                    } as PageElement;
                                    case ElementType.Roulette: return {
                                      ...base,
                                      type: ElementType.Roulette,
                                      title: 'Gire a Roleta!',
                                      buttonText: 'GIRAR AGORA',
                                      winningMessage: 'PARABÉNS! VOCÊ GANHOU!',
                                      subMessage: 'Clique abaixo para resgatar seu prêmio',
                                      items: [
                                        { id: `id_${Date.now()}_1`, emoji: '🎁', label: 'Prêmio 1' },
                                        { id: `id_${Date.now()}_2`, emoji: '⭐', label: 'Prêmio 2' },
                                        { id: `id_${Date.now()}_3`, emoji: '💰', label: 'Prêmio 3' },
                                        { id: `id_${Date.now()}_4`, emoji: '🎉', label: 'Prêmio 4' },
                                        { id: `id_${Date.now()}_5`, emoji: '💎', label: 'Prêmio 5' },
                                        { id: `id_${Date.now()}_6`, emoji: '🚀', label: 'Prêmio 6' }
                                      ],
                                      maxItems: 6,
                                      align: 'center',
                                      border: 'small',
                                      shadow: 'none',
                                      colorScheme: 'colorful'
                                    } as PageElement;
                                    case ElementType.SlotMachine: return {
                                      ...base,
                                      type: ElementType.SlotMachine,
                                      title: 'Tente a Sorte!',
                                      winningMessage: 'JACKPOT! VOCÊ GANHOU!',
                                      losingMessage: 'Não foi dessa vez...',
                                      subMessage: 'Tente novamente',
                                      maxAttempts: 3,
                                      alwaysWin: true,
                                      align: 'center',
                                      border: 'small',
                                      shadow: 'none'
                                    } as PageElement;
                                    default: throw new Error('Tipo de elemento desconhecido');
                                  }
                                })();
                                const current = selectedPage.elements || [];
                                const newElements = [...current];
                                newElements.splice(index, 0, newElement);
                                updateElements(newElements);
                              }}
                              onSelectElement={(element) => setSelectedElementId(element.id)}
                              selectedElementId={selectedElementId}
                              embedEditor={false}
                              config={quizData.config}
                            />
                          </React.Suspense>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-slate-500">Selecione uma página para editar ou adicione uma nova.</p>
              </div>
            )}
          </main>
          <DragOverlay>
            {activeId ? (
              String(activeId).startsWith('palette-') ? (
                <div className="p-3 bg-white border border-slate-300 rounded-lg shadow">Arraste para o canvas</div>
              ) : (
                <div className="p-3 bg-white border border-slate-300 rounded-lg shadow">Movendo componente</div>
              )
            ) : null}
          </DragOverlay>
        </DndContext>
        <aside className="flex-1 bg-white overflow-y-auto">
          <div className="p-6">

            {selectedPage && selectedElementId ? (
              (() => {
                const el = (selectedPage.elements || []).find(e => e.id === selectedElementId);
                if (!el) return <div className="text-slate-500">Selecione uma página ou componente para alterar suas propriedades.</div>;
                return (
                  <ElementEditor
                    element={el}
                    onUpdate={updateSelectedElement}
                    onDelete={deleteSelectedElement}
                    onMoveUp={() => moveSelectedElement('up')}
                    onMoveDown={() => moveSelectedElement('down')}
                    config={quizData.config}
                  />
                );
              })()
            ) : (
              selectedPage ? (
                <React.Suspense fallback={<LoadingSpinner />}>
                  <PagePropertiesEditor
                    page={selectedPage}
                    onUpdate={(updates) => handleUpdatePage(selectedPageIndex, { ...selectedPage, ...updates })}
                  />
                </React.Suspense>
              ) : (
                <div className="text-slate-500">Selecione uma página ou componente para alterar suas propriedades.</div>
              )
            )}
          </div>
        </aside>
      </div>
      {/* Desativado por enquanto: geração de arquivo */}
      {false && showGeneratedCode && <GeneratedCodeModal code={generateCode()} onClose={() => setShowGeneratedCode(false)} />}
      {!!changeSlugModal && (
        <ChangeSlugModal
          // Forçar visibilidade para debug
          open={true}
          currentSlug={changeSlugModal.currentSlug}
          projects={projects}
          currentQuizId={changeSlugModal.quizId}
          onCancel={() => {
            console.log('Closing modal');
            setChangeSlugModal(null);
          }}
          onConfirm={(newSlug) => {
            console.log('Saving slug:', newSlug);
            updateQuiz(changeSlugModal.projectId, changeSlugModal.quizId, { slug: newSlug });
            setChangeSlugModal(null);
          }}
        />
      )}
      {showDesktopPreview && <DesktopPreviewModal
        quizData={quizData}
        onClose={() => setShowDesktopPreview(false)}
        onButtonAction={handleDesktopPreviewButtonAction}
        isRedirecting={isRedirectingDesktop}
      />}
      {showMobilePreview && <MobilePreviewModal quizData={quizData} onClose={() => setShowMobilePreview(false)} />}
    </div>
  );
};




export default App;
